/**
 * Government Opportunities Dashboard - Step 1 (Enhanced)
 * Entity-aware dashboard for government procurement opportunities
 * Entity Type: govt_opportunity
 * 
 * Features:
 * - Entity-based queries with server-side pagination
 * - Status tracking (watching, bidding, won, lost, expired)
 * - Deadline calendar with notifications
 * - Auto-matching to company capabilities
 * - BI analytics (win rate, pipeline value, time-to-bid)
 * - Export functionality
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Button, Input, Badge, Select, formatNumber, formatCurrency, formatPercentage, Modal, useFeatureFlag, useConfig, useToastStore, LoadingSpinner, usePermissions, canWriteOpportunities, canAdminOpportunityPipeline, exportToCSV, exportToExcel, exportToPDF, HelpArticleIcon } from '@tracekit/core';
import { useWorkspace, queryEntities, getAPIClient, getProject, getPolicyPack, getEntitySources, getOpportunityFilterOptions } from '@tracekit/core';
import { useAuthContext } from '@tracekit/core';
// Calendar imports
import { format, parse, startOfWeek, endOfWeek, startOfMonth, endOfMonth, getDay } from 'date-fns';
import { Calendar as BigCalendar, dateFnsLocalizer, Event, View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CardSkeleton, DashboardSkeleton } from '../../components/shared/LoadingSkeleton';
import { SearchProgressIndicator } from '../../components/government-data/SearchProgressIndicator';
import { calculateMatchScore, batchCalculateMatchScores, updateOpportunityMatchScore } from '../../services/opportunityMatcher';
import { dataSourceRegistry, DataSourceType } from '../../services/dataSourceRegistry';
import { OpportunityDetailModal } from '../../components/government-data/OpportunityDetailModal';
import { MatchScoreTooltip, type MatchExplain } from '../../components/government-data/MatchScoreTooltip';
import { OpportunityPipelineKanban } from '../../components/government-data/OpportunityPipelineKanban';
import {
  applyPipelineStageToOpportunityCaches,
  BD_PRIORITY_OPTIONS,
  DISCARD_PIPELINE_STAGE,
  getEffectiveBdPriority,
  GOVT_OPPORTUNITIES_ALL_QUERY_KEY,
  GOVT_OPPORTUNITIES_KANBAN_QUERY_KEY,
  isDiscardedOpportunity,
  KANBAN_TRACKED_FETCH_LIMIT,
  type BdPriority,
  type PipelineStage,
} from '../../types/opportunityPipeline';
import { ProposalBuilder } from '../../components/proposal-management/ProposalBuilder';
import { KeywordRecommendationsModal } from '../../components/government-data/KeywordRecommendationsModal';
import { FilterCombobox } from '../../components/government-data/FilterCombobox';
import { SearchAutocomplete } from '../../components/government-data/SearchAutocomplete';
import { SearchInfoTooltip } from '../../components/government-data/SearchInfoTooltip';
import { deduplicateGovtOpportunityEntities } from '../../utils/opportunityDeduplication';
import {
  buildSemanticSearchReplacement,
  shouldApplySemanticResults,
  shouldRunSemanticUpgrade,
} from '../../utils/opportunityProgressiveSearch';
import {
  SEARCHABLE_FIELDS,
  parseSearchQuery,
  parsedQueryToFilters,
  validateSearchQuery,
  hasExplicitBooleanOperators,
  sanitizePlainTextSearch,
} from '../../utils/advancedSearchParser';
import {
  getRecommendedKeywords,
  getKeywordTaskStatus,
  searchOpportunitiesAutocomplete,
  searchOpportunitiesByFields,
  searchOpportunitiesSemantic,
  updateOpportunityBdPriority,
  updateOpportunityPipelineStage,
  type RecommendedKeywordsResponse,
} from '../../api/opportunities';
import {
  DEFAULT_SUGGESTION_CHIP_LIMIT,
  getUnselectedSuggestionChips,
} from '../../utils/alertKeywordRecommendations';
import {
  provinceOptionMatchesQuery,
} from './canadaProvinceCities';
import {
  enhanceOpportunityEntity,
  getDeadlineUrgency,
  getOpportunityValue,
  getPageFromSearch,
  getProjectIdDisplay,
  getStageBadgeColor,
  withPageParam,
} from '../../utils/opportunityUtils';
import { useExternalData } from '../../contexts/ExternalDataContext';
import { useOpportunitiesDashboardStats } from '../../hooks/useOpportunitiesDashboardStats';
import {
  decodeCountryAccessFromToken,
  getDefaultZone,
  getGeographicZoneFilterValue,
  resolveAllAccessDefaultZone,
} from '../../utils/countryAccess';
import {
  hasClientSideOnlyOpportunityFilters,
  shouldAutoScoreDefaultCatalog as shouldAutoScoreDefaultCatalogPolicy,
  shouldShowSourceInZoneDropdown,
} from '../../utils/opportunityDashboardQuery';
import {
  DEADLINE_PRESET_OPTIONS,
  deadlinePresetLabel,
  deadlinePresetMatchesDaysUntil,
  deadlinePresetToQueryFilters,
  type DeadlinePreset,
} from '../../utils/deadlineFilterPresets';

import {
  Filter,
  RefreshCw,
  Download,
  Calendar,
  Building2,
  DollarSign,
  Target,
  Clock,
  Award,
  AlertTriangle,
  Eye,
  FileText,
  Search,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  MapPin,
  Tag,
  Zap,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
  X,
  List,
  Sparkles,
  HelpCircle as HelpCircleIcon,
  Book,
  SquareKanban,
  User,
  Upload,
  PlusCircle,
  ClipboardCheck,
  FileSpreadsheet,
  CalendarCheck,
} from 'lucide-react';

/** Session key to show deadline summary toast at most once per dashboard visit */
const DEADLINE_SUMMARY_TOAST_KEY = 'opportunities-dashboard-deadline-summary-shown';
/** Persist in-flight Full Ranking task so leaving/returning the page resumes polling. */
const RANKING_TASK_SESSION_KEY = 'opportunities-dashboard-ranking-task';

/** Inline z-index so proposal-flow modals stack above OpportunityDetailModal (10050); below toast (11000). */
const PROPOSAL_FLOW_MODAL_CONTAINER_STYLE: React.CSSProperties = { zIndex: 10_900 };

// Inline label + tooltip so hover state is controlled by React (avoids CSS group-hover issues)
function FilterLabelWithTooltip({ label, tooltipText }: { label: string; tooltipText: string }) {
  const [show, setShow] = useState(false);
  return (
    <label className="flex items-center gap-1.5 text-sm font-medium mb-1 text-theme-text">
      <span>{label}</span>
      <span
        className="relative inline-flex"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <HelpCircleIcon className="h-3.5 w-3.5 text-theme-text-disabled cursor-help" aria-label="Help" />
        {show && (
          <span
            className="pointer-events-none absolute left-1/2 top-full z-[100] mt-1 -translate-x-1/2 px-2 py-1.5 text-xs font-normal text-white bg-theme-paper rounded shadow-xl border border-gray-700 max-w-[220px] text-left"
            role="tooltip"
          >
            {tooltipText}
          </span>
        )}
      </span>
    </label>
  );
}

// Entity type from Policy Pack v2.0
export interface GovtOpportunityEntity {
  id: string;
  entity_type: string;
  project_id?: string; // Project ID from entity metadata
  data: {
    // Core Identification
    opportunity_id?: string;
    external_id?: string;
    solicitation_number?: string;
    reference_number?: string;
    title: string;
    description?: string;

    // Organization & Agency
    organization?: string;
    issuing_agency?: string;
    department?: string;
    sub_agency?: string;
    office?: string;
    agency_code?: string;
    contracting_office?: string;

    // Classification
    opportunity_type?: string; // RFP, RFQ, IFB, EOI, etc.
    procurement_method?: string;
    contract_type?: string;
    award_type?: string;
    competition_type?: string;
    set_aside?: string;
    naics_codes?: string[];
    psc_codes?: string[];
    unspsc_codes?: string[];
    commodity_codes?: string[];

    // Status & Dates
    status?: string; // open, closed, awarded, expired, cancelled
    posted_date?: string;
    published_date?: string;
    issue_date?: string;
    deadline?: string;
    closing_date?: string;
    response_deadline?: string;
    last_modified_date?: string;
    archive_date?: string;
    award_date?: string;

    // Financial
    estimated_value?: number;
    estimated_budget?: number;
    contract_value?: number;
    budget_range_min?: number;
    budget_range_max?: number;
    currency?: string; // USD, CAD, etc.
    funding_source?: string;
    payment_terms?: string;
    value?: number; // Alias for estimated_value/contract_value

    // Geographic
    location?: string;
    region?: string;
    country?: string;
    state_province?: string;
    /** Some feeds store province separately from `state_province`. */
    province?: string;
    city?: string;
    postal_code?: string;
    place_of_performance?: string;
    geographic_scope?: string;

    // Project Details
    scope_of_work?: string;
    project_description?: string;
    project_duration?: string; // Duration in weeks/months
    contract_duration?: string;
    timeline_weeks?: number;
    key_milestones?: string[];
    deliverables?: string[];

    // Requirements
    requirements_summary?: string;
    mandatory_requirements?: string[];
    desirable_requirements?: string[];
    technical_requirements?: string[];
    administrative_requirements?: string[];
    commercial_requirements?: string[];
    legal_requirements?: string[];
    qualification_requirements?: string[];
    certification_requirements?: string[];
    insurance_requirements?: string;
    bonding_requirements?: string;
    security_clearance_required?: boolean;

    // Evaluation
    evaluation_criteria?: string;
    evaluation_methodology?: string;
    evaluation_weights?: Record<string, number>;
    scoring_criteria?: string;
    selection_criteria?: string;

    // Submission
    submission_format?: string;
    submission_method?: string;
    submission_location?: string;
    page_limit?: number;
    word_limit?: number;
    document_requirements?: string[];
    proposal_format?: string;
    number_of_copies?: number;

    // Contact Information
    contact_info?: string;
    primary_contact_name?: string;
    primary_contact_email?: string;
    primary_contact_phone?: string;
    technical_contact_name?: string;
    technical_contact_email?: string;
    contracting_officer_name?: string;
    contracting_officer_email?: string;

    // Additional
    keywords?: string[];
    tags?: string[];
    service_categories?: string[];
    matching_service_lines?: string[];
    industry_sector?: string;
    target_audience?: string;

    // Links & Documents
    url?: string;
    document_url?: string;
    attachment_urls?: string[];
    solicitation_document_url?: string;
    amendment_urls?: string[];

    // Source Metadata
    source?: string;
    source_system?: string;
    data_source?: string;
    integration_type?: string;
    normalized?: boolean;
    raw_data?: Record<string, any>;

    // Matching & Analysis
    match_score?: number;
    relevance_score?: number;
    /** ~10-word AI value line for opportunity cards (what to build, timeline, cost). */
    ai_card_summary?: string;
    ai_card_summary_generated_at?: string;
    ai_card_summary_status?: 'generating' | 'completed' | 'failed';
    win_probability?: number;
    complexity_score?: number;
    risk_level?: 'low' | 'medium' | 'high';
    strategic_importance?: string;

    // DB search relevance (from /opportunities/search/query|semantic - not persisted, populated per search)
    search_score?: number;
    search_tier?: 'exact_phrase' | 'all_words' | 'partial' | 'fuzzy' | 'semantic' | string;

    // History
    amendment_count?: number;
    amendment_history?: Array<Record<string, any>>;
    previous_awards?: Array<Record<string, any>>;
    incumbent_vendor?: string;
    num_bidders?: number;
    historical_win_rate?: number;

    // Custom tracking fields
    tracking_status?: 'watching' | 'bidding' | 'won' | 'lost' | 'declined';
    assigned_by?: string;
    days_until_deadline?: number;

    // SPARC-002 pipeline Kanban
    pipeline_stage?: PipelineStage;
    bd_priority?: BdPriority;
    assigned_bd_person?: string;
    pipeline_stage_history?: Array<{
      from: string;
      to: string;
      changed_by: string;
      changed_at: string;
    }>;

    // Computed fields
    proposal_stage?: 'EOI' | 'RFP' | 'Stage 1' | 'Stage 2' | 'Stage 3' | 'Unknown'; // Derived from opportunity_type
    duration_display?: string; // Formatted duration string

    // RFP Structure V3 (added 2026-01-24)
    rfp_documents?: string[];
    rfp_structure?: import('../../types/rfp').RfpStructureMetadata;
  };
  created_at?: string;
  updated_at?: string;

  // RFP Extraction Status Fields (added 2026-01-24)
  rfp_structure_metadata?: import('../../types/rfp').RfpStructureMetadata;
  rfp_extraction_status?: import('../../types/rfp').RfpExtractionStatus;
  rfp_extraction_method?: import('../../types/rfp').RfpExtractionMethod;
  rfp_extraction_quality_score?: number; // 0-100
  rfp_extracted_at?: string;
  rfp_extraction_error?: string;
}

interface OpportunityStats {
  total: number;
  watching: number;
  bidding: number;
  won: number;
  lost: number;
  pipelineValue: number;
  avgMatchScore: number;
  upcomingDeadlines: number;
  openOpportunities: number;
  trackedOpportunities: number;
  /** Watching + Bidding among opportunities that count as portal-open (used for Status ratio when Open card is selected) */
  trackedInOpenScope: number;
  avgDaysUntilDeadline: number | null;
}

function getDaysUntilDeadlineForOpp(opp: GovtOpportunityEntity): number | null {
  if (opp.data.days_until_deadline != null) return opp.data.days_until_deadline;
  const dateStr =
    opp.data.deadline ||
    opp.data.response_deadline ||
    (opp.data as any).close_date ||
    (opp.data as any).closing_date ||
    (opp.data as any).closing_time;
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function urgencyMultiplierForDaysUntil(daysUntil: number | null): number {
  if (daysUntil == null) return 1.0;
  if (daysUntil < 0) return 0.92;
  if (daysUntil <= 1) return 1.25;
  if (daysUntil <= 3) return 1.18;
  if (daysUntil <= 7) return 1.12;
  if (daysUntil <= 14) return 1.06;
  if (daysUntil <= 30) return 1.0;
  if (daysUntil <= 90) return 0.97;
  return 0.94;
}

/** Matches client-side list filter for portal "open" (aligned with Opp Portal Status = Open) */
function isGovernmentOpportunityOpen(opp: GovtOpportunityEntity): boolean {
  const status = (opp.data.status || '').toLowerCase().trim();
  const days = getDaysUntilDeadlineForOpp(opp);
  const hasFutureDeadline = days != null && days >= 0;
  const openStatuses = ['open', 'active', 'published', 'draft', 'pending', 'available', 'live'];
  const closedStates = ['closed', 'unknown', 'awarded', 'expired', 'cancelled', 'completed', 'ended', 'finished', 'terminated'];
  if (closedStates.includes(status)) return false;
  if (days != null && days < 0) return false;
  if (openStatuses.includes(status) && hasFutureDeadline) return true;
  if (!status && hasFutureDeadline) return true;
  if (status && !closedStates.includes(status) && hasFutureDeadline) return true;
  return false;
}

function opportunityHasUploadedDocuments(opp: GovtOpportunityEntity): boolean {
  const rfpDocuments = opp.data.rfp_documents;
  return Array.isArray(rfpDocuments) && rfpDocuments.length > 0;
}

/** Dummy/seed govt_opportunity entity IDs to hide from Government Data Center (always appear at start of query). */
const BLOCKED_OPPORTUNITY_ENTITY_IDS = new Set<string>([
  '6cda07d3-818c-48ec-8e8b-b2381832d02f',
  'a017fae0-19b3-4336-b89f-1f44bde6b3bc',
]);

/** Calendar, field search, and filter-preview dumps. Must stay at or below the API search max of 500. */
const OPPORTUNITY_DUMP_LIMIT = 400;

export function OpportunitiesDashboard() {
  const config = useConfig();
  const { addToast } = useToastStore();
  const { projectId } = useWorkspace();
  const { user, username, accessToken } = useAuthContext();
  const currentUserName = user?.full_name || username || 'Unknown';
  const { selectedGeographicZone, setSelectedGeographicZone, isGeographicZoneReady } = useExternalData();

  // Region toggle is server-scoped — reset location filters when zone changes.
  useEffect(() => {
    setFilterProvince('all');
    setFilterCity('all');
    setDraftFilterProvince('all');
    setDraftFilterCity('all');
    setSelectedDepartments(new Set());
    setDraftClient('all');
    setPage(1);
    setStatCardSelection(null);
  }, [selectedGeographicZone]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const arm = () => {
      if (!cancelled) setFilterPreviewIdle(true);
    };
    if (typeof win.requestIdleCallback === 'function') {
      const idleId = win.requestIdleCallback(arm, { timeout: 4000 });
      return () => {
        cancelled = true;
        win.cancelIdleCallback?.(idleId);
      };
    }
    const timeoutId = window.setTimeout(arm, 2500);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [projectId]);

  const queryClient = useQueryClient();
  const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
  const canWriteOpp = !permissionsLoading && canWriteOpportunities(hasAnyPermission);
  const canAdminPipeline = !permissionsLoading && canAdminOpportunityPipeline(hasAnyPermission);
  const enableTecWorkflow = config.customization?.featureFlags?.enableTecWorkflow === true;
  const canViewTecQueue =
    enableTecWorkflow &&
    !permissionsLoading &&
    hasAnyPermission(['admin', 'admin_tech', 'keystone_rfp_admin_tech', 'users.manage', '*']);
  const enableCalendarView = useFeatureFlag('enableCalendarView' as Parameters<typeof useFeatureFlag>[0]);
  const enableOpportunitiesImport =
    config.customization?.featureFlags?.enableOpportunitiesImport !== false;
  const softwareDefinedFilterConfigured =
    typeof config.customization?.featureFlags?.defaultSoftwareDefinedFilter === 'boolean';
  const defaultSoftwareDefinedFilter =
    config.customization?.featureFlags?.defaultSoftwareDefinedFilter === true;
  const allAccessDefaultZone = resolveAllAccessDefaultZone(
    config.customization?.defaultGeographicZone
  );
  const sparcDefaultZone = getDefaultZone(decodeCountryAccessFromToken(accessToken), {
    allAccessDefault: allAccessDefaultZone,
  });
  const zoneIsNonDefault = selectedGeographicZone !== sparcDefaultZone;
  const [filterSoftwareDefined, setFilterSoftwareDefined] = useState(defaultSoftwareDefinedFilter);
  const [searchInput, setSearchInput] = useState(''); // Input field value (what user types)
  const [searchTerm, setSearchTerm] = useState(''); // Actual search term used in query

  // Step 1: Confirm this component is mounted when user is on opportunities page
  useEffect(() => {
    console.log('[OpportunitiesDashboard] MOUNTED');
    return () => console.log('[OpportunitiesDashboard] UNMOUNTED');
  }, []);

  // Search history stored in localStorage (per project)
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  // New dedicated DB-backed opportunity search (separate from advanced search parser flow)
  const [dbSearchInput, setDbSearchInput] = useState('');
  const [dbSearchTerm, setDbSearchTerm] = useState('');
  const [dbSearchMatchedIds, setDbSearchMatchedIds] = useState<Set<string> | null>(null);
  const [dbSearchEntities, setDbSearchEntities] = useState<GovtOpportunityEntity[] | null>(null);
  const [dbAutocompleteOpen, setDbAutocompleteOpen] = useState(false);
  const [debouncedDbSearchInput, setDebouncedDbSearchInput] = useState('');
  const dbSearchContainerRef = useRef<HTMLDivElement | null>(null);
  const activeDbSearchRequestRef = useRef('');

  // Progressive search: keyword results render first; semantic upgrade replaces them when ready.
  const [semanticSearchStatus, setSemanticSearchStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [semanticMatchCount, setSemanticMatchCount] = useState(0);

  // Load search history when projectId changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const key = `opportunity-search-history-${projectId || 'global'}`;
      const stored = localStorage.getItem(key);
      setSearchHistory(stored ? JSON.parse(stored) : []);
    }
  }, [projectId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDbSearchInput(dbSearchInput.trim());
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [dbSearchInput]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (dbSearchContainerRef.current && !dbSearchContainerRef.current.contains(event.target as Node)) {
        setDbAutocompleteOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Save search to history (only 1-3 words)
  const saveToSearchHistory = (term: string) => {
    if (!term || !term.trim()) return;

    const trimmed = term.trim();
    // Keep history user-friendly: avoid saving advanced boolean/field syntax queries.
    // Also allow more natural-language searches (not just 1–3 words).
    const hasAdvancedSyntax = hasExplicitBooleanOperators(trimmed) || trimmed.includes(':');
    if (hasAdvancedSyntax) return;
    const wordCount = trimmed.split(/\s+/).length;
    if (wordCount < 1 || wordCount > 12) return;

    setSearchHistory((prev) => {
      // Remove duplicates and add to front
      const updated = [trimmed, ...prev.filter((s) => s !== trimmed)];
      // Keep only last 10 searches
      const limited = updated.slice(0, 10);

      // Save to localStorage
      if (typeof window !== 'undefined') {
        const key = `opportunity-search-history-${projectId || 'global'}`;
        localStorage.setItem(key, JSON.stringify(limited));
      }

      return limited;
    });
  };

  const [filterSource, setFilterSource] = useState<string>('all');
  /** Default 'open': landing shows open opportunities (server-side default when status omitted). */
  const [filterStatus, setFilterStatus] = useState<string>('open');
  const [filterTrackingStatus, setFilterTrackingStatus] = useState<string>('all');
  /** Synthetic value "tracked" = watching OR bidding (matches Status stat card) */
  const [filterDeadlinePreset, setFilterDeadlinePreset] = useState<DeadlinePreset>('all');
  /** Which summary card is visibly selected (independent of default open list filter on first load). */
  const [statCardSelection, setStatCardSelection] = useState<
    null | 'total' | 'open' | 'tracked' | 'deadlines'
  >(null);
  const [filterHasDocuments, setFilterHasDocuments] = useState<boolean | null>(null); // null = all, true = has docs, false = no docs

  /** Province/city/country/client filters — options loaded from opportunity data via API. */
  const [filterProvince, setFilterProvince] = useState<'all' | string>('all');
  const [filterCity, setFilterCity] = useState<'all' | string>('all');
  const [filterCountry, setFilterCountry] = useState<'all' | string>('all');
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());

  /** Draft filter row — applied only after "Apply filters" (see handleApplyFilters). */
  const [draftFilterSource, setDraftFilterSource] = useState<string>('all');
  const [draftFilterStatus, setDraftFilterStatus] = useState<string>('open');
  const [draftFilterTrackingStatus, setDraftFilterTrackingStatus] = useState<string>('all');
  const [draftFilterDeadlinePreset, setDraftFilterDeadlinePreset] = useState<DeadlinePreset>('all');
  const [draftFilterHasDocuments, setDraftFilterHasDocuments] = useState<boolean | null>(null);
  const [draftFilterProvince, setDraftFilterProvince] = useState<'all' | string>('all');
  const [draftFilterCity, setDraftFilterCity] = useState<'all' | string>('all');
  const [draftFilterCountry, setDraftFilterCountry] = useState<'all' | string>('all');
  /** Single client (organization) selection in the UI; applied copies to `selectedDepartments` Set. */
  const [draftClient, setDraftClient] = useState<'all' | string>('all');
  // SPARC-ISSUE-002: initialize from `?page=` so a fresh mount (e.g. after browser
  // back from the opportunity detail route) restores the previously viewed page.
  const [page, setPage] = useState(() =>
    typeof window === 'undefined' ? 1 : getPageFromSearch(window.location.search)
  );
  const [pageSize] = useState(20);
  const [filterPreviewIdle, setFilterPreviewIdle] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list' | 'calendar' | 'kanban'>('cards');
  const [filterKanbanPriority, setFilterKanbanPriority] = useState<'all' | BdPriority>('all');
  const [filterKanbanAssigned, setFilterKanbanAssigned] = useState<'all' | string>('all');
  const [showKanbanDiscarded, setShowKanbanDiscarded] = useState(false);
  const [kanbanWeeklyMeetingMode, setKanbanWeeklyMeetingMode] = useState(true);
  const [pipelineTransitionId, setPipelineTransitionId] = useState<string | null>(null);
  const [priorityUpdatingId, setPriorityUpdatingId] = useState<string | null>(null);
  const [isMatching, setIsMatching] = useState(false);
  const [isRankingJobQueued, setIsRankingJobQueued] = useState(false); // full catalog ranking job queued
  const [activeRankingTaskId, setActiveRankingTaskId] = useState<string | null>(null); // task_id for polling until complete
  const [rankingJobProgress, setRankingJobProgress] = useState<{
    total_opportunities?: number;
    total_matched?: number;
    chunks_processed?: number;
    opportunities_processed?: number;
    eta_seconds?: number;
    opportunities_per_second?: number;
  } | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<GovtOpportunityEntity | null>(null);
  const [matchScores, setMatchScores] = useState<Map<string, number>>(new Map()); // opportunity_id -> match_score
  const [sortByMatchScore, setSortByMatchScore] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Resume Full Ranking polling after leave/return (task_id is otherwise only in React state).
  useEffect(() => {
    if (!projectId || activeRankingTaskId) return;
    try {
      const raw = sessionStorage.getItem(RANKING_TASK_SESSION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { projectId?: string; taskId?: string };
      if (parsed?.projectId === projectId && parsed?.taskId) {
        setActiveRankingTaskId(parsed.taskId);
      }
    } catch {
      /* ignore */
    }
  }, [projectId, activeRankingTaskId]);
  /**
   * Modal deep-linking:
   * When URL query contains `opp_id`, open the OpportunityDetailModal for that opportunity.
   *
   * This enables email drafts / citations to link directly to the right opportunity modal.
   */
  const [urlOppId, setUrlOppId] = useState<string | null>(null);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [showCustomProposalModal, setShowCustomProposalModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [calendarLocalizer, setCalendarLocalizer] = useState<any>(null);
  const [calendarDate, setCalendarDate] = useState<Date>(() => new Date());
  const [calendarView, setCalendarView] = useState<View>('month');

  const syncOppIdInUrl = (oppId: string | null) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (oppId) {
      url.searchParams.set('opp_id', oppId);
      // Keep modal deep-link clean: rely on GovernmentDataDashboard default tab ('opportunities')
      url.searchParams.delete('tab');
    } else {
      url.searchParams.delete('opp_id');
    }
    window.history.pushState({}, '', url.toString());
    setUrlOppId(oppId);
    if (!oppId) {
      window.dispatchEvent(
        new CustomEvent('rfp_external_data_opp_id_change', {
          detail: { oppId },
        })
      );
    }
  };

  // Read `opp_id` from URL and open modal.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const initialId = params.get('opp_id');
    setUrlOppId(initialId);

    const onPopState = () => {
      const nextParams = new URLSearchParams(window.location.search);
      const nextId = nextParams.get('opp_id');
      setUrlOppId(nextId);
      // SPARC-ISSUE-002: restore pagination on same-mount back/forward navigation
      // (e.g. modal opened via pushState) so the list doesn't silently jump to page 1.
      setPage(getPageFromSearch(window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // If URL cleared opp_id, close modal too.
  useEffect(() => {
    if (urlOppId === null) {
      setIsModalOpen(false);
      setSelectedOpportunity(null);
    }
  }, [urlOppId]);

  // SPARC-ISSUE-002: keep `?page=` in sync with the URL via replaceState (no new
  // history entry) so opening a card summary or navigating to the full opportunity
  // detail route preserves the current listing page for the browser Back button.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = withPageParam(window.location.href, page);
    if (next !== window.location.href) {
      window.history.replaceState({}, '', next);
    }
  }, [page]);

  // Initialize calendar localizer with locale
  useEffect(() => {
    const initCalendar = () => {
      // Create localizer with a minimal but valid locale object
      // react-big-calendar requires the locales object, but the actual locale data
      // can be minimal since date-fns functions handle most formatting
      const locales = {
        'en-US': {
          code: 'en-US',
          formatDistance: () => '',
          formatRelative: () => '',
          localize: {
            ordinalNumber: (n: number) => n.toString(),
            era: () => ({} as any),
            quarter: () => ({} as any),
            month: () => ({} as any),
            day: () => ({} as any),
            dayPeriod: () => ({} as any),
          },
          formatLong: {
            date: () => 'PP',
            time: () => 'p',
            dateTime: () => 'PPp',
          },
          match: {
            ordinalNumber: () => null,
            era: () => null,
            quarter: () => null,
            month: () => null,
            day: () => null,
            dayPeriod: () => null,
          },
          options: {
            weekStartsOn: 0,
            firstWeekContainsDate: 1,
          },
        } as any,
      };

      const loc = dateFnsLocalizer({
        format,
        parse,
        startOfWeek,
        getDay,
        locales,
      });

      setCalendarLocalizer(loc);
    };

    if (viewMode === 'calendar' && !calendarLocalizer) {
      initCalendar();
    }
  }, [viewMode, calendarLocalizer]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('[OpportunitiesDashboard] State changed:', {
      isModalOpen,
      selectedOpportunityId: selectedOpportunity?.id,
      selectedOpportunityTitle: selectedOpportunity?.data?.title,
    });
  }, [isModalOpen, selectedOpportunity]);
  const [showProposalPreview, setShowProposalPreview] = useState(false);
  /** Default off: centered card (max-w-6xl, max-h 82vh) matches main OpportunitiesDashboard Modal flow; user can maximize. */
  const [isProposalModalFullscreen, setIsProposalModalFullscreen] = useState(false);
  const [proposalOpportunity, setProposalOpportunity] = useState<GovtOpportunityEntity | null>(null);
  const [proposalStartFresh, setProposalStartFresh] = useState(false);
  const [showNotTrackedWarning, setShowNotTrackedWarning] = useState(false);
  const [warningOpportunity, setWarningOpportunity] = useState<GovtOpportunityEntity | null>(null);
  const [showTeamMemberWarning, setShowTeamMemberWarning] = useState(false);
  const [teamMemberWarningOpp, setTeamMemberWarningOpp] = useState<GovtOpportunityEntity | null>(null);
  const [teamMemberName, setTeamMemberName] = useState<string>('');

  useEffect(() => {
    if (!showProposalPreview) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showProposalPreview]);

  const openProposalBuilder = (opportunity: GovtOpportunityEntity, startFresh = false) => {
    setProposalOpportunity(opportunity);
    setProposalStartFresh(startFresh);
    setShowProposalPreview(true);
  };

  const [keywordTaskId, setKeywordTaskId] = useState<string | null>(null);

  // Fetch keywords (policy seed immediately; poll Celery task for AI keywords)
  const { data: keywordsData, refetch: refetchKeywords } = useQuery({
    queryKey: ['recommended-keywords', projectId],
    queryFn: async () => {
      const response = await getRecommendedKeywords(projectId!);
      if (response.cached || !response.task_id) {
        setKeywordTaskId(null);
        return response;
      }
      if ((response.keywords?.length ?? 0) > 0 || (response.service_keyword_groups?.length ?? 0) > 0) {
        setKeywordTaskId(response.task_id ?? null);
        return response;
      }
      setKeywordTaskId(response.task_id ?? null);
      return response;
    },
    enabled: !!projectId,
    staleTime: 30 * 60 * 1000,
  });

  const { data: keywordTaskStatus } = useQuery({
    queryKey: ['keyword-task-status', keywordTaskId],
    queryFn: () => getKeywordTaskStatus(keywordTaskId!),
    enabled: !!keywordTaskId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false;
      }
      return 1000;
    },
  });

  const resolvedKeywordsData = useMemo((): RecommendedKeywordsResponse | undefined => {
    if (keywordTaskStatus?.status === 'completed') {
      return {
        keywords: keywordTaskStatus.keywords ?? [],
        total_count: keywordTaskStatus.total_count ?? keywordTaskStatus.keywords?.length ?? 0,
        location_keywords: keywordTaskStatus.location_keywords,
        department_names: keywordTaskStatus.department_names,
        location_count: keywordTaskStatus.location_count,
        department_count: keywordTaskStatus.department_count,
        location_keywords_with_count: keywordTaskStatus.location_keywords_with_count,
        department_names_with_count: keywordTaskStatus.department_names_with_count,
        total_opportunities_analyzed: keywordTaskStatus.total_opportunities_analyzed,
        service_keywords: keywordTaskStatus.service_keywords,
        service_keywords_count: keywordTaskStatus.service_keywords_count,
        service_keyword_groups: keywordTaskStatus.service_keyword_groups,
        cached: true,
      };
    }
    return keywordsData;
  }, [keywordTaskStatus, keywordsData]);

  useEffect(() => {
    if (keywordTaskStatus?.status === 'completed') {
      void refetchKeywords();
    }
  }, [keywordTaskStatus?.status, refetchKeywords]);

  const keywordSuggestionChips = useMemo(
    () => getUnselectedSuggestionChips(resolvedKeywordsData, dbSearchTerm ? [dbSearchTerm] : [], DEFAULT_SUGGESTION_CHIP_LIMIT),
    [resolvedKeywordsData, dbSearchTerm]
  );

  const { data: dbAutocompleteData, isFetching: isDbAutocompleteFetching } = useQuery({
    queryKey: ['opportunity-db-autocomplete', projectId, debouncedDbSearchInput],
    queryFn: () => searchOpportunitiesAutocomplete(projectId!, debouncedDbSearchInput, undefined, 12),
    enabled: !!projectId && debouncedDbSearchInput.length >= 2,
    staleTime: 30 * 1000,
  });

  /** Map a raw /search/query or /search/semantic API entity into a GovtOpportunityEntity for display. */
  const mapSearchResultEntity = (e: {
    id: string;
    entity_type?: string;
    data?: Record<string, any>;
    created_at?: string | null;
    updated_at?: string | null;
    search_score?: number;
    search_tier?: string;
  }): GovtOpportunityEntity => {
    const tmp: GovtOpportunityEntity = {
      id: String(e.id),
      entity_type: e.entity_type || 'govt_opportunity',
      data: e.data || {},
      created_at: e.created_at || new Date().toISOString(),
      updated_at: (e as any).updated_at,
    };
    const daysUntilDeadline = getDaysUntilDeadlineForOpp(tmp);
    return {
      ...tmp,
      data: {
        ...(tmp.data || {}),
        days_until_deadline: daysUntilDeadline,
        search_score: e.search_score,
        search_tier: e.search_tier,
      } as GovtOpportunityEntity['data'],
    };
  };

  const dbSearchMutation = useMutation({
    mutationFn: async (term: string) =>
      searchOpportunitiesByFields(projectId!, term, {
        limit: OPPORTUNITY_DUMP_LIMIT,
        includeEntities: true,
        geographicZone: selectedGeographicZone,
      }),
    onSuccess: (result, submittedTerm) => {
      const trimmed = submittedTerm.trim();
      setDbSearchTerm(trimmed);
      setDbSearchMatchedIds(new Set((result?.matched_entity_ids || []).map((id) => String(id))));
      const mapped = (result?.entities || [])
        .filter((e) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(String(e.id)))
        .map(mapSearchResultEntity);
      // Issue 21: the same real-world tender is often ingested as multiple SchemaEntity
      // rows (re-scraped later, mirrored across source portals), so a broad keyword
      // search - like the ones fired by Suggested Keywords chips - recalls all of them
      // and renders 2-3x duplicate cards. Collapse those before they ever reach state.
      setDbSearchEntities(deduplicateGovtOpportunityEntities(mapped));
      setPage(1);
      setStatCardSelection(null);
      setDbAutocompleteOpen(false);
      setSemanticSearchStatus('idle');
      setSemanticMatchCount(0);

      activeDbSearchRequestRef.current = trimmed;
      if (shouldRunSemanticUpgrade(trimmed)) {
        setSemanticSearchStatus('loading');
        semanticSearchMutation.mutate(trimmed);
      }
    },
  });

  const semanticSearchMutation = useMutation({
    mutationFn: async (term: string) =>
      searchOpportunitiesSemantic(projectId!, term, {
        limit: OPPORTUNITY_DUMP_LIMIT,
        includeEntities: true,
        geographicZone: selectedGeographicZone,
      }),
    onSuccess: (result, submittedTerm) => {
      if (!shouldApplySemanticResults(submittedTerm, activeDbSearchRequestRef.current)) {
        return;
      }

      const mapped = (result?.entities || [])
        .filter((e) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(String(e.id)))
        .map(mapSearchResultEntity);
      setDbSearchEntities((prevKeyword) => {
        const keywordEntities = prevKeyword ?? [];
        const replacement = buildSemanticSearchReplacement(keywordEntities, mapped);
        setSemanticMatchCount(replacement.matchCount);
        setDbSearchMatchedIds(replacement.matchedIds);
        setSemanticSearchStatus('done');
        return replacement.entities;
      });
    },
    onError: (_error, submittedTerm) => {
      if (!shouldApplySemanticResults(submittedTerm, activeDbSearchRequestRef.current)) {
        return;
      }
      setSemanticSearchStatus('error');
    },
  });

  // Build filters for query
  const queryFilters = useMemo(() => {
    const filters: Record<string, any> = {};

    // Apply source filter by opportunity portal/source text (matches card labels like bids_tender)
    if (filterSource !== 'all') {
      filters.source = filterSource;
    }

    // Opp Portal Status and Opportunity Stage: single tracking values are filtered
    // server-side (highly selective); "tracked" and other composite values stay client-side.
    if (
      filterTrackingStatus !== 'all' &&
      filterTrackingStatus !== 'tracked'
    ) {
      filters.tracking_status = filterTrackingStatus;
    }

    // Note: Document presence filtering is done client-side in the allOpportunities useMemo
    // because rfp_documents is a JSON array field that's complex to filter on the backend

    // Apply advanced search parsing
    if (searchTerm && searchTerm.trim()) {
      const trimmed = searchTerm.trim();

      // Boolean mode only when user explicitly types AND/OR.
      // Otherwise treat the whole input as plain text and escape literal special chars.
      const hasAdvancedSyntax = hasExplicitBooleanOperators(trimmed) ||
        (trimmed.includes(':') && SEARCHABLE_FIELDS.some(f => trimmed.startsWith(f + ':')));

      if (!hasAdvancedSyntax) {
        // Plain-text search: keep parentheses/hyphens literal
        filters._search = sanitizePlainTextSearch(trimmed);
      } else {
        const validation = validateSearchQuery(trimmed);
        if (!validation.valid) {
          filters._search = sanitizePlainTextSearch(trimmed);
        } else {
          const parsed = parseSearchQuery(trimmed);
          const searchFilters = parsedQueryToFilters(parsed);
          const excludeTerms = searchFilters._exclude_terms;
          delete searchFilters._exclude_terms;
          if (excludeTerms) (filters as any)._exclude_terms = excludeTerms;
          Object.assign(filters, searchFilters);
        }
      }
    }

    // Apply mapping-driven client filter (single-select in UI).
    if (selectedDepartments.size > 0) {
      const singleton = Array.from(selectedDepartments)[0];
      if (singleton) {
        filters.mapping_client = singleton;
      }
    }

    // Apply mapping-driven location filters (authoritative filtering in backend).
    if (filterProvince !== 'all') {
      filters.mapping_province = filterProvince;
    }
    if (filterCountry !== 'all') {
      filters.mapping_country = filterCountry;
    }
    if (filterCity !== 'all') {
      filters.mapping_city = filterCity;
    }

    // SPARC-009: dashboard region toggle — server-side country/zone scoping (World = omit param).
    const zoneFilter = getGeographicZoneFilterValue(selectedGeographicZone);
    if (zoneFilter) {
      filters.geographic_zone = zoneFilter;
    }

    if (filterSoftwareDefined) {
      filters.sparc_software_defined = true;
    }

    Object.assign(filters, deadlinePresetToQueryFilters(filterDeadlinePreset));

    // Opp Portal Status: server-side (default = open when omitted).
    if (filterStatus === 'closed') {
      filters.status = 'closed';
    } else if (filterStatus === 'all') {
      filters.status = 'all';
    }

    return filters;
  }, [
    searchTerm,
    filterSource,
    filterStatus,
    filterTrackingStatus,
    filterHasDocuments,
    selectedDepartments,
    filterProvince,
    filterCity,
    filterCountry,
    selectedGeographicZone,
    filterDeadlinePreset,
    filterSoftwareDefined,
  ]);

  // Deep-link fetch: if URL contains `opp_id`, fetch that opportunity and open the modal.
  const {
    data: urlOpportunityEntity,
    isLoading: urlOpportunityLoading,
    error: urlOpportunityError,
  } = useQuery({
    queryKey: ['govt-opportunity-by-id', projectId, urlOppId],
    enabled: !!projectId && !!urlOppId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      if (!projectId || !urlOppId) return null;
      const api = getAPIClient();
      const e = await api.get<any>(
        `/entities/${urlOppId}`,
        { params: { project_id: projectId } }
      );
      if (!e) return null;

      return {
        id: e.id,
        entity_type: e.entity_type || 'govt_opportunity',
        data: e.data,
        created_at: e.created_at,
        updated_at: (e as any).updated_at,
      } as GovtOpportunityEntity;
    },
  });

  useEffect(() => {
    // Only open when URL is requesting an opp and we fetched it successfully.
    if (!urlOppId) return;
    if (!urlOpportunityEntity) return;
    if (urlOpportunityEntity.id !== urlOppId) return;
    setSelectedOpportunity(urlOpportunityEntity);
    setIsModalOpen(true);
  }, [urlOppId, urlOpportunityEntity]);

  // Note: do NOT clear `opp_id` automatically on error/null here.
  // The dashboard already loads all opportunities; if the single-by-id fetch fails,
  // we can still open the modal by searching within the loaded list.

  // Step 5: Ref to run batch-match from queryFn success path (same tick as /entities/query result)
  const runBatchMatchAfterQueryRef = useRef<
    ((projectId: string, opportunityIds: string[], queryKey: unknown[]) => void) | null
  >(null);

  // ─── Pagination strategy ──────────────────────────────────────────────────
  // True client-side-only filters (documents JSON, composite tracking, semantic overlay).
  // Source and the main search box are already sent on POST /entities/query; they must
  // not flip the dashboard into a 5000-row dump.
  const hasClientSideOnlyFilters = hasClientSideOnlyOpportunityFilters({
    filterHasDocuments,
    filterTrackingStatus,
    dbSearchTerm,
    filterDeadlinePreset,
    filterProvince,
    filterCity,
    filterCountry,
    selectedDepartmentCount: selectedDepartments.size,
    filterStatus,
  });

  const canUseServerPagination =
    !hasClientSideOnlyFilters &&
    (statCardSelection === null || statCardSelection === 'total');

  // Backend-compatible filter object (same as the deletion logic inside the stats queryFn)
  const pageBackendFilters = useMemo(() => {
    const f: Record<string, any> = { ...queryFilters };
    delete f._exclude_terms;
    delete f.location_in;
    if (f._search && !f._search_mode) f._search_mode = 'hybrid';
    return f;
  }, [queryFilters]);

  // Fast page query: loads only the current page from the server (~pageSize items).
  // staleTime=30min so navigating back to a previously-viewed page is instant.
  const {
    data: pageQueryData,
    isLoading: pageQueryLoading,
  } = useQuery({
    queryKey: [
      'govt-opportunities-page',
      projectId,
      page,
      pageSize,
      searchTerm,
      filterSource,
      Array.from(selectedDepartments).sort().join(','),
      filterProvince,
      filterCity,
      filterCountry,
      selectedGeographicZone,
      filterDeadlinePreset,
      filterSoftwareDefined,
      filterStatus,
    ],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const useServerSort = !pageBackendFilters._search;
      return await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters: pageBackendFilters,
        sort_by: useServerSort ? 'data.match_score' : undefined,
        order: useServerSort ? 'desc' : undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
    },
    enabled: !!projectId && canUseServerPagination,
    staleTime: 60 * 60 * 1000, // 1 hour — switching between pages in the same session is instant
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  // Reset to page 1 when switching between server ↔ client-side pagination modes
  // so a stale offset never shows an empty result set.
  const prevCanUseServerPaginationRef = useRef(canUseServerPagination);
  useEffect(() => {
    if (prevCanUseServerPaginationRef.current !== canUseServerPagination) {
      prevCanUseServerPaginationRef.current = canUseServerPagination;
      setPage(1);
    }
  }, [canUseServerPagination]);
  // ─────────────────────────────────────────────────────────────────────────

  // Visible calendar grid range (includes partial weeks at month edges).
  const calendarVisibleRange = useMemo(() => {
    let rangeStart: Date;
    let rangeEnd: Date;
    if (calendarView === 'week' || calendarView === 'work_week') {
      rangeStart = startOfWeek(calendarDate);
      rangeEnd = endOfWeek(calendarDate);
    } else if (calendarView === 'day') {
      rangeStart = calendarDate;
      rangeEnd = calendarDate;
    } else {
      rangeStart = startOfWeek(startOfMonth(calendarDate));
      rangeEnd = endOfWeek(endOfMonth(calendarDate));
    }
    return {
      start: format(rangeStart, 'yyyy-MM-dd'),
      end: format(rangeEnd, 'yyyy-MM-dd'),
    };
  }, [calendarDate, calendarView]);

  const calendarBackendFilters = useMemo(() => {
    const f: Record<string, any> = { ...pageBackendFilters };
    f.deadline_gte = calendarVisibleRange.start;
    f.deadline_lte = calendarVisibleRange.end;
    // Show deadlines in the visible range even if already passed.
    f.include_expired = true;
    return f;
  }, [pageBackendFilters, calendarVisibleRange]);

  const {
    data: calendarOpportunitiesData,
    isLoading: calendarOpportunitiesLoading,
    isFetching: calendarOpportunitiesFetching,
  } = useQuery({
    queryKey: [
      'govt-opportunities-calendar',
      projectId,
      calendarVisibleRange.start,
      calendarVisibleRange.end,
      searchTerm,
      filterSource,
      Array.from(selectedDepartments).sort().join(','),
      filterProvince,
      filterCity,
      filterCountry,
      selectedGeographicZone,
      filterDeadlinePreset,
      filterSoftwareDefined,
    ],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters: calendarBackendFilters,
        sort_by: 'data.deadline',
        order: 'asc',
        limit: OPPORTUNITY_DUMP_LIMIT,
        offset: 0,
      });
      const entitiesFromApi = (result.entities || []).filter(
        (e: { id: string }) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id)
      );
      const mapped = entitiesFromApi.map((e: any) => {
        const tmp: GovtOpportunityEntity = {
          id: e.id,
          entity_type: e.entity_type || 'govt_opportunity',
          data: e.data || {},
          created_at: e.created_at || new Date().toISOString(),
          updated_at: (e as any).updated_at,
        };
        const daysUntilDeadline = getDaysUntilDeadlineForOpp(tmp);
        return {
          ...tmp,
          data: {
            ...(tmp.data || {}),
            days_until_deadline: daysUntilDeadline,
          } as GovtOpportunityEntity['data'],
        };
      }) as GovtOpportunityEntity[];
      return {
        entities: mapped,
        total_count: result.total_count ?? mapped.length,
      };
    },
    enabled: !!projectId && viewMode === 'calendar' && enableCalendarView,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const kanbanBackendFilters = useMemo(() => {
    const filters: Record<string, unknown> = { ...pageBackendFilters, pipeline_tracked: true };
    return filters;
  }, [pageBackendFilters]);

  const {
    data: kanbanOpportunitiesData,
    isLoading: kanbanOpportunitiesLoading,
  } = useQuery({
    queryKey: [
      GOVT_OPPORTUNITIES_KANBAN_QUERY_KEY,
      projectId,
      searchTerm,
      filterSource,
      Array.from(selectedDepartments).sort().join(','),
      filterProvince,
      filterCity,
      filterCountry,
      selectedGeographicZone,
      filterDeadlinePreset,
    ],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters: kanbanBackendFilters,
        sort_by: kanbanBackendFilters._search ? undefined : 'data.match_score',
        order: kanbanBackendFilters._search ? undefined : 'desc',
        limit: KANBAN_TRACKED_FETCH_LIMIT,
        offset: 0,
      });
      const entitiesFromApi = (result.entities || []).filter(
        (e: { id: string }) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id)
      );
      const mapped = entitiesFromApi.map((e: any) => {
        const tmp: GovtOpportunityEntity = {
          id: e.id,
          entity_type: e.entity_type || 'govt_opportunity',
          data: e.data || {},
          created_at: e.created_at || new Date().toISOString(),
          updated_at: (e as any).updated_at,
        };
        const daysUntilDeadline = getDaysUntilDeadlineForOpp(tmp);
        return {
          ...tmp,
          data: {
            ...(tmp.data || {}),
            days_until_deadline: daysUntilDeadline,
          } as GovtOpportunityEntity['data'],
        };
      }) as GovtOpportunityEntity[];
      return {
        entities: mapped,
        total_count: result.total_count ?? mapped.length,
      };
    },
    enabled: !!projectId && viewMode === 'kanban',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  /** Default landing: full catalog (total), no summary card selected.
   * Declared here (rather than near its other usages further below) so the
   * heavy opportunitiesData query below can key off it to auto-score the
   * default view (Issue 20 — see `shouldAutoScoreDefaultCatalog`). */
  const isDashboardDefaultFilterState =
    searchTerm.trim() === '' &&
    dbSearchTerm.trim() === '' &&
    filterSource === 'all' &&
    filterStatus === 'open' &&
    filterTrackingStatus === 'all' &&
    filterDeadlinePreset === 'all' &&
    filterHasDocuments === null &&
    filterProvince === 'all' &&
    filterCity === 'all' &&
    selectedDepartments.size === 0;

  // Backend-cached stat-card counts (total / open / tracked / upcoming deadlines).
  // The server caches these in Redis for 15 minutes — first call runs 4 DB queries,
  // every subsequent call within the window is sub-millisecond. Declared here (rather
  // than near its other usages further below, which share this exact queryKey and reuse
  // this same cache entry) so we can cheaply read `dashboardStats.total` to decide
  // whether the default view's catalog is small enough to auto-score below.
  const { data: dashboardStats, isLoading: dashboardStatsLoading } = useOpportunitiesDashboardStats(
    projectId,
    selectedGeographicZone,
    { enabled: isGeographicZoneReady }
  );

  // Issue 20 fix: the default (unfiltered, status=open) landing view never showed
  // expertise-relevant opportunities because the heavy opportunitiesData query below
  // (the only place that schedules the match-score batch job) was intentionally disabled
  // for the default view to avoid loading up to 5 000 rows on big catalogs. That meant
  // `data.match_score` stayed NULL for every entity, so the fast pageQuery's
  // `ORDER BY data.match_score DESC` had nothing to sort by and returned rows in
  // arbitrary/insertion order instead of expertise-relevance order. When the catalog is
  // small (<=200, mirroring the existing shouldAutoBatchMatch/Auto-Match-effect gates
  // below) we opt back in once so scores get computed and persisted; once matchScores is
  // populated this flips back off, so it isn't refetched every render.
  const shouldAutoScoreDefaultCatalog = shouldAutoScoreDefaultCatalogPolicy({
    isSparcSoftwareDefinedDefault: defaultSoftwareDefinedFilter,
    isDashboardDefaultFilterState,
    viewMode,
    matchScoreCount: matchScores.size,
    dashboardTotal: dashboardStats?.total,
  });

  // Background stats query: loads the full opportunity set (up to 5000) for computing
  // accurate aggregate stats — tracking counts, deadline alerts, avg match score,
  // pipeline value, open counts, etc. Runs in background and does NOT block the initial
  // page render (which uses the fast pageQuery above).
  const {
    data: opportunitiesData,
    isLoading,
    error,
    dataUpdatedAt,
    isFetching,
    refetch,
  } = useQuery({
    // Status, province/city, and tracking stage are client-side only: same fetched set is filtered in allOpportunities, then paginated
    queryKey: [
      'govt-opportunities-all',
      projectId,
      searchTerm,
      filterSource,
      filterHasDocuments,
      Array.from(selectedDepartments).sort().join(','),
      filterProvince,
      filterCity,
      filterCountry,
      selectedGeographicZone,
      filterDeadlinePreset,
      filterTrackingStatus,
      filterSoftwareDefined,
      viewMode === 'kanban' ? 'kanban' : 'list',
    ],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');

      // Remove client-side only filters before sending to backend so we get full set; filter in allOpportunities
      const backendFilters = { ...queryFilters };
      delete (backendFilters as any)._exclude_terms;
      delete (backendFilters as any).location_in;

      // Ensure _search_mode is set to 'hybrid' for vector search when search term is present
      if (backendFilters._search && !backendFilters._search_mode) {
        backendFilters._search_mode = 'hybrid';
      }

      // Sort server-side by match_score when no active text search.
      // `data.match_score` now holds the Keystone-priority ranking score (consulting /
      // infrastructure / utilities / environmental themes). Backend applies deadline as a
      // tie-breaker, so page 1/2 stays dominated by the right tenders.
      const useServerMatchSort = !backendFilters._search;

      // Always pull the full set (up to 5000) so the dashboard can show all opportunities.
      const effectiveLimit = 5000;

      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters: backendFilters,
        sort_by: useServerMatchSort ? 'data.match_score' : undefined,
        order: useServerMatchSort ? 'desc' : undefined,
        limit: effectiveLimit,
        offset: 0,
      });

      // Step 2: Confirm this queryFn = the /entities/query that just returned 200
      const entityCount = result?.entities?.length ?? 0;
      console.log('[OpportunitiesDashboard] queryFn received /entities/query result', { entityCount, projectId });

      // Filter out dummy/seed entity IDs so they are never shown in the Government Data Center
      const entitiesFromApi = (result.entities || []).filter(
        (e: { id: string }) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id)
      );

      // Extract relevance scores from search metadata if available
      const searchMetadata = (result as any).search_metadata;
      const entityRelevance = searchMetadata?.entity_relevance || {};

      // Backend returns total_count for pagination UI (e.g., "Showing 1-200 of 5000")
      const totalCount = result.total_count || result.entities?.length || 0;

      const mapToDashboardEntity = (e: any) => {
        const tmp: GovtOpportunityEntity = {
          id: e.id,
          entity_type: e.entity_type || 'govt_opportunity',
          data: e.data || {},
          created_at: e.created_at || new Date().toISOString(),
          updated_at: (e as any).updated_at,
        };
        const daysUntilDeadline = getDaysUntilDeadlineForOpp(tmp);

        // Get relevance score for this entity
        const relevance = entityRelevance[e.id];

        return {
          ...tmp,
          data: { ...(tmp.data || {}), days_until_deadline: daysUntilDeadline } as GovtOpportunityEntity['data'],
          // Add relevance metadata for display
          relevanceScore: relevance?.relevance_score,
          matchType: relevance?.match_type,
        };
      };

      const allEntities = entitiesFromApi.map(mapToDashboardEntity) as (GovtOpportunityEntity & { relevanceScore?: number; matchType?: string })[];

      // For name/title fallback search, keep an unfiltered candidate pool.
      // This fixes cases where backend _search misses exact opportunity names.
      let fallbackSearchEntities = allEntities;
      if (backendFilters._search) {
        const fallbackFilters = { ...backendFilters };
        delete (fallbackFilters as any)._search;
        delete (fallbackFilters as any)._search_mode;

        const fallbackResult = await queryEntities({
          project_id: projectId,
          entity_type: 'govt_opportunity',
          filters: fallbackFilters,
          sort_by: 'data.match_score',
          order: 'desc',
          limit: effectiveLimit,
          offset: 0,
        });

        const fallbackEntitiesFromApi = (fallbackResult.entities || []).filter(
          (e: { id: string }) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id)
        );
        fallbackSearchEntities = fallbackEntitiesFromApi.map(mapToDashboardEntity) as (GovtOpportunityEntity & {
          relevanceScore?: number;
          matchType?: string;
        })[];
      }

      // Calculate stats from all entities
      const watching = allEntities.filter((o) => o.data.tracking_status === 'watching').length;
      const bidding = allEntities.filter((o) => o.data.tracking_status === 'bidding').length;
      const won = allEntities.filter((o) => o.data.tracking_status === 'won').length;
      const lost = allEntities.filter((o) => o.data.tracking_status === 'lost').length;

      const pipelineValue = allEntities
        .filter((o) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
        .reduce((sum, o) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);

      const upcomingDeadlines = allEntities.filter(
        (o) => o.data.days_until_deadline !== null &&
          o.data.days_until_deadline !== undefined &&
          o.data.days_until_deadline >= 0 &&
          o.data.days_until_deadline <= 30
      ).length;

      // Calculate open opportunities with comprehensive logic
      // Consider an opportunity "open" if:
      // 1. status is explicitly open/active/published (case-insensitive), OR
      // 2. status is null/undefined/empty but has a future deadline (not yet closed), OR
      // 3. status is not a closed state and has a future deadline
      const openOpportunities = allEntities.filter((o) => {
        const status = (o.data.status || '').toLowerCase().trim();
        const hasFutureDeadline = o.data.days_until_deadline !== null &&
          o.data.days_until_deadline !== undefined &&
          o.data.days_until_deadline >= 0;

        // Explicitly open statuses (case-insensitive, handle variations from different sources)
        const openStatuses = ['open', 'active', 'published', 'draft', 'pending', 'available', 'live'];
        if (openStatuses.includes(status)) {
          return true;
        }

        // If status is null/undefined/empty but has future deadline, consider it open
        if (!status && hasFutureDeadline) {
          return true;
        }

        // If status is not a closed state, consider it open if it has a future deadline
        const closedStates = ['closed', 'awarded', 'expired', 'cancelled', 'completed', 'ended', 'finished', 'terminated'];
        if (status && !closedStates.includes(status) && hasFutureDeadline) {
          return true;
        }

        return false;
      }).length;

      const trackedOpportunities = allEntities.filter(
        (o) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
      ).length;

      const deadlines = allEntities
        .map((o) => o.data.days_until_deadline)
        .filter((d): d is number => d !== null && d !== undefined && d >= 0);
      const avgDaysUntilDeadline = deadlines.length > 0
        ? Math.round(deadlines.reduce((sum, d) => sum + d, 0) / deadlines.length)
        : null;

      // Step 5: Schedule batch-match only for the default full-catalog view.
      // Never auto-recompute scores on filtered/search subsets (preserve ranking stability).
      const totalFromBackend = result.total_count ?? 0;
      const shouldAutoBatchMatch =
        allEntities.length >= 1 &&
        totalFromBackend <= 200 &&
        !!projectId &&
        searchTerm.trim() === '' &&
        filterSource === 'all' &&
        filterStatus === 'open' &&
        filterTrackingStatus === 'all' &&
        filterDeadlinePreset === 'all' &&
        filterHasDocuments === null &&
        filterProvince === 'all' &&
        filterCity === 'all' &&
        selectedDepartments.size === 0;

      if (shouldAutoBatchMatch) {
        // Prefix key only — runBatchMatchWithIds uses setQueriesData so it still hits the
        // full queryKey (which also includes province/city/zone/deadline/tracking/viewMode).
        const queryKey = ['govt-opportunities-all', projectId];
        const ids = allEntities.map((e: { id: string }) => e.id);
        console.log('[OpportunitiesDashboard] queryFn scheduling batch-match after /entities/query', { entityCount: allEntities.length, totalCount: totalFromBackend, projectId });
        setTimeout(() => {
          runBatchMatchAfterQueryRef.current?.(projectId, ids, queryKey);
        }, 0);
      }

      return {
        allEntities, // All entities for client-side pagination
        fallbackSearchEntities, // Unfiltered pool used by client-side name fallback
        total: result.total_count,
        stats: {
          watching,
          bidding,
          won,
          lost,
          pipelineValue,
          upcomingDeadlines,
          openOpportunities,
          trackedOpportunities,
          avgDaysUntilDeadline,
        },
      };
    },
    // Only run the heavy 5 000-row query when the user has applied a client-side-only
    // filter (status, tracking, location, documents, deadline) or clicked a non-total
    // stat card. In the default (unfiltered) view the dashboard-stats endpoint above
    // provides all the stat-card numbers with zero row loading.
    enabled: !!projectId && (
      hasClientSideOnlyFilters ||
      (statCardSelection !== null && statCardSelection !== 'total') ||
      shouldAutoScoreDefaultCatalog
    ),
    staleTime: 60 * 60 * 1000, // 1 hour freshness window
    refetchOnWindowFocus: false,
  });

  // Background snapshot for filter preview counts (documents, status, stage, deadlines)
  // when the heavy 5k opportunitiesData query is not active (default landing view).
  const { data: filterPreviewSnapshot } = useQuery({
    queryKey: ['govt-opportunities-filter-preview', projectId, selectedGeographicZone],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const zone = getGeographicZoneFilterValue(selectedGeographicZone);
      const filters: Record<string, unknown> = { status: 'open' };
      if (zone) filters.geographic_zone = zone;
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters,
        limit: OPPORTUNITY_DUMP_LIMIT,
        offset: 0,
      });
      return (result.entities || [])
        .filter((e: { id: string }) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id))
        .map((e: any) => {
          const tmp: GovtOpportunityEntity = {
            id: e.id,
            entity_type: e.entity_type || 'govt_opportunity',
            data: e.data || {},
            created_at: e.created_at || new Date().toISOString(),
          };
          return {
            ...tmp,
            data: {
              ...(tmp.data || {}),
              days_until_deadline: getDaysUntilDeadlineForOpp(tmp),
            },
          };
        });
    },
    enabled: !!projectId && filterPreviewIdle && !opportunitiesData?.allEntities?.length,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Diagnostic: log when opportunities data is available (for auto-match debugging)
  useEffect(() => {
    const count = opportunitiesData?.allEntities?.length ?? 0;
    if (count > 0) {
      console.log('[OpportunitiesDashboard] Opportunities data available', { count, isLoading, firstId: opportunitiesData?.allEntities?.[0]?.id });
    }
  }, [opportunitiesData?.allEntities?.length, opportunitiesData?.allEntities?.[0]?.id, isLoading]);

  // Auto-enable match-score sort if persisted scores already exist on the loaded entities
  useEffect(() => {
    if (!sortByMatchScore && opportunitiesData?.allEntities) {
      const hasScores = opportunitiesData.allEntities.some(
        (e) => e.data.match_score !== undefined && e.data.match_score !== null
      );
      if (hasScores) setSortByMatchScore(true);
    }
    // Only run when new data loads, not every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunitiesData?.allEntities]);

  // Client-side pagination from allEntities
  // Filter and sort all opportunities before pagination
  const allOpportunities = useMemo(() => {
    let filtered = opportunitiesData?.allEntities ?? [];

    if (dbSearchTerm.trim() && dbSearchEntities && dbSearchEntities.length > 0) {
      filtered = dbSearchEntities;
    } else if (dbSearchMatchedIds && dbSearchTerm.trim()) {
      filtered = filtered.filter((opp) => dbSearchMatchedIds.has(String(opp.id)));
    }

    // Source (client-side safety net): keep selected source stable even if a refetch
    // briefly returns broader cached/server data.
    if (filterSource !== 'all') {
      // When filterSource is UUID (source_id), backend already applies authoritative filtering.
      // Keep this client-side safety net only for legacy textual source values.
      const isUuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(filterSource);
      if (!isUuidLike) {
        const expected = filterSource.toLowerCase().trim();
        filtered = filtered.filter((opp) => {
          const candidateSources = [
            opp?.data?.source,
            (opp?.data as any)?.integration_type,
            (opp?.data as any)?.source_system,
            (opp?.data as any)?.raw_data?.source,
            (opp?.data as any)?.source_name,
          ]
            .filter((value) => typeof value === 'string')
            .map((value) => String(value).toLowerCase().trim());
          return candidateSources.includes(expected);
        });
      }
    }

    // Client-side title/name fallback — catches cases where backend misses exact name matches.
    // IMPORTANT: do this BEFORE dropdown filters so all filters remain strict AND conditions.
    if (searchTerm && searchTerm.trim() && !searchTerm.includes(':')) {
      const lowerSearch = searchTerm.trim().toLowerCase();
      const backendIds = new Set(filtered.map((o) => o.id));

      const fallbackPool = (opportunitiesData as any)?.fallbackSearchEntities ?? opportunitiesData?.allEntities ?? [];
      const nameMatches = fallbackPool.filter(
        (opp: GovtOpportunityEntity) =>
          !backendIds.has(opp.id) &&
          (
            (opp.data.title || '').toLowerCase().includes(lowerSearch) ||
            (opp.data.opportunity_id || '').toLowerCase().includes(lowerSearch) ||
            (opp.data.solicitation_number || '').toLowerCase().includes(lowerSearch) ||
            (opp.data.organization || '').toLowerCase().includes(lowerSearch)
          )
      );

      if (nameMatches.length > 0) {
        filtered = [...filtered, ...nameMatches];
      }
    }

    // Opp Portal Status is applied server-side via filters.status (open default, all, closed).

    // Opportunity Stage (client-side): strict match; missing/empty tracking_status = exclude
    if (filterTrackingStatus !== 'all') {
      if (filterTrackingStatus === 'tracked') {
        filtered = filtered.filter((opp) => {
          const t = String(opp?.data?.tracking_status || '').trim().toLowerCase();
          return t === 'watching' || t === 'bidding';
        });
      } else {
        const expected = String(filterTrackingStatus || '').trim().toLowerCase();
        filtered = filtered.filter((opp) => String(opp?.data?.tracking_status || '').trim().toLowerCase() === expected);
      }
    }

    // Due-date window (client-side refinement when heavy 5k query is active)
    if (filterDeadlinePreset !== 'all') {
      filtered = filtered.filter((o) =>
        deadlinePresetMatchesDaysUntil(filterDeadlinePreset, o.data.days_until_deadline)
      );
    }

    // Apply NOT term exclusions if present in filters
    const excludeTerms = queryFilters._exclude_terms;
    if (excludeTerms && Array.isArray(excludeTerms) && excludeTerms.length > 0) {
      filtered = filtered.filter(opp => {
        const searchableText = [
          opp.data.title,
          opp.data.description,
          opp.data.organization,
          opp.data.issuing_agency,
          ...(opp.data.keywords || []),
          ...(opp.data.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        // Exclude if any NOT term is found
        return !excludeTerms.some(term =>
          searchableText.includes(term.toLowerCase())
        );
      });
    }

    // Apply document presence filter (client-side)
    if (filterHasDocuments !== null) {
      filtered = filtered.filter(opp => {
        const rfpDocuments = opp.data.rfp_documents;
        const hasDocuments = Array.isArray(rfpDocuments) && rfpDocuments.length > 0;
        return filterHasDocuments ? hasDocuments : !hasDocuments;
      });
    }

    // Client (organization) filtering is mapping-driven and applied server-side via mapping_client.
    // Do not re-apply a strict equality check on opp.data.organization here (it would break
    // cases where mapping organization contains the opportunity org as a substring).

    // Country / Province / City filtering is now applied server-side via location-based filters
    // in queryFilters. Keep list processing here focused on client-only filters.

    // Match score filtering removed - not needed

    const daysUntilForSort = (o: GovtOpportunityEntity) => getDaysUntilDeadlineForOpp(o);

    /** Tie-break: newer publish date first (Bids & Tenders / portal ``published_date``). */
    const publishTimeMs = (o: GovtOpportunityEntity): number | null => {
      const d = o.data as Record<string, unknown>;
      const raw = d.published_date ?? d.publish_date ?? d.converted_publish_date ?? d.posted_date;
      if (raw == null || typeof raw !== 'string') return null;
      const t = Date.parse(raw);
      return Number.isNaN(t) ? null : t;
    };

    const sortByDeadline = (list: GovtOpportunityEntity[]) =>
      [...list].sort((a, b) => {
        const aDays = daysUntilForSort(a);
        const bDays = daysUntilForSort(b);
        const aExpired = aDays != null && aDays < 0;
        const bExpired = bDays != null && bDays < 0;
        if (!aExpired && bExpired) return -1;
        if (aExpired && !bExpired) return 1;
        if (aDays != null && bDays != null) return aDays - bDays;
        if (aDays != null) return -1;
        if (bDays != null) return 1;
        return 0;
      });

    // When Auto-Match is on, order by match score (desc), then deadline as tie-breaker.
    // Otherwise, keep API order (server is already relevance-sorted by data.relevance_score).
    if (sortByMatchScore) {
      const scoreOf = (o: GovtOpportunityEntity) => {
        const fromMap = matchScores.get(String(o.id));
        if (fromMap !== undefined && fromMap !== null) return Number(fromMap);
        const raw = o.data.match_score;
        if (raw !== undefined && raw !== null) return Number(raw);
        return 0;
      };
      filtered = [...filtered].sort((a, b) => {
        const sa = scoreOf(a);
        const sb = scoreOf(b);
        if (sb !== sa) return sb - sa;
        const aDays = daysUntilForSort(a);
        const bDays = daysUntilForSort(b);
        const aExpired = aDays != null && aDays < 0;
        const bExpired = bDays != null && bDays < 0;
        if (!aExpired && bExpired) return -1;
        if (aExpired && !bExpired) return 1;
        if (aDays != null && bDays != null) return aDays - bDays;
        if (aDays != null) return -1;
        if (bDays != null) return 1;
        const pa = publishTimeMs(a);
        const pb = publishTimeMs(b);
        if (pa != null && pb != null && pb !== pa) return pb - pa;
        if (pa != null && pb == null) return -1;
        if (pa == null && pb != null) return 1;
        return 0;
      });
    } else {
      if (searchTerm && searchTerm.trim()) {
        filtered = [...filtered].sort((a, b) => {
          const aTracked = a.data.tracking_status === 'watching' || a.data.tracking_status === 'bidding';
          const bTracked = b.data.tracking_status === 'watching' || b.data.tracking_status === 'bidding';
          if (aTracked && !bTracked) return -1;
          if (!aTracked && bTracked) return 1;
          return 0;
        });
        filtered = sortByDeadline(filtered);
      }
    }

    return filtered;
  }, [
    opportunitiesData?.allEntities,
    filterSource,
    filterStatus,
    filterTrackingStatus,
    filterDeadlinePreset,
    sortByMatchScore,
    matchScores,
    queryFilters._exclude_terms,
    filterHasDocuments,
    searchTerm,
    dbSearchTerm,
    dbSearchMatchedIds,
    dbSearchEntities,
    filterProvince,
    filterCity,
  ]);

  const effectiveStatCardSelection = statCardSelection ?? 'total';

  // Overlay filter applied only to the *list* when a stat card is selected.
  // Card values remain computed from `allOpportunities` (dropdown filters only).
  const listOpportunities = useMemo(() => {
    if (effectiveStatCardSelection === 'total') return allOpportunities;

    if (effectiveStatCardSelection === 'open') {
      return allOpportunities.filter((opp) => isGovernmentOpportunityOpen(opp));
    }

    if (effectiveStatCardSelection === 'tracked') {
      return allOpportunities.filter(
        (opp) => opp.data.tracking_status === 'watching' || opp.data.tracking_status === 'bidding'
      );
    }

    if (effectiveStatCardSelection === 'deadlines') {
      return allOpportunities.filter(
        (opp) =>
          opp.data.days_until_deadline !== null &&
          opp.data.days_until_deadline !== undefined &&
          opp.data.days_until_deadline >= 0 &&
          opp.data.days_until_deadline <= 30
      );
    }

    return allOpportunities;
  }, [allOpportunities, effectiveStatCardSelection]);

  // When any filter is active, show filtered count; otherwise show backend total
  const backendTotalCount = opportunitiesData?.total ?? 0;
  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    dbSearchTerm.trim() !== '' ||
    filterSource !== 'all' ||
    filterStatus !== 'open' ||
    filterTrackingStatus !== 'all' ||
    filterDeadlinePreset !== 'all' ||
    filterHasDocuments !== null ||
    filterProvince !== 'all' ||
    filterCity !== 'all' ||
    filterCountry !== 'all' ||
    selectedDepartments.size > 0 ||
    (sortByMatchScore && matchScores.size > 0);
  const hasStatOverlay = effectiveStatCardSelection !== 'total';

  // isDashboardDefaultFilterState is declared earlier (above the opportunitiesData
  // query) so that query can key auto-scoring off it — see shouldAutoScoreDefaultCatalog.

  // ─── Display list: fast page query (server pagination) or client-side slice ───
  const startIdx = (page - 1) * pageSize;
  const endIdx = startIdx + pageSize;

  const opportunities = useMemo(() => {
    const scoreOf = (entity: { data?: { match_score?: number | null } }) => {
      const raw = entity.data?.match_score;
      if (raw === undefined || raw === null || raw === '') return 0;
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };

    if (canUseServerPagination && pageQueryData?.entities) {
      const entityRelevance = (pageQueryData as any).search_metadata?.entity_relevance || {};
      const mapped = (pageQueryData.entities as any[])
        .filter((e: any) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id))
        .map((e: any) => {
          const tmp: GovtOpportunityEntity = {
            id: e.id,
            entity_type: e.entity_type || 'govt_opportunity',
            data: e.data || {},
            created_at: e.created_at || new Date().toISOString(),
            updated_at: e.updated_at,
          };
          const daysUntilDeadline = getDaysUntilDeadlineForOpp(tmp);
          const relevance = entityRelevance[e.id];
          return {
            ...tmp,
            data: { ...(tmp.data || {}), days_until_deadline: daysUntilDeadline } as GovtOpportunityEntity['data'],
            relevanceScore: relevance?.relevance_score,
            matchType: relevance?.match_type,
          };
        }) as (GovtOpportunityEntity & { relevanceScore?: number; matchType?: string })[];
      return [...mapped].sort((a, b) => scoreOf(b) - scoreOf(a));
    }
    return listOpportunities.slice(startIdx, endIdx);
  }, [canUseServerPagination, pageQueryData, listOpportunities, startIdx, endIdx]);

  // totalCount, hasMore, and displayLoading are computed AFTER globalOpportunitiesTotalData
  // is declared (below). Placeholders used here; the real values are set after the query.
  // (TypeScript block-scope ordering — see ~line 1310 for the final derivations.)
  // Loading state for the display list only (not for stats cards)
  const displayLoading = canUseServerPagination
    ? pageQueryLoading && !pageQueryData
    : isLoading;

  // Use stats from the combined query
  const aggregateStatsData = opportunitiesData?.stats;

  // Deadline alerts: opportunities due in 7 days (urgent) and 14 days (soon) for banner + toasts
  const deadlineAlerts = useMemo(() => {
    const list = opportunitiesData?.allEntities ?? [];
    const urgent = list.filter(
      (o) =>
        o.data.days_until_deadline != null &&
        o.data.days_until_deadline >= 0 &&
        o.data.days_until_deadline <= 7
    );
    const soon = list.filter(
      (o) =>
        o.data.days_until_deadline != null &&
        o.data.days_until_deadline >= 0 &&
        o.data.days_until_deadline <= 14 &&
        (o.data.days_until_deadline == null || o.data.days_until_deadline > 7)
    );
    return { urgent, soon, urgentCount: urgent.length, soonCount: soon.length };
  }, [opportunitiesData?.allEntities]);

  // One-time deadline summary toast when dashboard loads with upcoming deadlines
  const deadlineSummaryShownRef = useRef(false);
  useEffect(() => {
    if (isLoading || deadlineSummaryShownRef.current || !opportunitiesData) return;
    const { urgentCount } = deadlineAlerts;
    if (urgentCount === 0) return;
    try {
      const already = sessionStorage.getItem(DEADLINE_SUMMARY_TOAST_KEY);
      if (already === '1') return;
      sessionStorage.setItem(DEADLINE_SUMMARY_TOAST_KEY, '1');
      deadlineSummaryShownRef.current = true;
      addToast({
        type: 'info',
        title: 'Deadline reminder',
        message:
          urgentCount === 1
            ? '1 opportunity has a deadline in the next 7 days.'
            : `${formatNumber(urgentCount)} opportunities have deadlines in the next 7 days.`,
        duration: 6000,
      });
    } catch {
      deadlineSummaryShownRef.current = true;
    }
  }, [isLoading, opportunitiesData, deadlineAlerts.urgentCount, addToast]);

  // Fetch project and policy pack for search suggestions
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      return await getProject(projectId);
    },
    enabled: !!projectId,
  });

  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: async () => {
      if (!project?.policy_pack_id) return null;
      return await getPolicyPack(project.policy_pack_id);
    },
    enabled: !!project?.policy_pack_id,
  });

  // Ranking stats: how many opportunities have match_score persisted (X of Y ranked)
  const { data: rankingStats, refetch: refetchRankingStats } = useQuery({
    queryKey: ['opportunities-ranking-stats', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const api = getAPIClient();
      const res = await api.get<{ total_opportunities: number; ranked_count: number }>(
        `/api/opportunities/ranking-stats?project_id=${projectId}`
      );
      return res;
    },
    enabled: !!projectId,
  });

  // dashboardStats is declared earlier (above the opportunitiesData query) so that
  // query can cheaply check dashboardStats.total — see shouldAutoScoreDefaultCatalog.
  // This replaces the client-side 5 000-row background query for the default (unfiltered) view.

  // Global total count of govt_opportunity entities for this project (unfiltered)
  const { data: globalOpportunitiesTotalData } = useQuery({
    queryKey: ['govt-opportunities-global-total', projectId, selectedGeographicZone],
    queryFn: async () => {
      if (!projectId) return null;
      return await queryEntities({
        project_id: projectId,
        entity_type: 'govt_opportunity',
        filters: {
          ...(getGeographicZoneFilterValue(selectedGeographicZone)
            ? { geographic_zone: getGeographicZoneFilterValue(selectedGeographicZone)! }
            : {}),
        },
        limit: 1, // We only need the total_count from header/meta
        offset: 0,
      });
    },
    enabled: !!projectId,
    staleTime: 60 * 60 * 1000,
  });

  // Derived pagination values — computed here after globalOpportunitiesTotalData is available
  const globalTotal = globalOpportunitiesTotalData?.total_count ?? 0;
  const totalCount = canUseServerPagination
    ? (hasActiveFilters ? (pageQueryData?.total_count ?? 0) : (globalTotal > 0 ? globalTotal : (pageQueryData?.total_count ?? 0)))
    : (hasActiveFilters || hasStatOverlay)
      ? listOpportunities.length
      : (backendTotalCount > 0 ? backendTotalCount : listOpportunities.length);
  // Stable dataset total for "Total Opportunities" card.
  // Unlike totalCount, this intentionally ignores stat-card overlay selection
  // (open/tracked/deadlines) so Total stays constant while overlaying the list.
  const baseTotalCount = canUseServerPagination
    ? (hasActiveFilters ? (pageQueryData?.total_count ?? 0) : (globalTotal > 0 ? globalTotal : (pageQueryData?.total_count ?? 0)))
    : (hasActiveFilters
      ? allOpportunities.length
      : (backendTotalCount > 0 ? backendTotalCount : allOpportunities.length));
  const hasMore = canUseServerPagination
    ? (page * pageSize) < totalCount
    : endIdx < listOpportunities.length;

  // Auto-match visibility — after globalTotal/baseTotalCount (server-pagination default view has empty listOpportunities)
  const totalCountForMatch =
    globalTotal > 0 ? globalTotal : (dashboardStats?.total ?? backendTotalCount);
  const catalogOpportunityCount =
    totalCountForMatch > 0 ? totalCountForMatch : listOpportunities.length;
  const showAutoMatchSection =
    catalogOpportunityCount > 0 ||
    listOpportunities.length > 0 ||
    (canUseServerPagination && (pageQueryData?.entities?.length ?? 0) > 0);
  const autoMatchWhenUnder200 = totalCountForMatch <= 200;
  const showManualRunFullRanking = showAutoMatchSection && totalCountForMatch > 200;
  const hasPersistedRankings = (rankingStats?.ranked_count ?? 0) > 0;
  const showPromptAutoMatch =
    showAutoMatchSection &&
    matchScores.size === 0 &&
    !hasPersistedRankings &&
    !isMatching &&
    !activeRankingTaskId;

  const hydrateMatchScoresFromQueryCache = useCallback(() => {
    const newMatchScores = new Map<string, number>();
    const hydrateFromEntities = (entities: Array<{ id: string; data?: { match_score?: number | null } }>) => {
      for (const entity of entities) {
        const score = entity.data?.match_score;
        if (score !== undefined && score !== null) {
          newMatchScores.set(String(entity.id), Number(score));
        }
      }
    };

    // Prefer the active page query (default SPARC view) — "all" is often disabled/stale.
    const pageEntries = queryClient.getQueriesData<{ entities?: Array<{ id: string; data?: { match_score?: number | null } }> }>({
      queryKey: ['govt-opportunities-page', projectId],
    });
    for (const [, data] of pageEntries) {
      if (data?.entities?.length) {
        hydrateFromEntities(data.entities);
        break;
      }
    }

    if (newMatchScores.size === 0) {
      const allEntries = queryClient.getQueriesData<{ allEntities?: Array<{ id: string; data?: { match_score?: number | null } }> }>({
        queryKey: ['govt-opportunities-all', projectId],
      });
      for (const [, data] of allEntries) {
        if (data?.allEntities?.length) {
          hydrateFromEntities(data.allEntities);
          break;
        }
      }
    }

    setMatchScores(newMatchScores);
  }, [projectId, queryClient]);

  const clearRankingTaskSession = useCallback(() => {
    try {
      sessionStorage.removeItem(RANKING_TASK_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const handleRankingJobComplete = useCallback(
    async (matched: number, total: number) => {
      setIsRankingJobQueued(false);
      setActiveRankingTaskId(null);
      clearRankingTaskSession();
      setRankingJobProgress(null);
      // Drop stale overlay scores so refetched entity.data.match_score / match_explain win.
      setMatchScores(new Map());
      setSortByMatchScore(true);
      setPage(1);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['govt-opportunities-all', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['govt-opportunities-page', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['govt-opportunities-filter-preview', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['govt-opportunities-dashboard-stats', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['govt-opportunities-zone-counts', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['jobs', projectId] }),
      ]);

      // Refetch after page reset so we pull page-1 scores (not the prior offset).
      await queryClient.refetchQueries({
        queryKey: ['govt-opportunities-page', projectId],
        type: 'active',
      });
      await queryClient.refetchQueries({
        queryKey: ['govt-opportunities-all', projectId],
        type: 'active',
      });

      try {
        await refetchRankingStats();
      } catch {
        /* non-fatal */
      }

      hydrateMatchScoresFromQueryCache();

      addToast({
        type: 'success',
        title: 'Ranking complete',
        message: `${formatNumber(matched)} of ${formatNumber(total)} opportunities ranked. Scores and order updated.`,
        duration: 8000,
      });
    },
    [projectId, queryClient, refetchRankingStats, hydrateMatchScoresFromQueryCache, addToast, clearRankingTaskSession],
  );

  const handleRankingJobFailed = useCallback(
    (errorMessage?: string) => {
      setIsRankingJobQueued(false);
      setActiveRankingTaskId(null);
      clearRankingTaskSession();
      setRankingJobProgress(null);
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      addToast({
        type: 'error',
        title: 'Ranking job failed',
        message: errorMessage || 'The background ranking job failed.',
        duration: 8000,
      });
    },
    [projectId, queryClient, addToast, clearRankingTaskSession],
  );

  // Poll ranking job status when we have an active task_id; refetch and clear when SUCCESS/FAILURE
  useEffect(() => {
    if (!activeRankingTaskId) return;
    const api = getAPIClient();
    let completionHandled = false;
    const poll = async () => {
      try {
        const res = await api.get<{
          status: string;
          total_opportunities?: number;
          total_matched?: number;
          chunks_processed?: number;
          opportunities_processed?: number;
          eta_seconds?: number;
          opportunities_per_second?: number;
          error?: string;
        }>(`/api/opportunities/ranking-job-status?task_id=${encodeURIComponent(activeRankingTaskId)}`);
        if (res.status === 'SUCCESS') {
          if (completionHandled) return;
          completionHandled = true;
          const total = res.total_opportunities ?? 0;
          const matched = res.total_matched ?? 0;
          try {
            await handleRankingJobComplete(matched, total);
          } catch (err: any) {
            handleRankingJobFailed(err?.message || 'Ranking finished but the list failed to refresh.');
          }
        } else if (res.status === 'FAILURE') {
          if (completionHandled) return;
          completionHandled = true;
          handleRankingJobFailed(res.error);
        } else if (res.status === 'PROGRESS' || res.status === 'STARTED') {
          setRankingJobProgress((prev) => {
            // Keep the first reported job size — never let a worker restart look like a countdown.
            const frozenTotal =
              prev?.total_opportunities && prev.total_opportunities > 0
                ? prev.total_opportunities
                : res.total_opportunities;
            const processed = Math.max(
              prev?.opportunities_processed ?? 0,
              res.opportunities_processed ?? 0,
            );
            const matched = Math.max(prev?.total_matched ?? 0, res.total_matched ?? 0);
            const chunks = Math.max(prev?.chunks_processed ?? 0, res.chunks_processed ?? 0);
            return {
              total_opportunities: frozenTotal,
              total_matched: matched,
              chunks_processed: chunks,
              opportunities_processed: processed,
              eta_seconds: res.eta_seconds ?? prev?.eta_seconds,
              opportunities_per_second: res.opportunities_per_second ?? prev?.opportunities_per_second,
            };
          });
        }
      } catch {
        // Keep polling on network errors
      }
    };
    const interval = setInterval(poll, 3000);
    poll();
    return () => clearInterval(interval);
  }, [activeRankingTaskId, handleRankingJobComplete, handleRankingJobFailed]);

  // Extract search suggestions from policy pack or config
  // Use search history instead of suggestions
  const displaySearchHistory = useMemo(() => {
    return searchHistory.slice(0, 3); // Show only last 3 searches
  }, [searchHistory]);

  // Mutation: Update tracking status
  const updateTrackingMutation = useMutation({
    mutationFn: async ({
      entityId,
      trackingStatus,
    }: {
      entityId: string;
      trackingStatus: string;
    }) => {
      const api = getAPIClient();
      // Convert empty string to null to clear tracking status
      const statusValue = trackingStatus === '' ? null : trackingStatus;
      return await api.put(`/entities/${entityId}`, {
        data: {
          tracking_status: statusValue,
          assigned_by: statusValue ? currentUserName : null,
        },
      });
    },
    onMutate: async ({ entityId, trackingStatus }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['govt-opportunities-all', projectId] });
      await queryClient.cancelQueries({ queryKey: ['govt-opportunities-page', projectId] });

      // Snapshot all previous query data for rollback
      const previousQueries: Record<string, any> = {};

      const newTrackingStatus = trackingStatus === '' ? undefined : trackingStatus;
      const newAssignedBy = trackingStatus === '' ? undefined : currentUserName;

      // Get all matching query keys and snapshot + optimistically update them
      const queryCache = queryClient.getQueryCache();
      queryCache.getAll().forEach((query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[1] !== projectId) return;

        if (key[0] === 'govt-opportunities-all') {
          previousQueries[JSON.stringify(key)] = query.state.data;

          queryClient.setQueryData(key, (old: any) => {
            if (!old?.allEntities) return old;
            const updatedEntities = old.allEntities.map((opp: GovtOpportunityEntity) =>
              opp.id === entityId
                ? { ...opp, data: { ...opp.data, tracking_status: newTrackingStatus, assigned_by: newAssignedBy } }
                : opp
            );
            const watching = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'watching').length;
            const bidding = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'bidding').length;
            const won = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'won').length;
            const lost = updatedEntities.filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'lost').length;
            const trackedOpportunities = updatedEntities.filter(
              (o: GovtOpportunityEntity) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
            ).length;
            const pipelineValue = updatedEntities
              .filter((o: GovtOpportunityEntity) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
              .reduce((sum: number, o: GovtOpportunityEntity) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);
            return {
              ...old,
              allEntities: updatedEntities,
              stats: { ...old.stats, watching, bidding, won, lost, trackedOpportunities, pipelineValue },
            };
          });
        }

        if (key[0] === 'govt-opportunities-page') {
          previousQueries[JSON.stringify(key)] = query.state.data;

          // Optimistically update server-paginated page results (default unfiltered view)
          queryClient.setQueryData(key, (old: any) => {
            if (!old?.entities) return old;
            const updatedEntities = old.entities.map((e: any) =>
              e.id === entityId
                ? { ...e, data: { ...e.data, tracking_status: newTrackingStatus, assigned_by: newAssignedBy } }
                : e
            );
            return { ...old, entities: updatedEntities };
          });
        }
      });

      return { previousQueries };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        Object.entries(context.previousQueries).forEach(([keyStr, data]) => {
          const key = JSON.parse(keyStr);
          queryClient.setQueryData(key, data);
        });
      }
    },
    onSuccess: (data: any, variables) => {
      const serverData = data?.data ?? {};
      const persistedTrackingStatus = serverData.tracking_status ?? (variables.trackingStatus === '' ? undefined : variables.trackingStatus);
      const queryCache = queryClient.getQueryCache();
      let updatedOpportunity: GovtOpportunityEntity | null = null;

      const recalcStats = (entities: GovtOpportunityEntity[]) => {
        const watching = entities.filter((o) => o.data.tracking_status === 'watching').length;
        const bidding = entities.filter((o) => o.data.tracking_status === 'bidding').length;
        const won = entities.filter((o) => o.data.tracking_status === 'won').length;
        const lost = entities.filter((o) => o.data.tracking_status === 'lost').length;
        const trackedOpportunities = entities.filter(
          (o) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
        ).length;
        const pipelineValue = entities
          .filter((o) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
          .reduce((sum, o) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);
        return { watching, bidding, won, lost, trackedOpportunities, pipelineValue };
      };

      queryCache.getAll().forEach((query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[1] !== projectId) return;

        if (key[0] === 'govt-opportunities-all') {
          queryClient.setQueryData(key, (old: any) => {
            if (!old?.allEntities) return old;
            const existingIdx = old.allEntities.findIndex((o: GovtOpportunityEntity) => o.id === variables.entityId);

            if (existingIdx >= 0) {
              const opp = old.allEntities[existingIdx];
              const merged = {
                ...opp.data,
                tracking_status: persistedTrackingStatus as GovtOpportunityEntity['data']['tracking_status'],
                assigned_by:
                  (typeof serverData.assigned_by === 'string' && serverData.assigned_by.trim()
                    ? serverData.assigned_by
                    : variables.trackingStatus === ''
                      ? undefined
                      : currentUserName),
              };
              if (merged.days_until_deadline == null && merged.deadline) {
                merged.days_until_deadline = Math.ceil(
                  (new Date(merged.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                );
              }
              const updated: GovtOpportunityEntity = { ...opp, data: merged };
              updatedOpportunity = updated;
              const updatedEntities = old.allEntities.map((o: GovtOpportunityEntity, i: number) =>
                i === existingIdx ? updated : o
              );
              return { ...old, allEntities: updatedEntities, stats: recalcStats(updatedEntities) };
            }

            if (data) {
              const newEntity: GovtOpportunityEntity = {
                id: variables.entityId,
                entity_type: 'govt_opportunity',
                data: {
                  ...serverData,
                  tracking_status: persistedTrackingStatus as GovtOpportunityEntity['data']['tracking_status'],
                },
                created_at: data.created_at,
              };
              if (newEntity.data.days_until_deadline == null && newEntity.data.deadline) {
                newEntity.data.days_until_deadline = Math.ceil(
                  (new Date(newEntity.data.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                );
              }
              updatedOpportunity = newEntity;
              const updatedEntities = [...old.allEntities, newEntity];
              return { ...old, allEntities: updatedEntities, stats: recalcStats(updatedEntities) };
            }
            return old;
          });
        }

        // Also reconcile server-paginated page caches (default unfiltered card view)
        if (key[0] === 'govt-opportunities-page') {
          queryClient.setQueryData(key, (old: any) => {
            if (!old?.entities) return old;
            const updatedEntities = old.entities.map((e: any) => {
              if (e.id !== variables.entityId) return e;
              const updated = { ...e, data: { ...e.data, tracking_status: persistedTrackingStatus } };
              // Capture for selectedOpportunity sync if not already found in allEntities
              if (!updatedOpportunity) {
                updatedOpportunity = {
                  id: e.id,
                  entity_type: e.entity_type || 'govt_opportunity',
                  data: updated.data,
                  created_at: e.created_at,
                  updated_at: e.updated_at,
                } as GovtOpportunityEntity;
              }
              return updated;
            });
            return { ...old, entities: updatedEntities };
          });
        }
      });

      // Update selectedOpportunity if it matches the updated entity
      if (updatedOpportunity && selectedOpportunity?.id === variables.entityId) {
        setSelectedOpportunity(updatedOpportunity);
      }

      // Don't invalidate here — it triggers a slow refetch when filterTrackingStatus is set.
      // Cache update above keeps UI in sync; run migrations/015_add_govt_opportunity_tracking_index.sql to speed up tracking filter.

      // Notification: when user sets tracking to "bidding", remind about deadline if due soon
      const oppForToast = updatedOpportunity as GovtOpportunityEntity | null;
      if (variables.trackingStatus === 'bidding' && oppForToast?.data) {
        const data = oppForToast.data;
        let days = data.days_until_deadline;
        if (days == null && data.deadline) {
          days = Math.ceil(
            (new Date(data.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
          );
        }
        const title = data.title || 'Opportunity';
        if (days != null && days >= 0 && days <= 7) {
          const deadlineStr = data.deadline
            ? new Date(data.deadline).toLocaleDateString('en-US', { dateStyle: 'medium' })
            : '';
          addToast({
            type: 'info',
            title: 'Deadline reminder',
            message: deadlineStr
              ? `"${title.slice(0, 40)}${title.length > 40 ? '…' : ''}" is due in ${days} day${days === 1 ? '' : 's'} (${deadlineStr}).`
              : `"${title.slice(0, 40)}${title.length > 40 ? '…' : ''}" is due in ${days} day${days === 1 ? '' : 's'}.`,
            duration: 6000,
          });
        }
      }

      // Background-invalidate both caches so next visit is fresh (non-blocking)
      queryClient.invalidateQueries({
        queryKey: ['govt-opportunities-all', projectId],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['govt-opportunities-page', projectId],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['weekly-global-opportunities'],
        refetchType: 'active',
      });
    },
  });

  const updatePipelineStageMutation = useMutation({
    mutationFn: async ({
      entityId,
      pipelineStage,
    }: {
      entityId: string;
      pipelineStage: PipelineStage;
    }) => {
      if (!projectId) throw new Error('Project ID required');
      return await updateOpportunityPipelineStage(entityId, projectId, pipelineStage);
    },
    onMutate: ({ entityId }) => {
      setPipelineTransitionId(entityId);
    },
    onSuccess: (_data, variables) => {
      if (projectId) {
        applyPipelineStageToOpportunityCaches(
          queryClient,
          projectId,
          variables.entityId,
          variables.pipelineStage,
        );
      }
      addToast({
        type: 'success',
        title: 'Pipeline stage updated',
        message: `Moved to ${variables.pipelineStage.replace('_', ' ')}`,
        duration: 3000,
      });
    },
    onError: (err: Error) => {
      addToast({
        type: 'error',
        title: 'Could not update stage',
        message: err?.message || 'Admin role required for pipeline transitions.',
        duration: 5000,
      });
    },
    onSettled: () => {
      setPipelineTransitionId(null);
      queryClient.invalidateQueries({
        queryKey: [GOVT_OPPORTUNITIES_KANBAN_QUERY_KEY, projectId],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: [GOVT_OPPORTUNITIES_ALL_QUERY_KEY, projectId],
        refetchType: 'active',
      });
    },
  });

  const updateBdPriorityMutation = useMutation({
    mutationFn: async ({
      entityId,
      bdPriority,
    }: {
      entityId: string;
      bdPriority: BdPriority | null;
    }) => {
      if (!projectId) throw new Error('Project ID required');
      return await updateOpportunityBdPriority(entityId, projectId, bdPriority);
    },
    onMutate: ({ entityId }) => {
      setPriorityUpdatingId(entityId);
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ['govt-opportunities-all', projectId] },
        (old: any) => {
          if (!old?.allEntities) return old;
          return {
            ...old,
            allEntities: old.allEntities.map((opp: GovtOpportunityEntity) =>
              opp.id === variables.entityId
                ? {
                    ...opp,
                    data: {
                      ...opp.data,
                      ...(variables.bdPriority
                        ? { bd_priority: variables.bdPriority }
                        : { bd_priority: undefined }),
                    },
                  }
                : opp
            ),
          };
        }
      );
      addToast({
        type: 'success',
        title: 'Priority updated',
        message: variables.bdPriority
          ? `Set to ${variables.bdPriority}`
          : 'Reverted to auto priority from tracking status',
        duration: 3000,
      });
    },
    onError: (err: Error) => {
      addToast({
        type: 'error',
        title: 'Could not update priority',
        message: err?.message || 'Admin role required for priority updates.',
        duration: 5000,
      });
    },
    onSettled: () => {
      setPriorityUpdatingId(null);
      queryClient.invalidateQueries({
        queryKey: ['govt-opportunities-all', projectId],
        refetchType: 'active',
      });
    },
  });

  // Filtered opportunities - now just use the paginated opportunities (filtering/sorting already done above)
  const filteredOpportunities = useMemo(() => {
    // Filtering and sorting are now done in allOpportunities useMemo above
    // This is just for compatibility with existing code that references filteredOpportunities
    return opportunities;
  }, [opportunities]);

  const kanbanSourceOpportunities = kanbanOpportunitiesData?.entities ?? [];

  const kanbanAssignedOptions = useMemo(() => {
    const names = new Set<string>();
    kanbanSourceOpportunities.forEach((opp) => {
      const name = opp.data.assigned_bd_person;
      if (name && String(name).trim()) names.add(String(name).trim());
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [kanbanSourceOpportunities]);

  const kanbanDisplayOpportunities = useMemo(() => {
    let filtered = kanbanSourceOpportunities.map((opp) => {
      const enhanced = enhanceOpportunityEntity(opp);
      const runtimeScore = matchScores.get(String(opp.id));
      if (runtimeScore !== undefined) {
        enhanced.data.match_score = runtimeScore;
      }
      return enhanced;
    });
    if (!showKanbanDiscarded) {
      filtered = filtered.filter((opp) => !isDiscardedOpportunity(opp.data));
    }
    if (filterKanbanPriority !== 'all') {
      filtered = filtered.filter(
        (opp) => getEffectiveBdPriority(opp.data) === filterKanbanPriority
      );
    }
    if (filterKanbanAssigned !== 'all') {
      filtered = filtered.filter(
        (opp) => String(opp.data.assigned_bd_person || '').trim() === filterKanbanAssigned
      );
    }
    if (kanbanWeeklyMeetingMode) {
      filtered = filtered.filter((opp) => {
        const tracking = String(opp?.data?.tracking_status || '').trim().toLowerCase();
        return tracking === 'watching' || tracking === 'bidding';
      });
      const priorityOrder: Record<string, number> = { P1: 0, P2: 1, P3: 2, P4: 3 };
      filtered = [...filtered].sort((a, b) => {
        const pa = priorityOrder[getEffectiveBdPriority(a.data) || 'P4'] ?? 4;
        const pb = priorityOrder[getEffectiveBdPriority(b.data) || 'P4'] ?? 4;
        return pa - pb;
      });
    }
    return filtered;
  }, [kanbanSourceOpportunities, filterKanbanPriority, filterKanbanAssigned, showKanbanDiscarded, kanbanWeeklyMeetingMode, matchScores]);

  // Calendar view: date-range fetch + same client-side filters as the list (not paginated page slice).
  const calendarDisplayOpportunities = useMemo(() => {
    let filtered = calendarOpportunitiesData?.entities ?? [];

    if (filterSource !== 'all') {
      const isUuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(filterSource);
      if (!isUuidLike) {
        const expected = filterSource.toLowerCase().trim();
        filtered = filtered.filter((opp) => {
          const candidateSources = [
            opp?.data?.source,
            (opp?.data as any)?.integration_type,
            (opp?.data as any)?.source_system,
            (opp?.data as any)?.raw_data?.source,
            (opp?.data as any)?.source_name,
          ]
            .filter((value) => typeof value === 'string')
            .map((value) => String(value).toLowerCase().trim());
          return candidateSources.includes(expected);
        });
      }
    }

    // Portal status is applied server-side on calendar fetch filters.

    if (filterTrackingStatus !== 'all') {
      if (filterTrackingStatus === 'tracked') {
        filtered = filtered.filter((opp) => {
          const t = String(opp?.data?.tracking_status || '').trim().toLowerCase();
          return t === 'watching' || t === 'bidding';
        });
      } else {
        const expected = String(filterTrackingStatus || '').trim().toLowerCase();
        filtered = filtered.filter((opp) => String(opp?.data?.tracking_status || '').trim().toLowerCase() === expected);
      }
    }

    if (filterHasDocuments !== null) {
      filtered = filtered.filter((opp) => {
        const rfpDocuments = opp.data.rfp_documents;
        const hasDocuments = Array.isArray(rfpDocuments) && rfpDocuments.length > 0;
        return filterHasDocuments ? hasDocuments : !hasDocuments;
      });
    }

    const excludeTerms = queryFilters._exclude_terms;
    if (excludeTerms && Array.isArray(excludeTerms) && excludeTerms.length > 0) {
      filtered = filtered.filter((opp) => {
        const searchableText = [
          opp.data.title,
          opp.data.description,
          opp.data.organization,
          opp.data.issuing_agency,
          ...(opp.data.keywords || []),
          ...(opp.data.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return !excludeTerms.some((term) => searchableText.includes(term.toLowerCase()));
      });
    }

    return filtered;
  }, [
    calendarOpportunitiesData?.entities,
    filterSource,
    filterStatus,
    filterTrackingStatus,
    filterHasDocuments,
    queryFilters._exclude_terms,
  ]);

  // Summary cards use the same client-filtered set as the list (source, portal status, stage, deadlines, etc.)
  // so card counts match "Showing … of …" and the table. When no filters are active, Total still uses the global project count.
  const stats = useMemo((): OpportunityStats => {
    const statsBase = allOpportunities;

    const watching = statsBase.filter((o) => o.data.tracking_status === 'watching').length;
    const bidding = statsBase.filter((o) => o.data.tracking_status === 'bidding').length;
    const won = statsBase.filter((o) => o.data.tracking_status === 'won').length;
    const lost = statsBase.filter((o) => o.data.tracking_status === 'lost').length;
    const trackedOpportunities = statsBase.filter(
      (o) => o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding'
    ).length;

    const trackedInOpenScope = statsBase.filter(
      (o) =>
        isGovernmentOpportunityOpen(o) &&
        (o.data.tracking_status === 'watching' || o.data.tracking_status === 'bidding')
    ).length;

    const matchScores = statsBase
      .map((o) => o.data.match_score)
      .filter((score): score is number => score != null);
    const avgMatchScore = matchScores.length > 0
      ? matchScores.reduce((sum, score) => sum + score, 0) / matchScores.length
      : 0;

    const upcomingDeadlines = statsBase.filter(
      (o) => o.data.days_until_deadline !== null &&
        o.data.days_until_deadline !== undefined &&
        o.data.days_until_deadline >= 0 &&
        o.data.days_until_deadline <= 30
    ).length;

    const openOpportunities = statsBase.filter(isGovernmentOpportunityOpen).length;

    const pipelineValue = statsBase
      .filter((o) => o.data.tracking_status === 'bidding' || o.data.tracking_status === 'watching')
      .reduce((sum, o) => sum + (o.data.value || o.data.estimated_value || o.data.contract_value || 0), 0);

    const deadlines = statsBase
      .map((o) => o.data.days_until_deadline)
      .filter((d): d is number => d !== null && d !== undefined && d >= 0);
    const avgDaysUntilDeadline = deadlines.length > 0
      ? Math.round(deadlines.reduce((sum, d) => sum + d, 0) / deadlines.length)
      : null;

    // Keep "Total Opportunities" aligned with what the dashboard is currently showing.
    // Using global totals here causes mismatches vs "Showing X of Y opportunities" on initial load.
    const total = baseTotalCount;

    // In the default (unfiltered) view, prefer the fast backend-cached stat counts over
    // counting from the local allOpportunities list (which may be empty if the 5k query
    // hasn't run yet because hasClientSideOnlyFilters=false).
    const useBackendStats =
      isDashboardDefaultFilterState &&
      !(statCardSelection !== null && statCardSelection !== 'total') &&
      dashboardStats != null;

    return {
      total,
      watching,
      bidding,
      won,
      lost,
      pipelineValue,
      avgMatchScore,
      upcomingDeadlines: useBackendStats ? dashboardStats!.upcoming_deadlines_count : upcomingDeadlines,
      openOpportunities: useBackendStats ? dashboardStats!.open_count : openOpportunities,
      trackedOpportunities: useBackendStats ? dashboardStats!.tracked_count : trackedOpportunities,
      trackedInOpenScope,
      avgDaysUntilDeadline,
    };
  }, [
    allOpportunities,
    dashboardStats,
    hasClientSideOnlyFilters,
    isDashboardDefaultFilterState,
    statCardSelection,
    hasActiveFilters,
    globalOpportunitiesTotalData,
    opportunitiesData?.total,
    baseTotalCount,
  ]);

  // Get unique service lines from loaded opportunities
  const serviceLines = useMemo(() => {
    const lines = new Set<string>();
    opportunities.forEach((opp) => {
      if (opp.data.matching_service_lines) {
        opp.data.matching_service_lines.forEach((line) => lines.add(line));
      }
    });
    return Array.from(lines).sort();
  }, [opportunities]);

  // Sources dropdown: zone-scoped registry snapshot only. Do not rebuild from the
  // filtered entity page or the list shrinks after apply. Hide 0-count outside World.
  const { data: sourcesData } = useQuery({
    queryKey: ['entity-sources', projectId, 'govt_opportunity', selectedGeographicZone],
    queryFn: async () => {
      if (!projectId) return { sources: [], total: 0 };
      const zone = getGeographicZoneFilterValue(selectedGeographicZone);
      return await getEntitySources(projectId, 'govt_opportunity', {
        geographic_zone: zone,
      });
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const sourceFilterOptions = useMemo(() => {
    const isWorldZone = !getGeographicZoneFilterValue(selectedGeographicZone);
    const prettifySourceLabel = (raw: string): string => {
      const cleaned = raw
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return cleaned
        ? cleaned.replace(/\b\w/g, (c) => c.toUpperCase())
        : 'Unknown';
    };

    const registryDisplayName = (integrationType: string): string | null => {
      const entry = dataSourceRegistry.get(integrationType as DataSourceType);
      return entry?.metadata?.name ?? null;
    };

    const cleanBackendLabel = (raw: string): string =>
      raw
        .replace(/API Integration/gi, '').trim()
        .replace(/- Configured from Policy Pack/gi, '').trim()
        .replace(/API$/gi, '').trim();

    const formatLabelWithCount = (baseLabel: string, count: number) =>
      `${baseLabel} (${formatNumber(count)})`;

    const seen = new Set<string>();
    const registryOptions: { value: string; label: string }[] = [];

    (sourcesData?.sources || []).forEach((s: any) => {
      const count = Number(s.entity_count ?? 0);
      if (!shouldShowSourceInZoneDropdown(count, isWorldZone)) return;

      const integrationType = (s.integration_type || '').trim();
      const label =
        registryDisplayName(integrationType) ||
        cleanBackendLabel(
          s.source_name || s.integration_type || s.source_id || ''
        ) ||
        prettifySourceLabel(integrationType || s.source_id || 'Unknown');

      const canonicalValue = integrationType || (s.source_name || s.source_id || '').trim();
      if (canonicalValue && !seen.has(canonicalValue)) {
        seen.add(canonicalValue);
        registryOptions.push({
          value: canonicalValue,
          label: formatLabelWithCount(label, count),
        });
      }
    });

    return registryOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [sourcesData, selectedGeographicZone]);

  const getSourceLabel = (value: string) =>
    sourceFilterOptions.find((o) => o.value === value)?.label ?? value;

  // Province/City/Client options come from live opportunity facets (GET /api/opportunities/filter-options).
  // Each dropdown list is constrained by the OTHER selected draft filters (cascading counts).
  const {
    data: opportunityFilterOptions,
    isFetching: isFetchingOpportunityFilterOptions,
  } = useQuery({
    queryKey: [
      'opportunity-filter-options',
      projectId,
      draftFilterProvince,
      draftFilterCity,
      draftFilterCountry,
      draftClient,
      selectedGeographicZone,
    ],
    queryFn: async () =>
      getOpportunityFilterOptions({
        project_id: projectId!,
        province: draftFilterProvince,
        city: draftFilterCity,
        country: draftFilterCountry,
        client: draftClient,
        ...(getGeographicZoneFilterValue(selectedGeographicZone)
          ? { geographic_zone: getGeographicZoneFilterValue(selectedGeographicZone)! }
          : {}),
      }),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });

  const facetOptionsWithCounts = useCallback(
    (items: { value: string; count: number }[] | undefined) =>
      (items ?? []).map((item) => ({
        value: item.value,
        label: `${item.value} (${item.count.toLocaleString()})`,
      })),
    []
  );

  const draftProvinceFilterOptions = useMemo(() => {
    const base = facetOptionsWithCounts(opportunityFilterOptions?.provinces);
    if (draftFilterProvince !== 'all' && !base.some((o) => o.value === draftFilterProvince)) {
      return [...base, { value: draftFilterProvince, label: draftFilterProvince }];
    }
    return base;
  }, [opportunityFilterOptions?.provinces, draftFilterProvince, facetOptionsWithCounts]);

  const draftCityFilterOptions = useMemo(() => {
    const base = facetOptionsWithCounts(opportunityFilterOptions?.cities);
    if (draftFilterCity !== 'all' && !base.some((o) => o.value === draftFilterCity)) {
      return [...base, { value: draftFilterCity, label: draftFilterCity }];
    }
    return base;
  }, [opportunityFilterOptions?.cities, draftFilterCity, facetOptionsWithCounts]);

  const draftCountryFilterOptions = useMemo(() => {
    const base = facetOptionsWithCounts(opportunityFilterOptions?.countries);
    if (draftFilterCountry !== 'all' && !base.some((o) => o.value === draftFilterCountry)) {
      return [...base, { value: draftFilterCountry, label: draftFilterCountry }];
    }
    return base;
  }, [opportunityFilterOptions?.countries, draftFilterCountry, facetOptionsWithCounts]);

  const clientComboboxOptions = useMemo(() => {
    const base = facetOptionsWithCounts(opportunityFilterOptions?.clients);
    if (draftClient !== 'all' && !base.some((o) => o.value === draftClient)) {
      return [...base, { value: draftClient, label: draftClient }];
    }
    return base;
  }, [opportunityFilterOptions?.clients, draftClient, facetOptionsWithCounts]);

  const formatFilterCountLabel = useCallback(
    (base: string, count: number | undefined) =>
      count != null ? `${base} (${formatNumber(count)})` : base,
    []
  );

  const filterPreviewEntityPool = useMemo(() => {
    if (opportunitiesData?.allEntities?.length) {
      return opportunitiesData.allEntities;
    }
    if (filterPreviewSnapshot?.length) {
      return filterPreviewSnapshot;
    }
    if (pageQueryData?.entities?.length) {
      return (pageQueryData.entities as any[])
        .filter((e: { id: string }) => !BLOCKED_OPPORTUNITY_ENTITY_IDS.has(e.id))
        .map((e: any) => {
          const tmp: GovtOpportunityEntity = {
            id: e.id,
            entity_type: e.entity_type || 'govt_opportunity',
            data: e.data || {},
            created_at: e.created_at || new Date().toISOString(),
          };
          return {
            ...tmp,
            data: {
              ...(tmp.data || {}),
              days_until_deadline: getDaysUntilDeadlineForOpp(tmp),
            },
          };
        });
    }
    return [];
  }, [opportunitiesData?.allEntities, filterPreviewSnapshot, pageQueryData?.entities]);

  const filterOptionCounts = useMemo(() => {
    const pool = filterPreviewEntityPool;
    const poolLoaded = pool.length > 0;
    const catalogTotal = poolLoaded
      ? pool.length
      : (dashboardStats?.total ?? baseTotalCount ?? 0);

    let withDocuments = 0;
    let openCount = 0;
    let closedCount = 0;
    let watching = 0;
    let bidding = 0;
    let won = 0;
    let lost = 0;
    let declined = 0;
    let tracked = 0;
    const deadlineCounts: Record<DeadlinePreset, number> = {
      all: 0,
      next7: 0,
      next14: 0,
      next30: 0,
      next60: 0,
      next90: 0,
      this_month: 0,
      next_month: 0,
    };

    if (poolLoaded) {
      pool.forEach((opp) => {
        if (opportunityHasUploadedDocuments(opp)) withDocuments++;
        if (isGovernmentOpportunityOpen(opp)) openCount++;
        else closedCount++;

        const tracking = String(opp.data.tracking_status || '').trim().toLowerCase();
        if (tracking === 'watching') watching++;
        if (tracking === 'bidding') bidding++;
        if (tracking === 'won') won++;
        if (tracking === 'lost') lost++;
        if (tracking === 'declined') declined++;
        if (tracking === 'watching' || tracking === 'bidding') tracked++;

        deadlineCounts.all++;
        (Object.keys(deadlineCounts) as DeadlinePreset[]).forEach((preset) => {
          if (preset !== 'all' && deadlinePresetMatchesDaysUntil(preset, opp.data.days_until_deadline)) {
            deadlineCounts[preset]++;
          }
        });
      });
    } else {
      openCount = dashboardStats?.open_count ?? dashboardStats?.total ?? 0;
      tracked = dashboardStats?.tracked_count ?? 0;
      deadlineCounts.next30 = dashboardStats?.upcoming_deadlines_count ?? 0;
    }

    return {
      poolLoaded,
      catalogTotal,
      documents: {
        all: catalogTotal,
        has: poolLoaded ? withDocuments : undefined,
        none: poolLoaded ? pool.length - withDocuments : undefined,
      },
      portalStatus: {
        all: poolLoaded ? pool.length : catalogTotal,
        open: poolLoaded ? openCount : (dashboardStats?.open_count ?? dashboardStats?.total),
        closed: poolLoaded ? closedCount : undefined,
      },
      tracking: {
        all: poolLoaded ? pool.length : catalogTotal,
        watching: poolLoaded ? watching : undefined,
        bidding: poolLoaded ? bidding : undefined,
        won: poolLoaded ? won : undefined,
        lost: poolLoaded ? lost : undefined,
        declined: poolLoaded ? declined : undefined,
        tracked: poolLoaded ? tracked : dashboardStats?.tracked_count,
      },
      deadline: deadlineCounts,
    };
  }, [filterPreviewEntityPool, dashboardStats, baseTotalCount]);

  const showDocumentsUploadBanner =
    filterOptionCounts.poolLoaded &&
    filterOptionCounts.catalogTotal > 0 &&
    filterOptionCounts.documents.has === 0;

  const portalStatusFilterOptions = useMemo(
    () => [
      { value: 'all', label: formatFilterCountLabel('All', filterOptionCounts.portalStatus.all) },
      { value: 'open', label: formatFilterCountLabel('Open', filterOptionCounts.portalStatus.open) },
      { value: 'closed', label: formatFilterCountLabel('Closed', filterOptionCounts.portalStatus.closed) },
    ],
    [filterOptionCounts.portalStatus, formatFilterCountLabel]
  );

  const trackingStatusFilterOptions = useMemo(
    () => [
      { value: 'all', label: formatFilterCountLabel('All Stages', filterOptionCounts.tracking.all) },
      { value: 'watching', label: formatFilterCountLabel('Watching', filterOptionCounts.tracking.watching) },
      { value: 'bidding', label: formatFilterCountLabel('Bidding', filterOptionCounts.tracking.bidding) },
      { value: 'won', label: formatFilterCountLabel('Won', filterOptionCounts.tracking.won) },
      { value: 'lost', label: formatFilterCountLabel('Lost', filterOptionCounts.tracking.lost) },
      { value: 'declined', label: formatFilterCountLabel('Declined', filterOptionCounts.tracking.declined) },
      {
        value: 'tracked',
        label: formatFilterCountLabel('Watching + Bidding', filterOptionCounts.tracking.tracked),
      },
    ],
    [filterOptionCounts.tracking, formatFilterCountLabel]
  );

  const deadlineFilterOptions = useMemo(
    () =>
      DEADLINE_PRESET_OPTIONS.map((option) => ({
        value: option.value,
        label: formatFilterCountLabel(option.label, filterOptionCounts.deadline[option.value]),
      })),
    [filterOptionCounts.deadline, formatFilterCountLabel]
  );

  const documentsFilterOptions = useMemo(
    () => [
      { value: 'all', label: formatFilterCountLabel('All Opportunities', filterOptionCounts.documents.all) },
      { value: 'has', label: formatFilterCountLabel('Has Documents', filterOptionCounts.documents.has) },
      { value: 'none', label: formatFilterCountLabel('No Documents', filterOptionCounts.documents.none) },
    ],
    [filterOptionCounts.documents, formatFilterCountLabel]
  );

  // If any current draft selection becomes invalid given the constrained option sets,
  // reset it to 'all' (keeps the UI consistent with other dropdowns).
  useEffect(() => {
    // Avoid clearing selections during cascaded refetch; only validate after queries settle.
    if (isFetchingOpportunityFilterOptions) {
      return;
    }

    const provinces = new Set(draftProvinceFilterOptions.map((o) => o.value));
    const cities = new Set(draftCityFilterOptions.map((o) => o.value));
    const clients = new Set(clientComboboxOptions.map((o) => o.value));

    if (draftFilterProvince !== 'all' && !provinces.has(draftFilterProvince)) {
      setDraftFilterProvince('all');
    }
    if (draftFilterCity !== 'all' && !cities.has(draftFilterCity)) {
      setDraftFilterCity('all');
    }
    if (draftClient !== 'all' && !clients.has(draftClient)) {
      setDraftClient('all');
    }
  }, [
    draftProvinceFilterOptions,
    draftCityFilterOptions,
    clientComboboxOptions,
    draftFilterProvince,
    draftFilterCity,
    draftClient,
    isFetchingOpportunityFilterOptions,
  ]);

  const sortedDbSuggestions = useMemo(
    () =>
      [...(dbAutocompleteData?.suggestions || [])].sort((a, b) =>
        String(a.value || '').localeCompare(String(b.value || ''), undefined, { sensitivity: 'base' })
      ),
    [dbAutocompleteData?.suggestions]
  );

  // Handler: Update tracking status
  const handleUpdateTracking = (entityId: string, trackingStatus: string) => {
    updateTrackingMutation.mutate({ entityId, trackingStatus });
  };

  // SPARC-ISSUE-014 (Global Opportunities cluster): export supports CSV, Excel, and
  // PDF via a format chooser (previously CSV-only). Row shape is shared across all
  // three formats so columns stay consistent regardless of which one is picked.
  const buildExportRows = () =>
    filteredOpportunities.map((opp) => ({
      Title: opp.data.title || '',
      Organization: opp.data.organization || '',
      Status: opp.data.status || '',
      Tracking: opp.data.tracking_status || '',
      Deadline: opp.data.deadline || '',
      Value: opp.data.value?.toString() || '',
      Source: opp.data.source || '',
      URL: opp.data.url || '',
    }));

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    const rows = buildExportRows();
    if (rows.length === 0) {
      addToast({ type: 'error', message: 'No opportunities to export with the current filters.' });
      return;
    }
    const datestamp = new Date().toISOString().split('T')[0];
    if (format === 'csv') {
      exportToCSV(rows, `opportunities-${datestamp}.csv`);
    } else if (format === 'excel') {
      exportToExcel(rows, `opportunities-${datestamp}.xlsx`, {
        sheetName: 'Opportunities',
        autoFilter: true,
        freezeHeader: true,
      });
    } else {
      exportToPDF(rows, `opportunities-${datestamp}.pdf`, {
        title: 'Government Opportunities Export',
        orientation: 'landscape',
      });
    }
    setShowExportModal(false);
    addToast({ type: 'success', message: `Exported ${rows.length} opportunit${rows.length === 1 ? 'y' : 'ies'} to ${format.toUpperCase()}.` });
  };

  // Pagination handlers
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => {
    if (hasMore) setPage((p) => p + 1);
  };

  // Ref so auto-run effect always calls the latest handleAutoMatch (avoids stale closure)
  const handleAutoMatchRef = useRef<() => Promise<void>>(async () => { });

  // Handler: Auto-match opportunities using new batch vector matching endpoint (called by button and by auto-run effect)
  const handleAutoMatch = async () => {
    if (!projectId) return;
    if (process.env.NODE_ENV === 'development') {
      console.log('[OpportunitiesDashboard] handleAutoMatch called', { projectId, opportunityCount: listOpportunities.length });
    }
    setIsMatching(true);
    try {
      const apiClient = getAPIClient();

      // Match current view: full client list, or server-paginated page when catalog query is deferred
      const oppsToMatch =
        listOpportunities.length > 0
          ? listOpportunities
          : opportunities;
      const opportunityIds = oppsToMatch.map((opp) => opp.id);

      // Call new batch matching endpoint
      const response = await apiClient.post('/api/opportunities/batch-match', {
        project_id: projectId,
        opportunity_ids: opportunityIds.length > 0 ? opportunityIds : undefined, // If empty, match all
        match_against: 'both',
        limit_per_opportunity: 50,
        min_similarity_threshold: 0.15,
      });

      // Store match scores in a Map for quick lookup
      const newMatchScores = new Map<string, number>();
      response.results.forEach((result: any) => {
        newMatchScores.set(String(result.opportunity_id), result.match_score);
      });

      setMatchScores(newMatchScores);

      // Enable sorting by match score
      setSortByMatchScore(true);

      // Update all catalog cache variants (full queryKey includes zone/filters/viewMode).
      queryClient.setQueriesData({ queryKey: ['govt-opportunities-all', projectId] }, (oldData: any) => {
        if (!oldData?.allEntities) return oldData;

        const updatedEntities = oldData.allEntities.map((entity: any) => {
          const matchScore = newMatchScores.get(String(entity.id));
          if (matchScore !== undefined) {
            return {
              ...entity,
              data: {
                ...entity.data,
                match_score: matchScore,
              },
            };
          }
          return entity;
        });

        return {
          ...oldData,
          allEntities: updatedEntities,
        };
      });

      // Issue 20: default landing uses server pageQuery (ORDER BY data.match_score).
      // Scores + match_explain are persisted by batch-match — refetch so page 1 re-sorts.
      setPage(1);
      await queryClient.refetchQueries({
        queryKey: ['govt-opportunities-page', projectId],
        type: 'active',
      });
      await queryClient.refetchQueries({
        queryKey: ['govt-opportunities-all', projectId],
        type: 'active',
      });

      try {
        await refetchRankingStats();
      } catch {
        /* non-fatal */
      }

      hydrateMatchScoresFromQueryCache();

      const matchedCount = response.results.filter((r: any) => r.match_score > 0).length;
      if (process.env.NODE_ENV === 'development') {
        console.log('[OpportunitiesDashboard] handleAutoMatch completed', { matchedCount, total: response.total_opportunities });
      }
      if (matchedCount > 0) {
        addToast({
          type: 'success',
          title: 'Auto-Match complete',
          message: `Successfully matched ${formatNumber(matchedCount)} of ${formatNumber(response.total_opportunities)} opportunities in ${(response.processing_time_ms / 1000).toFixed(1)}s!`,
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('Auto-match failed:', error);
      addToast({ type: 'error', title: 'Auto-match failed', message: error.message || 'Unknown error' });
    } finally {
      setIsMatching(false);
    }
  };

  handleAutoMatchRef.current = handleAutoMatch;

  // Run full catalog ranking as a background job (all opportunities; scores persisted to entity.data)
  const handleRunFullRankingJob = async () => {
    if (!projectId) return;
    setIsRankingJobQueued(true);
    setRankingJobProgress(null);
    try {
      const apiClient = getAPIClient();
      const response = await apiClient.post<{
        task_id: string;
        job_id?: string;
        status: string;
        message: string;
      }>('/api/opportunities/run-full-ranking-job', { project_id: projectId });
      setActiveRankingTaskId(response.task_id);
      setIsRankingJobQueued(false);
      try {
        sessionStorage.setItem(
          RANKING_TASK_SESSION_KEY,
          JSON.stringify({ projectId, taskId: response.task_id }),
        );
      } catch {
        /* ignore */
      }
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      addToast({
        type: 'success',
        title: 'Ranking job queued',
        message: response.job_id
          ? 'Visible under Data Pipeline → Jobs. This page updates when complete.'
          : 'Ranking in progress. This page will update when complete.',
        duration: 6000,
      });
    } catch (err: any) {
      addToast({ type: 'error', title: 'Could not queue ranking job', message: err.message || 'Unknown error' });
      setIsRankingJobQueued(false);
    }
  };

  // Step 5: Callback run from queryFn success path – calls batch-match API and updates cache/state
  const runBatchMatchWithIds = async (
    pid: string,
    opportunityIds: string[],
    queryKey: unknown[]
  ) => {
    console.log('[OpportunitiesDashboard] runBatchMatchWithIds called (from queryFn)', { projectId: pid, count: opportunityIds.length });
    const key = `${opportunityIds.length}-${opportunityIds[0] ?? ''}-${(queryKey as any)[2] ?? ''}`;
    autoMatchRunRef.current = key;
    setIsMatching(true);
    try {
      const apiClient = getAPIClient();
      console.log('[OpportunitiesDashboard] POST /api/opportunities/batch-match', { project_id: pid, opportunity_ids_length: opportunityIds.length });
      const response = await apiClient.post('/api/opportunities/batch-match', {
        project_id: pid,
        opportunity_ids: opportunityIds.length > 0 ? opportunityIds : undefined,
        match_against: 'both',
        limit_per_opportunity: 50,
        min_similarity_threshold: 0.15,
      });
      const newMatchScores = new Map<string, number>();
      response.results.forEach((r: any) => newMatchScores.set(String(r.opportunity_id), r.match_score));
      setMatchScores(newMatchScores);
      setSortByMatchScore(true);
      // Prefer prefix match — scheduled queryKey may be ['govt-opportunities-all', projectId]
      // while the live cache entry includes zone/filter/viewMode suffixes.
      queryClient.setQueriesData({ queryKey: queryKey as string[] }, (oldData: any) => {
        if (!oldData?.allEntities) return oldData;
        return {
          ...oldData,
          allEntities: oldData.allEntities.map((entity: any) => {
            const score = newMatchScores.get(String(entity.id));
            if (score === undefined) return entity;
            return { ...entity, data: { ...entity.data, match_score: score } };
          }),
        };
      });
      // Issue 20: default view reads from server pageQuery sorted by data.match_score —
      // refetch so the landing page reorders once scores + match_explain are persisted.
      setPage(1);
      await queryClient.refetchQueries({
        queryKey: ['govt-opportunities-page', pid],
        type: 'active',
      });
      await queryClient.refetchQueries({
        queryKey: ['govt-opportunities-all', pid],
        type: 'active',
      });
      try {
        await refetchRankingStats();
      } catch {
        /* non-fatal */
      }
      const matchedCount = response.results.filter((r: any) => r.match_score > 0).length;
      console.log('[OpportunitiesDashboard] batch-match completed', { matchedCount, total: response.total_opportunities });
      if (matchedCount > 0) {
        addToast({
          type: 'success',
          title: 'Auto-Match complete',
          message: `Successfully matched ${formatNumber(matchedCount)} of ${formatNumber(response.total_opportunities)} opportunities in ${(response.processing_time_ms / 1000).toFixed(1)}s!`,
          duration: 5000,
        });
      }
    } catch (err: any) {
      console.error('[OpportunitiesDashboard] batch-match failed', err);
      addToast({ type: 'error', title: 'Auto-match failed', message: err.message || 'Unknown error' });
    } finally {
      setIsMatching(false);
    }
  };
  runBatchMatchAfterQueryRef.current = runBatchMatchWithIds;

  // KBF-067: Auto-Match after results load; also run after Search click when results return.
  const autoMatchRunRef = useRef<string | null>(null);
  const pendingAutoMatchAfterSearchRef = useRef<boolean>(false);
  const rawCount = opportunitiesData?.allEntities?.length ?? 0;
  const rawFirstId = opportunitiesData?.allEntities?.[0]?.id ?? '';
  const rawTotalCount = opportunitiesData?.total ?? 0;
  const queryFulfilled = !isLoading && opportunitiesData != null;
  useEffect(() => {
    // Step 3: Log every time this effect runs (before any skip)
    console.log('[OpportunitiesDashboard] Auto-Match effect RUN', { rawCount, rawTotalCount, queryFulfilled, isLoading, dataUpdatedAt, hasData: opportunitiesData != null });
    const key = `${rawCount}-${rawFirstId}-${searchTerm}`;
    const afterSearch = pendingAutoMatchAfterSearchRef.current;
    const hasAnyFiltering =
      searchTerm.trim() !== '' ||
      dbSearchTerm.trim() !== '' ||
      filterSource !== 'all' ||
      filterStatus !== 'open' ||
      filterTrackingStatus !== 'all' ||
      filterDeadlinePreset !== 'all' ||
      filterHasDocuments !== null ||
      filterProvince !== 'all' ||
      filterCity !== 'all' ||
      selectedDepartments.size > 0;
    const skipReasons: string[] = [];
    if (!queryFulfilled) skipReasons.push('queryFulfilled=false(isLoading=' + isLoading + ',hasData=' + (opportunitiesData != null) + ')');
    if (rawCount < 1) skipReasons.push('rawCount=' + rawCount + '<1');
    if (rawTotalCount > 200) skipReasons.push('totalCount=' + rawTotalCount + '>200 (manual Run full ranking)');
    if (rawCount > 500) skipReasons.push('rawCount=' + rawCount + '>500');
    if (hasAnyFiltering) skipReasons.push('hasAnyFiltering=true (preserve existing scores on filtered/search views)');
    if (isMatching) skipReasons.push('isMatching=true');
    if (!projectId) skipReasons.push('!projectId');
    if (!afterSearch && matchScores.size > 0) skipReasons.push('matchScores.size=' + matchScores.size);
    if (!afterSearch && autoMatchRunRef.current === key) skipReasons.push('alreadyRan(key=' + key + ')');
    if (skipReasons.length > 0) {
      console.log('[OpportunitiesDashboard] Auto-Match skip', { rawCount, rawFirstId: rawFirstId || '(none)', searchTerm, projectId, key, afterSearch, dataUpdatedAt, skipReasons });
      return;
    }
    if (afterSearch) pendingAutoMatchAfterSearchRef.current = false;
    autoMatchRunRef.current = key;
    console.log('[OpportunitiesDashboard] Auto-Match effect firing', { rawCount, key, projectId, afterSearch: !!afterSearch, dataUpdatedAt });
    const t = setTimeout(() => {
      handleAutoMatchRef.current();
    }, 200);
    return () => clearTimeout(t);
  }, [queryFulfilled, rawCount, rawFirstId, rawTotalCount, searchTerm, dbSearchTerm, matchScores.size, isMatching, projectId, isLoading, dataUpdatedAt, filterSource, filterStatus, filterTrackingStatus, filterDeadlinePreset, filterHasDocuments, filterProvince, filterCity, selectedDepartments.size]);

  // Handle search submission (Enter key or search icon click)
  const handleSearch = (value?: string) => {
    const searchValue = value !== undefined ? value : searchInput;
    const trimmed = searchValue.trim();

    // Validate query syntax
    const validation = validateSearchQuery(trimmed);
    if (!validation.valid) {
      alert(`Invalid search query: ${validation.error}`);
      return;
    }

    // Search should not trigger score recomputation on subset results.
    pendingAutoMatchAfterSearchRef.current = false;
    setStatCardSelection(null);
    setSearchTerm(trimmed);
    setSearchInput(trimmed); // Sync input with search term
    setPage(1); // Reset to first page when searching
    // Save to search history (only if 1-3 words or simple queries)
    if (trimmed) {
      saveToSearchHistory(trimmed);
    }
  };

  const handleDbSearchSubmit = (value?: string) => {
    const submitted = (value !== undefined ? value : dbSearchInput).trim();
    if (!submitted) {
      activeDbSearchRequestRef.current = '';
      setDbSearchTerm('');
      setDbSearchMatchedIds(null);
      setDbAutocompleteOpen(false);
      return;
    }
    saveToSearchHistory(submitted);
    dbSearchMutation.mutate(submitted);
  };

  const clearDbSearch = () => {
    activeDbSearchRequestRef.current = '';
    setDbSearchInput('');
    setDbSearchTerm('');
    setDbSearchMatchedIds(null);
    setDbSearchEntities(null);
    setDbAutocompleteOpen(false);
    setSemanticSearchStatus('idle');
    setSemanticMatchCount(0);
  };

  const appliedClientSingleton =
    selectedDepartments.size === 0 ? 'all' : Array.from(selectedDepartments)[0];

  const hasUnappliedFilterChanges = useMemo(
    () =>
      draftFilterSource !== filterSource ||
      draftFilterStatus !== filterStatus ||
      draftFilterTrackingStatus !== filterTrackingStatus ||
      draftFilterDeadlinePreset !== filterDeadlinePreset ||
      draftFilterHasDocuments !== filterHasDocuments ||
      draftFilterProvince !== filterProvince ||
      draftFilterCity !== filterCity ||
      draftFilterCountry !== filterCountry ||
      draftClient !== appliedClientSingleton,
    [
      draftFilterSource,
      draftFilterStatus,
      draftFilterTrackingStatus,
      draftFilterDeadlinePreset,
      draftFilterHasDocuments,
      draftFilterProvince,
      draftFilterCity,
      draftFilterCountry,
      draftClient,
      filterSource,
      filterStatus,
      filterTrackingStatus,
      filterDeadlinePreset,
      filterHasDocuments,
      filterProvince,
      filterCity,
      filterCountry,
      appliedClientSingleton,
    ]
  );

  const handleApplyFilters = React.useCallback(() => {
    const provinceValues = new Set(draftProvinceFilterOptions.map((o) => o.value));
    const nextProvince =
      draftFilterProvince === 'all' || provinceValues.has(draftFilterProvince)
        ? draftFilterProvince
        : 'all';
    const nextCityOptions = new Set(draftCityFilterOptions.map((o) => o.value));
    const nextCity =
      draftFilterCity === 'all' || nextCityOptions.has(draftFilterCity) ? draftFilterCity : 'all';
    const nextCountryOptions = new Set(draftCountryFilterOptions.map((o) => o.value));
    const nextCountry =
      draftFilterCountry === 'all' || nextCountryOptions.has(draftFilterCountry)
        ? draftFilterCountry
        : 'all';

    const nextClientOptions = new Set(clientComboboxOptions.map((o) => o.value));
    const nextClient =
      draftClient === 'all' || nextClientOptions.has(draftClient) ? draftClient : 'all';

    setStatCardSelection(null);
    setFilterSource(draftFilterSource);
    setFilterStatus(draftFilterStatus);
    setFilterTrackingStatus(draftFilterTrackingStatus);
    setFilterDeadlinePreset(draftFilterDeadlinePreset);
    setFilterHasDocuments(draftFilterHasDocuments);
    setFilterProvince(nextProvince);
    setFilterCity(nextCity);
    setFilterCountry(nextCountry);
    setSelectedDepartments(nextClient === 'all' ? new Set() : new Set([nextClient]));
    setPage(1);
  }, [
    draftFilterSource,
    draftFilterStatus,
    draftFilterTrackingStatus,
    draftFilterDeadlinePreset,
    draftFilterHasDocuments,
    draftFilterProvince,
    draftFilterCity,
    draftFilterCountry,
    draftClient,
    draftProvinceFilterOptions,
    draftCityFilterOptions,
    draftCountryFilterOptions,
    clientComboboxOptions,
  ]);

  // Handle keyword selection from modal — run db field search with selected term
  const handleKeywordSelect = (keyword: string) => {
    setShowKeywordModal(false);
    setDbSearchInput(keyword);
    handleDbSearchSubmit(keyword);
  };

  // Handle Enter key in search input (now handled by SearchAutocomplete)
  // This is kept for backward compatibility but SearchAutocomplete handles it internally

  // Reset page when search term or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, dbSearchTerm, filterSource, filterStatus, filterTrackingStatus, filterDeadlinePreset, filterHasDocuments]);

  /** Stat cards: apply the same filters the cards measure */
  const applyStatCardFilter = (card: 'total' | 'open' | 'tracked' | 'deadlines') => {
    setPage(1);
    // Do not mutate dropdown filters here.
    // Stat cards only apply an overlay filter to the list; the numeric card values remain derived from dropdown filters.
    if (card === 'total') {
      setStatCardSelection('total');
      return;
    }
    setStatCardSelection(card);
  };

  // Show a full-page loading overlay while opportunities are being fetched

  if (!projectId) {
    return (
      <div className="container mx-auto">
        <Card>
          <p className="text-center py-12 text-gray-500">Select a project to view opportunities</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-theme-text">{config.customization?.opportunitiesDashboardLabel ?? 'Government Opportunities Dashboard'}</h1>
          <p className="text-theme-text-secondary mt-1">
            Track procurement opportunities from government sources
          </p>
          {softwareDefinedFilterConfigured && (
            <div className="mt-3">
              <button
                type="button"
                aria-pressed={filterSoftwareDefined}
                title={
                  filterSoftwareDefined
                    ? 'Software-defined tenders only. Click to show all categories.'
                    : 'Showing all categories. Click to show software-defined tenders only.'
                }
                onClick={() => {
                  setFilterSoftwareDefined((current) => !current);
                  setPage(1);
                }}
                className={`inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 ${
                  filterSoftwareDefined
                    ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                    : 'border-theme-border bg-theme-paper text-theme-text hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700'
                }`}
              >
                {filterSoftwareDefined ? (
                  <CheckCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                ) : (
                  <Filter className="h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <span>{filterSoftwareDefined ? 'Software-defined' : 'All categories'}</span>
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          {/* View Mode Selector */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-theme-text">View:</span>
            <div className="flex flex-wrap gap-1">
              <Button
                size="sm"
                variant={viewMode === 'cards' ? 'primary' : 'outline'}
                onClick={() => setViewMode('cards')}
              >
                <Book className="h-4 w-4 mr-1" />
                Cards
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'primary' : 'outline'}
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4 mr-1" />
                List
              </Button>
              {enableCalendarView && (
                <Button
                  size="sm"
                  variant={viewMode === 'calendar' ? 'primary' : 'outline'}
                  onClick={() => setViewMode('calendar')}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Calendar
                </Button>
              )}
              <Button
                size="sm"
                variant={viewMode === 'kanban' ? 'primary' : 'outline'}
                onClick={() => setViewMode('kanban')}
              >
                <SquareKanban className="h-4 w-4 mr-1" />
                Kanban
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canViewTecQueue && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  window.location.href = '/rfp/proposals-management?tab=review';
                }}
                title="Weekly TEC committee review for internal proposals"
              >
                <ClipboardCheck className="h-4 w-4 mr-1" />
                Review & TEC
              </Button>
            )}
            <div className="flex items-center gap-1">
              {(!isDashboardDefaultFilterState ||
                zoneIsNonDefault ||
                (sortByMatchScore && matchScores.size > 0)) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSortByMatchScore(false);
                    setSearchTerm('');
                    setSearchInput('');
                    setDbSearchInput('');
                    setDbSearchTerm('');
                    setDbSearchMatchedIds(null);
                    setFilterSource('all');
                    setFilterStatus('open');
                    setFilterTrackingStatus('all');
                    setFilterDeadlinePreset('all');
                    setFilterHasDocuments(null);
                    setFilterProvince('all');
                    setFilterCity('all');
                    setSelectedDepartments(new Set());
                    setDraftFilterSource('all');
                    setDraftFilterStatus('open');
                    setDraftFilterTrackingStatus('all');
                    setDraftFilterDeadlinePreset('all');
                    setDraftFilterHasDocuments(null);
                    setDraftFilterProvince('all');
                    setDraftFilterCity('all');
                    setDraftClient('all');
                    if (zoneIsNonDefault) {
                      setSelectedGeographicZone(sparcDefaultZone);
                    }
                    setStatCardSelection(null);
                    setPage(1);
                  }}
                  title="Reset filters to the default view (open opportunities, all sources)"
                  aria-label="Reset filters"
                >
                  <X className="h-3 w-3 mr-1" aria-hidden />
                  Reset filters
                </Button>
              )}
            </div>
            {enableOpportunitiesImport && (
              <Button size="sm" variant="outline" onClick={() => setShowImportModal(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setShowExportModal(true)}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm" variant="primary" onClick={() => setShowCustomProposalModal(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Custom Proposal
            </Button>
          </div>
        </div>
      </div>

      {displayLoading ? (
        /* ── Page loading: skeleton cards + contextual message ─────────────── */
        <div aria-busy="true" aria-live="polite">
          {/* Header message */}
          <div className="mb-5 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                {searchTerm
                  ? `Searching for "${searchTerm}"…`
                  : 'Loading opportunities…'}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                Usually completes in 10–30 seconds. First visit may take a little longer.
              </p>
            </div>
          </div>
          {/* Skeleton stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-theme-paper rounded-xl border border-theme-border shadow-md p-4 animate-pulse">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/5" />
                  </div>
                  <div className="h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0" />
                </div>
              </div>
            ))}
          </div>
          {/* Skeleton opportunity rows */}
          <div className="space-y-3">
            {Array.from({ length: Math.min(pageSize, 8) }).map((_, i) => (
              <div
                key={i}
                className="bg-theme-paper rounded-xl border border-theme-border p-4 animate-pulse"
                style={{ opacity: Math.max(0.3, 1 - i * 0.09) }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Title */}
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    {/* Org + source */}
                    <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-1.5" />
                    {/* Description line */}
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                  </div>
                  {/* Match score badge */}
                  <div className="h-7 w-24 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0 mt-1" />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="h-5 w-14 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Search and Filters - Collapsible */}
          <Card className="p-4 mb-6">
            <div className="space-y-3">
              {/* Search Bar with Icon on Left, Filters on Right */}
              <div className="flex items-center gap-3">
                {/* Search Input with Autocomplete - Takes remaining space */}
                {/* <div className="flex-1 relative">
                  <SearchAutocomplete
                    value={searchInput}
                    onChange={setSearchInput}
                    onSelect={handleSearch}
                    searchHistory={searchHistory}
                    projectId={projectId}
                    placeholder="Search opportunities... (Use AND field:value syntax)"
                    className="bg-theme-paper text-theme-text border-theme-border dark:text-black"
                  />
                </div> */}

                {/* Search Button - Clear call-to-action */}
                {/* <Button
                  type="button"
                  onClick={() => handleSearch()}
                  variant="primary"
                  size="md"
                  className="flex-shrink-0"
                  aria-label="Search"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button> */}

                {/* Search Info Tooltip */}
                {/* <div className="flex-shrink-0">
                  <SearchInfoTooltip />
                </div> */}

                {/* Filters are always visible by default */}
              </div>

              {/* Search Progress Indicator - Shows during vector search */}
              {displayLoading && searchTerm && (
                <div className="mt-4">
                  <SearchProgressIndicator
                    isSearching={displayLoading}
                    stage="calculating" // Show "Calculating Similarities" stage for semantic search
                    message={
                      searchTerm
                        ? `Finding opportunities matching "${searchTerm}"...`
                        : 'Searching opportunities...'
                    }
                  />
                </div>
              )}

              {/* Opportunity search placed above all dropdown filters */}
              <div className="mt-2" ref={dbSearchContainerRef}>
                <div className="relative">
                  {dbSearchTerm && semanticSearchStatus !== 'idle' && (
                    <div className="flex items-center gap-2 mb-2">
                      {semanticSearchStatus === 'loading' && (
                        <span className="text-xs text-theme-text-secondary flex items-center gap-1">
                          <RefreshCw className="h-3 w-3 animate-spin" />
                          Refining results with AI...
                        </span>
                      )}
                      {semanticSearchStatus === 'done' && (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400">
                          {semanticMatchCount > 0
                            ? `Showing ${semanticMatchCount} AI-ranked match${semanticMatchCount === 1 ? '' : 'es'}`
                            : 'No AI matches found - showing keyword results'}
                        </span>
                      )}
                      {semanticSearchStatus === 'error' && (
                        <span className="text-xs text-red-500">
                          AI search unavailable - showing keyword results
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 w-full">
                    <Input
                      value={dbSearchInput}
                      onChange={(e) => {
                        setDbSearchInput(e.target.value);
                        setDbAutocompleteOpen(true);
                      }}
                      onFocus={() => setDbAutocompleteOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleDbSearchSubmit();
                        }
                      }}
                      placeholder="Search opportunities....."
                      className="w-full flex-1 min-w-0"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={() => handleDbSearchSubmit()}
                      disabled={dbSearchMutation.isPending}
                    >
                      <Search className="h-4 w-4 mr-1" />
                      Search
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowKeywordModal(true)}
                      title="Browse AI-recommended search keywords for your project"
                      className="flex-shrink-0 flex items-center gap-1.5"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span className="hidden sm:inline">Suggested Keywords</span>
                    </Button>
                    <HelpArticleIcon
                      articleId="rfp-ai-keyword-suggestions"
                      label="Help: Suggested Keywords"
                    />
                    {(dbSearchTerm || dbSearchInput) && (
                      <Button type="button" size="sm" variant="outline" onClick={clearDbSearch}>
                        Clear
                      </Button>
                    )}
                  </div>
                  {dbAutocompleteOpen && (
                    <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-lg">
                      {(() => {
                        const q = dbSearchInput.trim().toLowerCase();
                        const historyMatches = (q
                          ? searchHistory.filter((s) => s.toLowerCase().includes(q))
                          : searchHistory
                        ).slice(0, 8);
                        const showDb = debouncedDbSearchInput.length >= 2;

                        const hasAny =
                          historyMatches.length > 0 ||
                          keywordSuggestionChips.length > 0 ||
                          (showDb && sortedDbSuggestions.length > 0) ||
                          (showDb && isDbAutocompleteFetching);
                        if (!hasAny) {
                          return (
                            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {q ? 'No suggestions' : keywordTaskId ? 'Loading keyword suggestions...' : 'No recent searches'}
                            </div>
                          );
                        }

                        return (
                          <div className="max-h-64 overflow-y-auto">
                            {keywordSuggestionChips.length > 0 && !q && (
                              <div>
                                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-slate-800/50">
                                  Suggested keywords
                                </div>
                                {keywordSuggestionChips.map((chip, idx) => (
                                  <button
                                    key={`kw-${chip.keyword}`}
                                    type="button"
                                    onClick={() => {
                                      setDbSearchInput(chip.keyword);
                                      setDbAutocompleteOpen(false);
                                      handleDbSearchSubmit(chip.keyword);
                                    }}
                                    className={`block w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-slate-100 bg-white dark:bg-slate-700 transition-colors hover:bg-gray-100 dark:hover:bg-slate-600 focus:bg-gray-100 dark:focus:bg-slate-600 focus:outline-none ${idx !== 0 ? 'border-t border-gray-200 dark:border-slate-600' : ''}`}
                                  >
                                    <span className="inline-flex items-center gap-2">
                                      <Sparkles className="h-3.5 w-3.5 text-blue-500" />
                                      <span className="truncate">{chip.keyword}</span>
                                      {chip.confidence_pct != null && (
                                        <span className="text-xs text-gray-500">({chip.confidence_pct}%)</span>
                                      )}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {historyMatches.length > 0 && (
                              <div>
                                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-slate-800/50">
                                  Recent searches
                                </div>
                                {historyMatches.map((term, idx) => (
                                  <button
                                    key={`history-${term}`}
                                    type="button"
                                    onClick={() => {
                                      setDbSearchInput(term);
                                      setDbAutocompleteOpen(false);
                                      handleDbSearchSubmit(term);
                                    }}
                                    className={`block w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-slate-100 bg-white dark:bg-slate-700 transition-colors hover:bg-gray-100 dark:hover:bg-slate-600 focus:bg-gray-100 dark:focus:bg-slate-600 focus:outline-none ${idx !== 0 ? 'border-t border-gray-200 dark:border-slate-600' : ''
                                      }`}
                                  >
                                    <span className="block truncate">{term}</span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {showDb && (
                              <div className={historyMatches.length > 0 ? 'border-t border-gray-200 dark:border-slate-600' : ''}>
                                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-slate-800/50">
                                  Suggestions
                                </div>
                                {isDbAutocompleteFetching ? (
                                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Loading suggestions...</div>
                                ) : sortedDbSuggestions.length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No suggestions</div>
                                ) : (
                                  sortedDbSuggestions.map((suggestion, idx) => (
                                    <button
                                      key={`${suggestion.field}-${suggestion.value}`}
                                      type="button"
                                      onClick={() => {
                                        setDbSearchInput(suggestion.value);
                                        // Selecting a suggestion should only fill the input.
                                        // Actual search runs only on Enter or Search button.
                                        setDbAutocompleteOpen(false);
                                      }}
                                      className={`block w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-slate-100 bg-white dark:bg-slate-700 transition-colors hover:bg-gray-100 dark:hover:bg-slate-600 focus:bg-gray-100 dark:focus:bg-slate-600 focus:outline-none ${idx !== 0 ? 'border-t border-gray-200 dark:border-slate-600' : ''
                                        }`}
                                    >
                                      <span className="block truncate">{suggestion.value}</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
                {dbSearchTerm && (
                  <p className="mt-1 text-xs text-theme-text-muted">
                    Showing opportunities matching: "{dbSearchTerm}"
                    {dbSearchMatchedIds ? ` (${dbSearchMatchedIds.size})` : ''}
                  </p>
                )}
              </div>

              {/* Always-visible Filters */}
              <>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8 gap-4 pt-4 border-t border-theme-border">
                  <FilterCombobox
                    id="filter-source-combobox"
                    label="Source"
                    value={draftFilterSource}
                    onChange={(next) => {
                      setDraftFilterSource(next);
                      setFilterSource(next);
                      setStatCardSelection(null);
                      setPage(1);
                    }}
                    options={sourceFilterOptions}
                    placeholder="All sources — type to search"
                    allRowLabel="All Sources"
                    usePortal
                  />

                  <div>
                    <FilterLabelWithTooltip
                      label="Opp Portal Status"
                      tooltipText="Open = deadline in the future; Closed = past deadline or portal status closed/awarded/expired; All = show everything."
                    />
                    <FilterCombobox
                      id="filter-portal-status-combobox"
                      value={draftFilterStatus}
                      onChange={setDraftFilterStatus}
                      label={undefined}
                      options={portalStatusFilterOptions}
                      placeholder="All — type to search"
                      allRowLabel="All"
                      usePortal
                    />
                  </div>

                  <div>
                    <FilterLabelWithTooltip
                      label="Opportunity Stage"
                      tooltipText="Internal workflow stage (e.g. Watching, Bidding, Won, Lost)"
                    />
                    <FilterCombobox
                      id="filter-stage-combobox"
                      value={draftFilterTrackingStatus}
                      onChange={setDraftFilterTrackingStatus}
                      label={undefined}
                      options={trackingStatusFilterOptions}
                      placeholder="All stages — type to search"
                      allRowLabel="All Stages"
                      usePortal
                    />
                  </div>

                  <div>
                    <FilterLabelWithTooltip
                      label="Deadline window"
                      tooltipText="Filter by how soon the opportunity closes (uses days until deadline)."
                    />
                    <FilterCombobox
                      id="filter-deadline-window-combobox"
                      value={draftFilterDeadlinePreset}
                      onChange={(v) => setDraftFilterDeadlinePreset(v as DeadlinePreset)}
                      label={undefined}
                      options={deadlineFilterOptions}
                      placeholder="All deadlines — type to search"
                      allRowLabel="All deadlines"
                      usePortal
                    />
                  </div>

                  <FilterCombobox
                    id="filter-documents-combobox"
                    label="Documents"
                    value={draftFilterHasDocuments === null ? 'all' : draftFilterHasDocuments ? 'has' : 'none'}
                    onChange={(v) => setDraftFilterHasDocuments(v === 'all' ? null : v === 'has')}
                    options={documentsFilterOptions}
                    placeholder="All opportunities — type to search"
                    allRowLabel="All Opportunities"
                    usePortal
                  />

                  <FilterCombobox
                    id="filter-country-combobox"
                    label="Country"
                    value={draftFilterCountry}
                    onChange={setDraftFilterCountry}
                    options={draftCountryFilterOptions}
                    placeholder="All countries — type to search"
                    allRowLabel="All countries"
                    usePortal
                  />

                  <FilterCombobox
                    id="filter-province-combobox"
                    label="Province / territory"
                    value={draftFilterProvince}
                    onChange={setDraftFilterProvince}
                    options={draftProvinceFilterOptions}
                    placeholder="All provinces — type to search"
                    allRowLabel="All provinces / territories"
                    optionMatchesQuery={provinceOptionMatchesQuery}
                    usePortal
                  />

                  <FilterCombobox
                    id="filter-city-combobox"
                    label="City"
                    value={draftFilterCity}
                    onChange={setDraftFilterCity}
                    options={draftCityFilterOptions}
                    placeholder="All cities — type to search"
                    allRowLabel="All cities"
                    usePortal
                  />

                  <FilterCombobox
                    id="filter-client-combobox"
                    label="Client"
                    value={draftClient}
                    onChange={setDraftClient}
                    options={clientComboboxOptions}
                    placeholder="All clients — type to search"
                    allRowLabel="All clients"
                    usePortal
                  />

                  {/* Match Score Filter removed - not needed */}
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-theme-border pt-4">
                  {/* {hasUnappliedFilterChanges ? (
                    <span className="text-xs text-amber-700 dark:text-amber-300">
                      You have filter changes that are not applied yet.
                    </span>
                  ) : (
                    <span />
                  )} */}
                  <Button type="button" variant="primary" size="sm" onClick={handleApplyFilters}>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>
              </>

              {/* Results Count and live data indicator */}
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-theme-text-secondary pt-2 border-t border-theme-border">
                <span>
                  Showing {filteredOpportunities.length} of {totalCount} opportunities
                  {filterTrackingStatus !== 'all' && (
                    <span className="ml-2 text-primary-600 dark:text-primary-400 font-medium">
                      (Tracking:{' '}
                      {filterTrackingStatus === 'tracked' ? 'Watching + Bidding' : filterTrackingStatus})
                    </span>
                  )}
                  {filterDeadlinePreset !== 'all' && (
                    <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">
                      ({deadlinePresetLabel(filterDeadlinePreset)})
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2" title="Data from POST /entities/query; updates when you change filters or refocus the window">
                  {isFetching && !isLoading ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary-500" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden />
                  )}
                  <span className="text-xs">
                    {isFetching && !isLoading ? 'Updating…' : dataUpdatedAt ? `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}` : 'Live'}
                  </span>
                </span>
              </div>

              {/* Auto-Match (≤200 opps) or Run full ranking (>200 opps, background Celery job) */}
              {showAutoMatchSection && (
                <div className="pt-3 border-t border-theme-border flex items-center justify-between gap-3 flex-wrap">
                  {!canWriteOpp && (
                    <p className="text-xs text-gray-500 dark:text-theme-text-muted w-full">
                      Ranking requires <code className="text-xs">opportunities.write</code> or{' '}
                      <code className="text-xs">opportunities.manage</code> permission.
                    </p>
                  )}
                  {autoMatchWhenUnder200 ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={showPromptAutoMatch ? 'primary' : 'outline'}
                          onClick={handleAutoMatch}
                          disabled={!canWriteOpp || isMatching || (listOpportunities.length === 0 && opportunities.length === 0)}
                          className={
                            showPromptAutoMatch
                              ? 'ring-2 ring-amber-400 ring-offset-2 dark:ring-offset-gray-900 animate-pulse'
                              : ''
                          }
                          title={
                            !canWriteOpp
                              ? 'You do not have permission to run opportunity ranking'
                              : 'Rank opportunities by similarity to your past projects and capability statements'
                          }
                        >
                          <Zap className={`h-4 w-4 mr-2 ${isMatching ? 'animate-pulse' : ''}`} />
                          {isMatching ? 'Matching...' : 'Auto-Match'}
                        </Button>
                        <span className="text-xs text-gray-500 dark:text-theme-text-muted inline-flex items-center gap-1">
                          Rank by relevance to your capabilities
                          <HelpArticleIcon
                            articleId="rfp-match-scores-ranking"
                            label="Help: Match Scores & Ranking"
                          />
                        </span>
                      </div>
                      {showPromptAutoMatch && canWriteOpp && (
                        <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                          Click Auto-Match to rank results by relevance
                        </span>
                      )}
                    </>
                  ) : showManualRunFullRanking ? (
                    <>
                      <div className="flex flex-wrap items-center gap-3 w-full">
                        {activeRankingTaskId && (
                          <p className="text-sm text-gray-600 dark:text-theme-text-secondary w-full">
                            {rankingJobProgress?.opportunities_processed != null ||
                            rankingJobProgress?.chunks_processed != null
                              ? `Ranking ${formatNumber(rankingJobProgress.opportunities_processed ?? 0)} of ${formatNumber(rankingJobProgress.total_opportunities ?? 0)} (${formatNumber(rankingJobProgress.total_matched ?? 0)} matched)${
                                  rankingJobProgress.eta_seconds != null && rankingJobProgress.eta_seconds > 0
                                    ? ` · ~${
                                        rankingJobProgress.eta_seconds >= 60
                                          ? `${Math.ceil(rankingJobProgress.eta_seconds / 60)} min`
                                          : `${Math.ceil(rankingJobProgress.eta_seconds)}s`
                                      } left`
                                    : ''
                                }…`
                              : 'Ranking in progress…'}
                          </p>
                        )}
                        <Button
                          variant={showPromptAutoMatch ? 'primary' : 'outline'}
                          onClick={handleRunFullRankingJob}
                          disabled={
                            !canWriteOpp ||
                            isRankingJobQueued ||
                            !!activeRankingTaskId ||
                            !projectId
                          }
                          className={
                            showPromptAutoMatch
                              ? 'ring-2 ring-amber-400 ring-offset-2 dark:ring-offset-gray-900'
                              : ''
                          }
                          title={
                            !canWriteOpp
                              ? 'You do not have permission to run opportunity ranking'
                              : `Rank all ${formatNumber(totalCountForMatch)} opportunities in a background job`
                          }
                        >
                          <Zap className={`h-4 w-4 mr-2 ${activeRankingTaskId ? 'animate-pulse' : ''}`} />
                          {activeRankingTaskId
                            ? rankingJobProgress?.opportunities_processed != null ||
                              rankingJobProgress?.total_opportunities != null
                              ? `Ranking… ${formatNumber(rankingJobProgress.opportunities_processed ?? 0)}/${formatNumber(rankingJobProgress.total_opportunities ?? 0)}`
                              : 'Ranking in progress…'
                            : isRankingJobQueued
                              ? 'Queuing…'
                              : `Run Full Ranking (${formatNumber(totalCountForMatch)})`}
                        </Button>
                        <span className="text-xs text-gray-500 dark:text-theme-text-muted inline-flex items-center gap-1">
                          Background job — scores saved on each opportunity; page refreshes when complete
                          <HelpArticleIcon
                            articleId="rfp-match-scores-ranking"
                            label="Help: Match Scores & Ranking"
                          />
                        </span>
                        <button
                          type="button"
                          onClick={handleAutoMatch}
                          disabled={!canWriteOpp || isMatching || opportunities.length === 0}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
                          title="Rank only the current page of results (fast preview)"
                        >
                          {isMatching ? 'Matching current page…' : `Rank current page (${opportunities.length})`}
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </Card>

          {/* Stats loading indicator — only shown while the 15-min backend cache is being built (first load of the day) */}
          {dashboardStatsLoading && !displayLoading && (
            <div className="mb-4 rounded-xl border border-blue-100 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-900/10 px-4 py-2 flex items-center gap-2">
              <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin shrink-0" />
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Loading stats…
              </p>
            </div>
          )}

          {/* Stats Cards — click to apply matching filters (native button: Card component does not forward onClick) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <button
              type="button"
              onClick={() => applyStatCardFilter('total')}
              className={`w-full text-left bg-theme-paper rounded-xl border border-theme-border shadow-md transition-all duration-200 p-4 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${statCardSelection === 'total' ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}`}
              title="Show all opportunities (clears filters)"
              aria-pressed={statCardSelection === 'total'}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-theme-text-secondary">Total Opportunities</p>
                  {dashboardStatsLoading
                    ? <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded mt-1 animate-pulse" />
                    : <p className="text-2xl font-bold mt-1 text-theme-text">{formatNumber(baseTotalCount)}</p>}
                </div>
                <Target className="h-8 w-8 text-blue-500 shrink-0" aria-hidden />
              </div>
            </button>

            <button
              type="button"
              onClick={() => applyStatCardFilter('open')}
              className={`w-full text-left bg-theme-paper rounded-xl border border-theme-border shadow-md transition-all duration-200 p-4 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${statCardSelection === 'open' ? 'ring-2 ring-green-500 dark:ring-green-400' : ''}`}
              title="Filter to open / currently active opportunities"
              aria-pressed={statCardSelection === 'open'}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-theme-text-secondary">Open Opportunities</p>
                  <p className="text-xs text-theme-text-muted mt-1">Currently Active</p>
                  {dashboardStatsLoading
                    ? <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded mt-1 animate-pulse" />
                    : <p className="text-2xl font-bold mt-1 text-green-600">{formatNumber(stats.openOpportunities)}</p>}
                </div>
                <CheckCircle className="h-8 w-8 text-green-500 shrink-0" aria-hidden />
              </div>
            </button>

            <button
              type="button"
              onClick={() => applyStatCardFilter('tracked')}
              className={`w-full text-left bg-theme-paper rounded-xl border border-theme-border shadow-md transition-all duration-200 p-4 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${statCardSelection === 'tracked' ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}`}
              title="Filter to Watching or Bidding"
              aria-pressed={statCardSelection === 'tracked'}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-theme-text-secondary">Status</p>
                  <p className="text-xs text-theme-text-muted mt-1">Watching + Bidding</p>
                  {dashboardStatsLoading
                    ? <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded mt-1 animate-pulse" />
                    : <p className="text-2xl font-bold mt-1 text-blue-600">{formatNumber(stats.trackedOpportunities)}</p>}
                </div>
                <Eye className="h-8 w-8 text-blue-500 shrink-0" aria-hidden />
              </div>
            </button>

            <button
              type="button"
              onClick={() => applyStatCardFilter('deadlines')}
              className={`w-full text-left bg-theme-paper rounded-xl border border-theme-border shadow-md transition-all duration-200 p-4 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${statCardSelection === 'deadlines' ? 'ring-2 ring-orange-500 dark:ring-orange-400' : ''}`}
              title="Filter to opportunities due in the next 30 days"
              aria-pressed={statCardSelection === 'deadlines'}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-theme-text-secondary">Upcoming Deadlines</p>
                  <p className="text-xs text-theme-text-muted mt-1">Next 30 days</p>
                  {dashboardStatsLoading
                    ? <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded mt-1 animate-pulse" />
                    : <p className="text-2xl font-bold mt-1 text-orange-600">{formatNumber(stats.upcomingDeadlines)}</p>}
                </div>
                <Clock className="h-8 w-8 text-orange-500 shrink-0" aria-hidden />
              </div>
            </button>
          </div>

          {showDocumentsUploadBanner && (
            <Card className="mb-6 p-4 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40">
              <div className="flex items-start gap-3">
                <Upload className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    No opportunities have uploaded documents yet
                  </p>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                    Upload RFP PDFs and supporting files to your top-ranked tender portal opportunities
                    to unlock suitability analysis and faster proposal prep.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Deadline alerts & reminders banner */}
          {(deadlineAlerts.urgentCount > 0 || deadlineAlerts.soonCount > 0) && (
            <Card className="mb-6 p-4 border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                    Alerts &amp; reminders
                  </p>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                    {deadlineAlerts.urgentCount > 0 && (
                      <span>
                        {deadlineAlerts.urgentCount} opportunit{deadlineAlerts.urgentCount === 1 ? 'y' : 'ies'} due in the next 7 days.
                      </span>
                    )}
                    {deadlineAlerts.urgentCount > 0 && deadlineAlerts.soonCount > 0 && ' '}
                    {deadlineAlerts.soonCount > 0 && (
                      <span>
                        {deadlineAlerts.soonCount} due in 8-14 days.
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    Set status to &quot;Bidding&quot; to get a reminder when the deadline is close. Proposals created from an opportunity show its deadline in the success message.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && enableCalendarView && (
            <Card className="p-4 mb-6">
              {calendarLocalizer ? (
                <div style={{ height: '600px' }}>
                  {(calendarOpportunitiesLoading || calendarOpportunitiesFetching) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-theme-text mb-2">
                      <LoadingSpinner size="sm" />
                      Loading deadlines for {calendarVisibleRange.start} – {calendarVisibleRange.end}…
                    </div>
                  )}
                  <BigCalendar
                    localizer={calendarLocalizer}
                    date={calendarDate}
                    view={calendarView}
                    onNavigate={(newDate) => setCalendarDate(newDate)}
                    onView={(newView) => setCalendarView(newView)}
                    events={calendarDisplayOpportunities
                      .filter((opp) => opp.data.deadline || opp.data.response_deadline)
                      .map((opp) => {
                        const deadlineRaw = opp.data.deadline || opp.data.response_deadline!;
                        return {
                          id: opp.id,
                          title: opp.data.title || 'Untitled',
                          start: new Date(deadlineRaw),
                          end: new Date(deadlineRaw),
                          resource: opp,
                        };
                      })}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    onSelectEvent={(event: Event & { resource?: GovtOpportunityEntity }) => {
                      if (event.resource) {
                        syncOppIdInUrl(event.resource.id);
                        setSelectedOpportunity(event.resource);
                        setIsModalOpen(true);
                      }
                    }}
                    popup
                    className="bg-white dark:bg-theme-paper"
                  />
                  {!calendarOpportunitiesLoading && calendarOpportunitiesData && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Showing {calendarDisplayOpportunities.length} of {calendarOpportunitiesData.total_count} opportunities with deadlines in this view
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-theme-text">Loading calendar...</p>
                </div>
              )}
            </Card>
          )}

          {/* Deadline Calendar (optional view) - DEPRECATED */}
          {false && (
            <Card className="p-4 mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Upcoming Deadlines
              </h3>
              <div className="space-y-2">
                {filteredOpportunities
                  .filter((opp) => opp.data.deadline)
                  .sort((a, b) => {
                    const dateA = new Date(a.data.deadline!).getTime();
                    const dateB = new Date(b.data.deadline!).getTime();
                    return dateA - dateB;
                  })
                  .slice(0, 10)
                  .map((opp) => {
                    const daysUntil = opp.data.days_until_deadline ?? null;
                    const isUrgent = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
                    const isExpired = daysUntil !== null && daysUntil < 0;

                    return (
                      <div
                        key={opp.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${isUrgent
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-gray-900 dark:text-theme-text'
                          : isExpired
                            ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-theme-text'
                            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-gray-900 dark:text-theme-text'
                          }`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{opp.data.title}</p>
                          <p className="text-xs text-gray-600 dark:text-theme-text-secondary">
                            {opp.data.organization || ((opp.data as any)._source_type === 'upload' ? 'Manually uploaded' : '')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">
                            {new Date(opp.data.deadline!).toLocaleDateString()}
                          </p>
                          <p
                            className={`text-xs ${isUrgent
                              ? 'text-red-600 font-semibold'
                              : isExpired
                                ? 'text-gray-500'
                                : 'text-blue-600'
                              }`}
                          >
                            {isExpired
                              ? `${Math.abs(daysUntil!)} days overdue`
                              : daysUntil === 0
                                ? 'Due today!'
                                : `${daysUntil} days left`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Card>
          )}

          {/* Kanban View (SPARC-002) */}
          {viewMode === 'kanban' && (
            // SPARC-ISSUE-012: min-w-0 + overflow-hidden so the board's overflow-x
            // scroll is constrained to the viewport (flex/grid ancestors default to
            // min-width:auto and otherwise let columns push the page wider — the
            // rightmost cards then appear "shifted" / half-cut-off).
            <Card className="p-6 mb-6 min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-200 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex-1 min-w-[140px]">
                  <label className="text-xs font-medium text-theme-text-secondary mb-1 block">
                    Priority
                  </label>
                  <Select
                    value={filterKanbanPriority}
                    onChange={(e) => setFilterKanbanPriority(e.target.value as 'all' | BdPriority)}
                  >
                    <option value="all">All priorities</option>
                    {BD_PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="text-xs font-medium text-theme-text-secondary mb-1 block">
                    Assigned BD
                  </label>
                  <Select
                    value={filterKanbanAssigned}
                    onChange={(e) => setFilterKanbanAssigned(e.target.value)}
                  >
                    <option value="all">All assignees</option>
                    {kanbanAssignedOptions.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex items-center gap-2 sm:pb-2">
                  <input
                    id="kanban-weekly-meeting"
                    type="checkbox"
                    checked={kanbanWeeklyMeetingMode}
                    onChange={(e) => setKanbanWeeklyMeetingMode(e.target.checked)}
                    className="rounded border-slate-300 dark:border-slate-600"
                  />
                  <label
                    htmlFor="kanban-weekly-meeting"
                    className="text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                    title="Show Watching and Bidding only, sorted by BD priority"
                  >
                    <CalendarCheck className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
                    Weekly Meeting Mode
                  </label>
                </div>
                <div className="flex items-center gap-2 sm:pb-2">
                  <input
                    id="kanban-show-discarded"
                    type="checkbox"
                    checked={showKanbanDiscarded}
                    onChange={(e) => setShowKanbanDiscarded(e.target.checked)}
                    className="rounded border-slate-300 dark:border-slate-600"
                  />
                  <label
                    htmlFor="kanban-show-discarded"
                    className="text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <XCircle className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
                    Show Discarded
                  </label>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 sm:pb-2 tabular-nums">
                  Country/region uses dashboard filters above ·{' '}
                  {kanbanOpportunitiesLoading ? 'Loading...' : `${kanbanDisplayOpportunities.length} cards`}
                </p>
              </div>
              <OpportunityPipelineKanban
                opportunities={kanbanDisplayOpportunities}
                showDiscarded={showKanbanDiscarded}
                isLoading={kanbanOpportunitiesLoading}
                canChangeStage={canAdminPipeline}
                canChangePriority={canAdminPipeline}
                isTransitioning={updatePipelineStageMutation.isPending}
                isPriorityUpdating={updateBdPriorityMutation.isPending}
                priorityUpdatingId={priorityUpdatingId}
                discardingId={pipelineTransitionId}
                onViewList={() => setViewMode('list')}
                onOpportunityClick={(opp) => {
                  syncOppIdInUrl(opp.id);
                  setSelectedOpportunity(opp);
                  setIsModalOpen(true);
                }}
                onStageChange={(entityId, newStage) => {
                  updatePipelineStageMutation.mutate({ entityId, pipelineStage: newStage });
                }}
                onDiscard={(entityId) => {
                  updatePipelineStageMutation.mutate({
                    entityId,
                    pipelineStage: DISCARD_PIPELINE_STAGE,
                  });
                }}
                onPriorityChange={(entityId, priority) => {
                  updateBdPriorityMutation.mutate({ entityId, bdPriority: priority });
                }}
              />
            </Card>
          )}

          {/* Opportunities Grid/List - hide when calendar or kanban */}
          {viewMode !== 'calendar' && viewMode !== 'kanban' && (
            filteredOpportunities.length === 0 ? (
              <Card>
                <div className="text-center py-12">
                  {filterSoftwareDefined ? (
                    <Filter className="h-16 w-16 mx-auto text-theme-text-secondary mb-4" aria-hidden="true" />
                  ) : (
                    <Building2 className="h-16 w-16 mx-auto text-theme-text-secondary mb-4" aria-hidden="true" />
                  )}
                  <p className="text-theme-text font-medium">No opportunities found</p>
                  <p className="text-sm text-theme-text-secondary mt-2">
                    {filterSoftwareDefined
                      ? 'The software-defined filter hid every tender in this view. Civil and field work stay off the list until you show all categories.'
                      : !isDashboardDefaultFilterState || (sortByMatchScore && matchScores.size > 0)
                      ? 'Try adjusting your filters'
                      : (opportunitiesData?.allEntities?.length ?? 0) > 0
                        ? 'No opportunities match the current view. Try Total or other filters.'
                        : 'No opportunities have been loaded yet'}
                  </p>
                  {filterSoftwareDefined && (
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFilterSoftwareDefined(false);
                          setPage(1);
                        }}
                      >
                        <Filter className="h-4 w-4 mr-2" aria-hidden="true" />
                        Show all categories
                      </Button>
                    </div>
                  )}
                  {/* TODO: re-enable Custom Proposal button in empty state once feature is ready */}
                  <div className="mt-6 flex flex-col items-center gap-3">
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Have an RFP that isn&apos;t in the system yet?
                    </p>
                    <Button variant="primary" size="sm" onClick={() => setShowCustomProposalModal(true)}>
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Create Custom Proposal
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <>
                <div className={`grid gap-6 mb-6 ${viewMode === 'cards'
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                  : 'grid-cols-1'
                  }`}>
                  {filteredOpportunities.map((opp) => {
                    // Enhance opportunity with computed fields
                    const enhancedOpp = enhanceOpportunityEntity(opp);
                    const openOpportunitySummary = () => {
                      syncOppIdInUrl(enhancedOpp.id);
                      setSelectedOpportunity(enhancedOpp);
                      setIsModalOpen(true);
                    };
                    // Add match score from state if available
                    const matchScore = matchScores.get(String(opp.id));
                    if (matchScore !== undefined) {
                      enhancedOpp.data.match_score = matchScore;
                    }

                    const daysUntil = enhancedOpp.data.days_until_deadline ?? null;
                    const urgency = getDeadlineUrgency(daysUntil);
                    const isUrgent = urgency === 'urgent';
                    const isExpired = urgency === 'expired';
                    const isSoon = urgency === 'soon';

                    // Get opportunity value
                    const opportunityValue = getOpportunityValue(enhancedOpp.data);
                    const projectId = getProjectIdDisplay(enhancedOpp);
                    const stage = enhancedOpp.data.proposal_stage || 'Unknown';
                    const duration = enhancedOpp.data.duration_display || 'Not specified';

                    return (
                      <div
                        key={opp.id}
                        className="cursor-default"
                      >
                        <Card
                          className={`h-full p-4 hover:shadow-lg transition-shadow duration-200 flex flex-col border ${enhancedOpp.data.tracking_status === 'watching'
                            ? 'bg-blue-50 dark:bg-blue-900/30 dark:text-slate-100 border-blue-300 dark:border-blue-700'
                            : enhancedOpp.data.tracking_status === 'bidding'
                              ? 'bg-green-50 dark:bg-green-900/30 dark:text-slate-100 border-green-300 dark:border-green-700'
                              : enhancedOpp.data.tracking_status === 'won'
                                ? 'bg-emerald-50 dark:bg-emerald-900/30 dark:text-slate-100 border-emerald-300 dark:border-emerald-700'
                                : enhancedOpp.data.tracking_status === 'lost'
                                  ? 'bg-red-50 dark:bg-red-900/30 dark:text-slate-100 border-red-300 dark:border-red-700'
                                  : enhancedOpp.data.tracking_status === 'declined'
                                    ? 'bg-gray-50 dark:bg-gray-800 dark:text-slate-100 border-gray-300 dark:border-gray-600'
                                    : 'bg-white dark:bg-slate-800 dark:text-slate-100 border-theme-border'
                            }`}
                        >
                          {/* Header with Project ID and Match Score */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold text-lg line-clamp-2 text-gray-900 dark:text-slate-100" title={enhancedOpp.data.title}>{enhancedOpp.data.title}</h3>
                                {/* Relevance Score Badge from Vector Search - HIDDEN: Only show Auto-Match score */}
                                {/* Tracking Status Badge */}
                                {enhancedOpp.data.tracking_status && (
                                  <Badge
                                    variant={
                                      enhancedOpp.data.tracking_status === 'watching'
                                        ? 'default'
                                        : enhancedOpp.data.tracking_status === 'bidding'
                                          ? 'success'
                                          : enhancedOpp.data.tracking_status === 'won'
                                            ? 'success'
                                            : enhancedOpp.data.tracking_status === 'lost'
                                              ? 'error'
                                              : 'default'
                                    }
                                    className={
                                      enhancedOpp.data.tracking_status === 'watching'
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700'
                                        : enhancedOpp.data.tracking_status === 'bidding'
                                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'
                                          : enhancedOpp.data.tracking_status === 'won'
                                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700'
                                            : enhancedOpp.data.tracking_status === 'lost'
                                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
                                              : ''
                                    }
                                  >
                                    {enhancedOpp.data.tracking_status.charAt(0).toUpperCase() + enhancedOpp.data.tracking_status.slice(1)}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300 mb-2">
                                <span className="font-mono text-xs bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-700 dark:text-slate-200 line-clamp-3">
                                  ID: {projectId}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300 mb-2">
                                <Building2 className="h-4 w-4 flex-shrink-0" />
                                <span className="line-clamp-1">
                                  {enhancedOpp.data.organization || enhancedOpp.data.issuing_agency || (
                                    (enhancedOpp.data as any)._source_type === 'upload'
                                      ? <span className="italic text-gray-400 dark:text-gray-500">Manually uploaded</span>
                                      : <span className="italic text-gray-400 dark:text-gray-500">Unknown</span>
                                  )}
                                </span>
                              </div>
                              {(enhancedOpp.data as any).assigned_by && (
                                <div className="flex items-center gap-1 text-xs text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 px-2 py-1 rounded-full w-fit mb-2">
                                  <User className="h-3 w-3" />
                                  <span>Status changed by: <strong>{(enhancedOpp.data as any).assigned_by}</strong></span>
                                </div>
                              )}
                            </div>
                            {(enhancedOpp.data.match_score !== undefined && enhancedOpp.data.match_score !== null) || matchScores.get(String(opp.id)) !== undefined ? (
                              <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                                <MatchScoreTooltip
                                  score={Number(enhancedOpp.data.match_score ?? matchScores.get(String(opp.id)) ?? 0)}
                                  variant="badge"
                                  explain={
                                    (enhancedOpp.data as { match_explain?: MatchExplain | null }).match_explain ?? null
                                  }
                                />
                              </div>
                            ) : null}
                          </div>

                          {/* Key Stats Row - Stage, Value, Duration */}
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            {/* Proposal Stage Badge */}
                            {stage !== 'Unknown' && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStageBadgeColor(stage)}`}>
                                {stage}
                              </span>
                            )}

                            {/* Dollar Value */}
                            {opportunityValue !== null && (
                              <div className="flex items-center gap-1 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-md border border-green-200 dark:border-green-800">
                                <DollarSign className="h-3 w-3 text-green-600 dark:text-green-400" />
                                <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                                  {formatCurrency(opportunityValue, { compact: true, symbol: enhancedOpp.data.currency === 'CAD' ? 'C$' : '$' })}
                                </span>
                              </div>
                            )}

                            {/* Duration */}
                            {duration !== 'Not specified' && (
                              <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-slate-300">
                                <Clock className="h-3 w-3" />
                                <span>{duration}</span>
                              </div>
                            )}
                          </div>

                          {/* Status Badges */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <Badge
                              variant={
                                enhancedOpp.data.status === 'open'
                                  ? 'success'
                                  : enhancedOpp.data.status === 'closed'
                                    ? 'default'
                                    : 'warning'
                              }
                            >
                              {enhancedOpp.data.status || 'Unknown'}
                            </Badge>
                            {enhancedOpp.data.source && (
                              <Badge variant="default">
                                <Tag className="h-3 w-3 mr-1" />
                                {enhancedOpp.data.source}
                              </Badge>
                            )}
                          </div>

                          {/* Deadline with Urgency Indicator */}
                          {enhancedOpp.data.deadline && (
                            <div className={`mb-3 p-2 rounded-lg border ${isUrgent
                              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                              : isExpired
                                ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                                : isSoon
                                  ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                                  : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                              }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock
                                    className={`h-4 w-4 ${isUrgent ? 'text-red-600 dark:text-red-400' :
                                      isExpired ? 'text-gray-400 dark:text-slate-400' :
                                        isSoon ? 'text-yellow-600 dark:text-yellow-400' :
                                          'text-blue-600 dark:text-blue-400'
                                      }`}
                                  />
                                  <span className="text-xs font-medium text-gray-900 dark:text-slate-100">
                                    Deadline
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className={`text-sm font-semibold ${isUrgent
                                    ? 'text-red-700 dark:text-red-300'
                                    : isExpired
                                      ? 'text-gray-500 dark:text-gray-400'
                                      : isSoon
                                        ? 'text-yellow-700 dark:text-yellow-300'
                                        : 'text-blue-700 dark:text-blue-300'
                                    }`}>
                                    {new Date(enhancedOpp.data.deadline).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </div>
                                  <div className={`text-xs ${isUrgent
                                    ? 'text-red-600 dark:text-red-400'
                                    : isExpired
                                      ? 'text-gray-500 dark:text-gray-400'
                                      : isSoon
                                        ? 'text-yellow-600 dark:text-yellow-400'
                                        : 'text-blue-600 dark:text-blue-400'
                                    }`}>
                                    {isExpired
                                      ? `Expired ${Math.abs(daysUntil!)} days ago`
                                      : daysUntil === 0
                                        ? 'Due today!'
                                        : daysUntil === 1
                                          ? '1 day left'
                                          : `${daysUntil} days left`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Closing / closed date */}
                          {(enhancedOpp.data.closing_date || (enhancedOpp.data as any).close_date || (enhancedOpp.data.raw_data as any)?.close_date) && (() => {
                            const closeDateStr = enhancedOpp.data.closing_date || (enhancedOpp.data as any).close_date || (enhancedOpp.data.raw_data as any)?.close_date;
                            const closeDate = new Date(closeDateStr);
                            if (Number.isNaN(closeDate.getTime())) return null;
                            const formatted = closeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            const isPast = closeDate.getTime() < Date.now();
                            return (
                              <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                                {isPast ? `Closed on: ${formatted}` : `Will be closed on ${formatted}`}
                              </p>
                            );
                          })()}

                          {/* AI card value line */}
                          {enhancedOpp.data.ai_card_summary_status === 'generating' && !enhancedOpp.data.ai_card_summary ? (
                            <div className="mb-3 space-y-1.5 animate-pulse" aria-label="Generating summary">
                              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-full" />
                              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-4/5" />
                            </div>
                          ) : (enhancedOpp.data.ai_card_summary || enhancedOpp.data.description) ? (
                            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-3 line-clamp-2">
                              {enhancedOpp.data.ai_card_summary || enhancedOpp.data.description}
                            </p>
                          ) : null}

                          {/* Description (raw) — superseded by ai_card_summary when present */}

                          {/* Additional Metadata */}
                          <div className="space-y-2 mb-3 flex-1">
                            {enhancedOpp.data.location && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
                                <MapPin className="h-4 w-4 flex-shrink-0" />
                                <span className="line-clamp-1">{enhancedOpp.data.location}</span>
                              </div>
                            )}
                            {enhancedOpp.data.matching_service_lines && enhancedOpp.data.matching_service_lines.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {enhancedOpp.data.matching_service_lines.slice(0, 2).map((line) => (
                                  <span
                                    key={line}
                                    className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded border border-blue-200 dark:border-blue-800"
                                  >
                                    {line}
                                  </span>
                                ))}
                                {enhancedOpp.data.matching_service_lines.length > 2 && (
                                  <span className="text-xs text-gray-600 dark:text-slate-300">
                                    +{enhancedOpp.data.matching_service_lines.length - 2} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Tracking Status Selector */}
                          <div
                            className="mb-3"
                            onClick={(e) => {
                              console.log('[OpportunitiesDashboard] Tracking selector clicked, stopping propagation');
                              e.stopPropagation();
                            }}
                          >
                            <label className="block text-xs font-medium text-gray-700 dark:text-slate-200 mb-1">
                              Status
                            </label>
                            <select
                              value={enhancedOpp.data.tracking_status || ''}
                              onChange={(e) => {
                                console.log('[OpportunitiesDashboard] Tracking status changed:', e.target.value);
                                e.stopPropagation();
                                handleUpdateTracking(opp.id, e.target.value);
                              }}
                              onClick={(e) => {
                                console.log('[OpportunitiesDashboard] Select clicked, stopping propagation');
                                e.stopPropagation();
                              }}
                              className="w-full px-2 py-1 text-sm border border-theme-border rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="">Not tracking</option>
                              <option value="watching">Watching</option>
                              <option value="bidding">Bidding</option>
                              <option value="won">Won</option>
                              <option value="lost">Lost</option>
                              <option value="declined">Declined</option>
                            </select>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2" onClick={(e) => {
                            console.log('[OpportunitiesDashboard] Actions div clicked, stopping propagation');
                            e.stopPropagation();
                          }}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={(e) => {
                                console.log('[OpportunitiesDashboard] Summary button clicked');
                                e.stopPropagation();
                                e.preventDefault();
                                openOpportunitySummary();
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Summary
                            </Button>
                            {opp.data.url && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={(e) => {
                                  console.log('[OpportunitiesDashboard] View button clicked');
                                  e.stopPropagation();
                                  e.preventDefault();
                                  window.open(opp.data.url, '_blank');
                                }}
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            )}
                            <Button
                              variant="primary"
                              size="sm"
                              className="flex-1"
                              onClick={(e) => {
                                console.log('[OpportunitiesDashboard] Proposal button clicked');
                                e.stopPropagation();
                                e.preventDefault();
                                if (!opp.data.tracking_status) {
                                  setWarningOpportunity(opp);
                                  setShowNotTrackedWarning(true);
                                  return;
                                }
                                const assignedBy = (opp.data as any).assigned_by;
                                if (assignedBy && assignedBy !== currentUserName) {
                                  setTeamMemberName(assignedBy);
                                  setTeamMemberWarningOpp(opp);
                                  setShowTeamMemberWarning(true);
                                  return;
                                }
                                setProposalOpportunity(opp);
                                setShowProposalPreview(true);
                              }}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Proposal
                            </Button>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {(() => {
                  const totalPages = Math.ceil(totalCount / pageSize);
                  const pageStart = (page - 1) * pageSize + 1;
                  const pageEnd = Math.min(page * pageSize, totalCount);

                  // Build visible page numbers: always show first, last, current ±2, with ellipsis
                  const getPageNumbers = () => {
                    const delta = 2;
                    const range: (number | '...')[] = [];
                    const rangeSet = new Set<number>();
                    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) rangeSet.add(i);
                    rangeSet.add(1);
                    rangeSet.add(totalPages);
                    const sorted = Array.from(rangeSet).sort((a, b) => a - b);
                    sorted.forEach((n, i) => {
                      if (i > 0 && n - (sorted[i - 1] as number) > 1) range.push('...');
                      range.push(n);
                    });
                    return range;
                  };

                  return (
                    <Card className="p-3">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        {/* Info */}
                        <div className="text-sm text-gray-500 dark:text-theme-text-secondary whitespace-nowrap">
                          Showing <span className="font-medium text-gray-700 dark:text-theme-text">{pageStart}–{pageEnd}</span> of <span className="font-medium text-gray-700 dark:text-theme-text">{totalCount}</span> opportunities
                        </div>

                        {/* Page controls */}
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* First */}
                          <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1} className="px-2" title="First page">
                            <ChevronsLeft className="h-4 w-4" />
                          </Button>
                          {/* Prev */}
                          <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={page === 1} className="px-2" title="Previous page">
                            <ChevronLeft className="h-4 w-4" />
                          </Button>

                          {/* Numbered pages */}
                          {getPageNumbers().map((n, i) =>
                            n === '...'
                              ? <span key={`ellipsis-${i}`} className="px-1 text-gray-400 select-none">…</span>
                              : <Button
                                key={n}
                                variant={n === page ? 'primary' : 'outline'}
                                size="sm"
                                onClick={() => setPage(n as number)}
                                className={`w-9 px-0 ${n === page ? 'font-bold' : ''}`}
                              >
                                {n}
                              </Button>
                          )}

                          {/* Next */}
                          <Button variant="outline" size="sm" onClick={handleNextPage} disabled={!hasMore} className="px-2" title="Next page">
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          {/* Last */}
                          <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2" title="Last page">
                            <ChevronsRight className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Jump to page */}
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-theme-text-secondary">
                          <span className="whitespace-nowrap">Go to</span>
                          <input
                            type="number"
                            min={1}
                            max={totalPages}
                            defaultValue={page}
                            key={page}
                            className="w-14 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-theme-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const v = parseInt((e.target as HTMLInputElement).value);
                                if (v >= 1 && v <= totalPages) setPage(v);
                              }
                            }}
                          />
                          <span className="whitespace-nowrap">of {totalPages}</span>
                        </div>
                      </div>
                    </Card>
                  );
                })()}
              </>
            )
          )}
        </>
      )}

      {/* Proposal Builder Modal (custom div, OpportunityDetailModal-style) */}
      {showProposalPreview &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className={`fixed inset-0 overscroll-contain ${isProposalModalFullscreen ? 'overflow-hidden' : 'overflow-y-auto'
              }`}
            style={{ ...PROPOSAL_FLOW_MODAL_CONTAINER_STYLE, backgroundColor: 'rgb(0 0 0 / 88%)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="proposal-builder-modal-title"
          >
            <button
              type="button"
              className="fixed inset-0 z-0 block cursor-default border-0 bg-slate-900/60 p-0 transition-opacity dark:bg-black/70"
              aria-label="Close proposal builder"
              onClick={() => {
                setShowProposalPreview(false);
                setProposalOpportunity(null);
              }}
            />

            <div
              className={`pointer-events-none absolute inset-0 z-10 flex justify-center ${isProposalModalFullscreen ? 'items-stretch p-0' : 'items-start p-3 pt-8 sm:p-6 sm:pt-10'
                }`}
            >
              <div
                className={`pointer-events-auto relative flex min-h-0 flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 ${isProposalModalFullscreen
                  ? 'h-[100dvh] w-full max-w-full max-h-[100dvh] rounded-none border-0'
                  : 'h-auto max-h-[82vh] w-full max-w-6xl rounded-xl border border-slate-200 dark:border-slate-700'
                  }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700">
                  <h3 id="proposal-builder-modal-title" className="text-lg font-semibold text-theme-text">
                    Generate Proposal
                  </h3>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setIsProposalModalFullscreen((v) => !v)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-500 dark:text-gray-300"
                      aria-label={isProposalModalFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                      title={isProposalModalFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    >
                      {isProposalModalFullscreen ? (
                        <Minimize2 className="h-5 w-5" />
                      ) : (
                        <Maximize2 className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProposalPreview(false);
                        setProposalOpportunity(null);
                      }}
                      className="p-1.5 text-theme-text-secondary hover:text-theme-text hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      aria-label="Close"
                      title="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
                  {proposalOpportunity && (
                    <ProposalBuilder
                      key={`${proposalOpportunity.id}-${proposalStartFresh ? 'fresh' : 'resume'}`}
                      opportunityId={proposalOpportunity.id}
                      opportunityTitle={proposalOpportunity.data.title || 'Untitled Opportunity'}
                      clientName={proposalOpportunity?.data?.title || proposalOpportunity.data.organization || 'Government Agency'}
                      resumePreviousProgress={!proposalStartFresh}
                      onComplete={(reportId) => {
                        setShowProposalPreview(false);
                        setProposalOpportunity(null);
                        setProposalStartFresh(false);
                        queryClient.invalidateQueries({ queryKey: ['reports'] });
                        queryClient.invalidateQueries({ queryKey: ['report-jobs'] });
                        const deadlineStr = proposalOpportunity.data.deadline
                          ? new Date(proposalOpportunity.data.deadline).toLocaleDateString('en-US', { dateStyle: 'medium' })
                          : '';
                        const openPath = `/rfp/proposals-management?tab=list&reportId=${encodeURIComponent(reportId)}`;
                        addToast({
                          type: 'success',
                          title: 'Proposal ready',
                          message: deadlineStr
                            ? `Open your proposal (Proposal Management): ${openPath} — Deadline: ${deadlineStr}.`
                            : `Open your proposal (Proposal Management): ${openPath}`,
                          duration: 8000,
                        });
                      }}
                      onCancel={() => {
                        const opportunity = proposalOpportunity;
                        setShowProposalPreview(false);
                        setProposalOpportunity(null);
                        setProposalStartFresh(false);
                        if (opportunity) {
                          syncOppIdInUrl(opportunity.id);
                          setSelectedOpportunity(opportunity);
                          setIsModalOpen(true);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Opportunity Detail Modal */}
      <OpportunityDetailModal
        opportunity={selectedOpportunity}
        isOpen={isModalOpen}
        onClose={() => {
          console.log('[OpportunitiesDashboard] Modal onClose called');
          syncOppIdInUrl(null);
          setIsModalOpen(false);
          setSelectedOpportunity(null);
        }}
        onSaveToList={(listId) => {
          if (selectedOpportunity) {
            // TODO: Implement save to list
            console.log('Save to list:', listId, selectedOpportunity.id);
          }
        }}
        onUpdateTracking={handleUpdateTracking}
        onOpportunityUpdate={(updatedOpportunity) => {
          // Update the selected opportunity state immediately for instant UI feedback
          setSelectedOpportunity(updatedOpportunity);
          // Also sync the card in the opportunities list cache so it reflects the change without a refetch
          const queryCache = queryClient.getQueryCache();
          queryCache.getAll().forEach((query) => {
            const key = query.queryKey;
            if (Array.isArray(key) && key[0] === 'govt-opportunities-all' && key[1] === projectId) {
              queryClient.setQueryData(key, (old: any) => {
                if (!old?.allEntities) return old;
                const updatedEntities = old.allEntities.map((opp: any) =>
                  opp.id === updatedOpportunity.id ? updatedOpportunity : opp
                );
                return { ...old, allEntities: updatedEntities };
              });
            }
          });
        }}
        onGenerateProposal={() => {
          if (selectedOpportunity) {
            if (!selectedOpportunity.data.tracking_status) {
              setWarningOpportunity(selectedOpportunity);
              setShowNotTrackedWarning(true);
              return;
            }
            const assignedBy = (selectedOpportunity.data as any).assigned_by;
            if (assignedBy && assignedBy !== currentUserName) {
              setTeamMemberName(assignedBy);
              setTeamMemberWarningOpp(selectedOpportunity);
              setShowTeamMemberWarning(true);
              return;
            }
            syncOppIdInUrl(null);
            setIsModalOpen(false);
            openProposalBuilder(selectedOpportunity);
          }
        }}
      />

      {/* Proposal-flow warnings: portal to body so they stack above OpportunityDetailModal (also portaled). */}
      {typeof document !== 'undefined' &&
        createPortal(
          <>
            <Modal
              isOpen={showNotTrackedWarning}
              onClose={() => {
                setShowNotTrackedWarning(false);
                setWarningOpportunity(null);
              }}
              title="Status Required"
              size="sm"
              containerStyle={PROPOSAL_FLOW_MODAL_CONTAINER_STYLE}
            >
              <div className="p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Please set a status before generating a proposal
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      This opportunity is currently <strong>Not Tracking</strong>. Please set it to <strong>Watching</strong> or{' '}
                      <strong>Bidding</strong> to proceed with proposal generation.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowNotTrackedWarning(false);
                      setWarningOpportunity(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      if (warningOpportunity) {
                        handleUpdateTracking(warningOpportunity.id, 'watching');
                        setShowNotTrackedWarning(false);
                        syncOppIdInUrl(null);
                        setIsModalOpen(false);
                        setSelectedOpportunity(null);
                        openProposalBuilder(warningOpportunity);
                        setWarningOpportunity(null);
                      }
                    }}
                  >
                    Set to Watching &amp; Continue
                  </Button>
                </div>
              </div>
            </Modal>

            <Modal
              isOpen={showTeamMemberWarning}
              onClose={() => {
                setShowTeamMemberWarning(false);
                setTeamMemberWarningOpp(null);
                setTeamMemberName('');
              }}
              title="Team Member Working Here"
              size="sm"
              containerStyle={PROPOSAL_FLOW_MODAL_CONTAINER_STYLE}
            >
              <div className="p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <User className="h-6 w-6 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {teamMemberName} is currently working on this opportunity
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Another team member has assigned themselves to this opportunity. Choose whether to continue alongside them,
                      or start a separate new proposal run from scratch.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowTeamMemberWarning(false);
                      setTeamMemberWarningOpp(null);
                      setTeamMemberName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      const opp = teamMemberWarningOpp;
                      setShowTeamMemberWarning(false);
                      setTeamMemberWarningOpp(null);
                      setTeamMemberName('');
                      if (opp) {
                        if (isModalOpen) {
                          syncOppIdInUrl(null);
                          setIsModalOpen(false);
                          setSelectedOpportunity(null);
                        }
                        openProposalBuilder(opp);
                      }
                    }}
                  >
                    Continue (resume / shared)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const opp = teamMemberWarningOpp;
                      setShowTeamMemberWarning(false);
                      setTeamMemberWarningOpp(null);
                      setTeamMemberName('');
                      if (!opp) {
                        addToast({ type: 'error', message: 'No opportunity selected' });
                        return;
                      }
                      if (isModalOpen) {
                        syncOppIdInUrl(null);
                        setIsModalOpen(false);
                        setSelectedOpportunity(null);
                      }
                      openProposalBuilder(opp, true);
                    }}
                  >
                    New proposal (fresh start)
                  </Button>
                </div>
              </div>
            </Modal>
          </>,
          document.body
        )}

      {/* Keyword Recommendations Modal */}
      <KeywordRecommendationsModal
        isOpen={showKeywordModal}
        onClose={() => setShowKeywordModal(false)}
        onSelectKeyword={handleKeywordSelect}
      />

      {/* Custom Proposal modal */}
      {showCustomProposalModal && (
        <CustomProposalModal
          onClose={() => setShowCustomProposalModal(false)}
          onSuccess={(opportunityId, title, clientName) => {
            setShowCustomProposalModal(false);
            // Fabricate a minimal GovtOpportunityEntity so ProposalBuilder can open
            const syntheticOpp: GovtOpportunityEntity = {
              id: opportunityId,
              entity_type: 'govt_opportunity',
              data: { title, organization: clientName, status: 'open' },
            };
            openProposalBuilder(syntheticOpp, true);
          }}
        />
      )}

      {/* Import Opportunities modal (hidden when enableOpportunitiesImport is false, e.g. Keystone) */}
      {showImportModal && enableOpportunitiesImport && (
        <ImportOpportunitiesModal
          onClose={() => setShowImportModal(false)}
          onSuccess={(count) => {
            setShowImportModal(false);
            addToast({ type: 'success', message: `Successfully imported ${count} opportunit${count !== 1 ? 'ies' : 'y'}.` });
            queryClient.invalidateQueries({ queryKey: ['entities'] });
          }}
        />
      )}

      {/* Export format chooser (SPARC-ISSUE-014) — CSV, Excel, or PDF */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export opportunities"
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-sm text-theme-text-secondary">
            Choose a format to export the {filteredOpportunities.length} opportunit
            {filteredOpportunities.length === 1 ? 'y' : 'ies'} matching your current filters.
          </p>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => handleExport('csv')}
              className="flex items-center gap-3 rounded-lg border border-theme-border p-3 text-left transition-colors hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <FileText className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-theme-text">CSV</p>
                <p className="text-xs text-theme-text-secondary">Plain text, opens in any spreadsheet app</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleExport('excel')}
              className="flex items-center gap-3 rounded-lg border border-theme-border p-3 text-left transition-colors hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <FileSpreadsheet className="h-5 w-5 text-emerald-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-theme-text">Excel (.xlsx)</p>
                <p className="text-xs text-theme-text-secondary">Formatted workbook with autofilter &amp; frozen header</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleExport('pdf')}
              className="flex items-center gap-3 rounded-lg border border-theme-border p-3 text-left transition-colors hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <FileText className="h-5 w-5 text-red-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-theme-text">PDF</p>
                <p className="text-xs text-theme-text-secondary">Printable table, ready to share</p>
              </div>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CustomProposalModal
// Creates a manual govt_opportunity entity from user-entered metadata, then
// hands off to the existing ProposalBuilder for document upload & generation.
// ---------------------------------------------------------------------------

interface CustomProposalModalProps {
  onClose: () => void;
  onSuccess: (opportunityId: string, title: string, clientName: string) => void;
}

/** YYYY-MM-DD in local calendar for `<input type="date" min="…">` */
function formatLocalDateForInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function CustomProposalModal({ onClose, onSuccess }: CustomProposalModalProps) {
  const { projectId } = useWorkspace();
  const { addToast } = useToastStore();

  const [title, setTitle] = useState('');
  const [organization, setOrganization] = useState('');
  const [deadline, setDeadline] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const minDeadlineDate = formatLocalDateForInput(new Date());
  const canContinue =
    !!title.trim() &&
    !!organization.trim() &&
    !!deadline.trim() &&
    !!referenceNumber.trim() &&
    deadline >= minDeadlineDate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;
    if (!projectId) {
      addToast({ type: 'error', message: 'No project selected' });
      return;
    }
    setIsSubmitting(true);
    try {
      const api = getAPIClient();
      const entity = await api.post('/entities', {
        project_id: projectId,
        entity_type: 'govt_opportunity',
        data: {
          title: title.trim(),
          organization: organization.trim() || undefined,
          issuing_agency: organization.trim() || undefined,
          deadline: deadline || undefined,
          reference_number: referenceNumber.trim() || undefined,
          solicitation_number: referenceNumber.trim() || undefined,
          description: description.trim() || undefined,
          status: 'open',
          source: 'manual',
          data_source: 'manual_custom',
        },
      });
      onSuccess(entity.id, title.trim(), organization.trim() || 'Custom Opportunity');
    } catch (err: any) {
      addToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to create opportunity. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary-500" />
              Create Custom Proposal
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Enter basic details below. You&apos;ll upload your RFP documents in the next step.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ml-4 mt-0.5">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Opportunity Title <span className="text-red-500">*</span>
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Environmental Assessment Services – City of Vancouver" className="w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Issuing Organization / Client <span className="text-red-500">*</span>
            </label>
            <Input value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="e.g. City of Vancouver" className="w-full" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Submission Deadline <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                min={minDeadlineDate}
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reference / Solicitation # <span className="text-red-500">*</span>
              </label>
              <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="e.g. RFP-2026-045" className="w-full" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Brief Description <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short summary of the scope of work..." rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none text-sm resize-none" />
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <FileText className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              After clicking Continue, you&apos;ll be taken to the Proposal Builder where you can upload your RFP PDF and supporting documents.
            </p>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || !canContinue}>
              {isSubmitting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Creating...</>
              ) : (
                <><FileText className="h-4 w-4 mr-2" />Continue to Proposal Builder</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImportOpportunitiesModal
// CSV import using exact canonical column names only.
// Column matching is done by exact name lookup — no AI, no fuzzy matching.
// Users must use the template or match column names exactly.
// ---------------------------------------------------------------------------

/**
 * The canonical field names the import expects in the CSV header row.
 * Column matching is EXACT (case-insensitive, spaces → underscores).
 * Any column not in this list is stored as-is under its raw name.
 */
const IMPORT_FIELDS: Array<{ field: string; label: string; required?: boolean; example: string; description: string }> = [
  { field: 'title', label: 'title', required: true, example: 'Environmental Site Assessment – Phase I', description: 'Opportunity title (required)' },
  { field: 'organization', label: 'organization', example: 'City of Vancouver', description: 'Issuing organization or client' },
  { field: 'deadline', label: 'deadline', example: '2026-06-30', description: 'Submission deadline (YYYY-MM-DD)' },
  { field: 'reference_number', label: 'reference_number', example: 'RFP-2026-045', description: 'RFP / solicitation reference number' },
  { field: 'description', label: 'description', example: 'Phase I ESA for a brownfield site...', description: 'Brief scope or description' },
  { field: 'location', label: 'location', example: 'Vancouver, BC', description: 'Project location' },
  { field: 'estimated_value', label: 'estimated_value', example: '150000', description: 'Estimated contract value (number, no $ or commas)' },
  { field: 'url', label: 'url', example: 'https://merx.com/opportunity/123', description: 'Link to the original tender posting' },
  { field: 'status', label: 'status', example: 'open', description: 'open, closed, or awarded' },
  { field: 'source', label: 'source', example: 'merx', description: 'Where this opportunity came from' },
];

/** Canonical column names the importer recognises (exact match after lowercase + spaces→underscores) */
const CSV_COLUMN_MAP: Record<string, string> = Object.fromEntries(
  IMPORT_FIELDS.map(({ field }) => [field, field])
);

const TEMPLATE_CSV = [
  IMPORT_FIELDS.map((f) => f.field).join(','),
  IMPORT_FIELDS.map((f) => `"${f.example}"`).join(','),
].join('\n');

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'opportunities_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function parseCSV(text: string): Array<Record<string, string>> {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, '').trim().toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).filter((l) => l.trim()).map((line) => {
    const values: string[] = [];
    let inQuote = false;
    let current = '';
    for (const ch of line) {
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === ',' && !inQuote) { values.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    values.push(current.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
    return row;
  });
}

interface ImportOpportunitiesModalProps {
  onClose: () => void;
  onSuccess: (count: number) => void;
}

function ImportOpportunitiesModal({ onClose, onSuccess }: ImportOpportunitiesModalProps) {
  const { projectId } = useWorkspace();
  const { addToast } = useToastStore();
  const [rows, setRows] = useState<Array<Record<string, string>>>([]);
  const [filename, setFilename] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [showFieldRef, setShowFieldRef] = useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  /** Columns in the uploaded file that are NOT recognised canonical names */
  const unknownColumns = rows[0]
    ? Object.keys(rows[0]).filter((col) => !(col in CSV_COLUMN_MAP))
    : [];

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setParseError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        if (file.name.endsWith('.json') || file.name.endsWith('.jsonl')) {
          let parsed: any[] = [];
          if (file.name.endsWith('.jsonl')) {
            parsed = text.trim().split('\n').map((l) => JSON.parse(l));
          } else {
            const obj = JSON.parse(text);
            parsed = Array.isArray(obj) ? obj : obj.opportunities ?? obj.entities ?? [obj];
          }
          setRows(parsed.map((item: any) => {
            const flat: Record<string, string> = {};
            Object.keys(item).forEach((k) => { flat[k] = String(item[k] ?? ''); });
            return flat;
          }));
        } else {
          setRows(parseCSV(text));
        }
      } catch (err: any) {
        setParseError(`Could not parse file: ${err.message}`);
        setRows([]);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!projectId || rows.length === 0) return;
    setIsSubmitting(true);
    try {
      const api = getAPIClient();
      let created = 0;
      const batchSize = 50;
      for (let i = 0; i < rows.length; i += batchSize) {
        const batch = rows.slice(i, i + batchSize);
        await Promise.all(batch.map(async (row) => {
          const data: Record<string, any> = { status: 'open', source: 'import' };
          Object.keys(row).forEach((col) => {
            const mapped = CSV_COLUMN_MAP[col] ?? col;
            const val = row[col];
            if (val && val.trim()) {
              if (mapped === 'estimated_value') {
                const num = parseFloat(val.replace(/[^0-9.]/g, ''));
                if (!isNaN(num)) data[mapped] = num;
              } else {
                data[mapped] = val.trim();
              }
            }
          });
          if (!data.title) return;
          await api.post('/entities', { project_id: projectId, entity_type: 'govt_opportunity', data });
          created++;
        }));
      }
      onSuccess(created);
    } catch (err: any) {
      addToast({ type: 'error', message: err?.response?.data?.detail || 'Import failed. Please check the file format.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewRows = rows.slice(0, 5);
  const previewColumns = rows[0] ? Object.keys(rows[0]).slice(0, 7) : [];

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-start justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary-500" />
              Import Opportunities
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Upload a CSV file using the exact column names shown below.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-4 mt-0.5">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">

          {/* Template download callout */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Column names must match exactly</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Matching is done by exact column name — there is no AI or fuzzy matching. Download the template to guarantee correct mapping.
              </p>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-700/60 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Download template
            </button>
          </div>

          {/* Field reference (collapsible) */}
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              onClick={() => setShowFieldRef((v) => !v)}
            >
              <span>Supported column names</span>
              {showFieldRef ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showFieldRef && (
              <div className="border-t border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-700/50">
                {IMPORT_FIELDS.map(({ field, required, description, example }) => (
                  <div key={field} className="flex items-center gap-3 px-4 py-2 text-xs">
                    <code className="font-mono text-primary-600 dark:text-primary-400 w-36 flex-shrink-0">
                      {field}{required && <span className="text-red-500 ml-0.5">*</span>}
                    </code>
                    <span className="text-gray-500 dark:text-gray-400 flex-1">{description}</span>
                    <span className="text-gray-400 dark:text-gray-500 italic truncate max-w-[140px]">{example}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload area */}
          <div
            className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {filename ? filename : 'Click to upload CSV or JSON'}
            </p>
            <p className="text-xs text-gray-400 mt-1">.csv, .json, .jsonl supported</p>
            <input ref={fileRef} type="file" accept=".csv,.json,.jsonl" className="hidden" onChange={handleFile} />
          </div>

          {parseError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {parseError}
            </div>
          )}

          {/* Unknown columns warning */}
          {unknownColumns.length > 0 && (
            <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-xs text-orange-700 dark:text-orange-300">
              <span className="font-medium">Unrecognised columns</span> — these will be stored as raw data but won&apos;t map to standard fields:{' '}
              {unknownColumns.map((c) => <code key={c} className="ml-1 font-mono bg-orange-100 dark:bg-orange-800/40 px-1 rounded">{c}</code>)}
            </div>
          )}

          {/* Preview */}
          {rows.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Preview — {rows.length} row{rows.length !== 1 ? 's' : ''} detected
                </p>
                <span className="text-xs text-gray-400">(first 5 shown)</span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-900/50">
                    <tr>
                      {previewColumns.map((col) => (
                        <th key={col} className={`px-3 py-2 text-left font-medium whitespace-nowrap ${col in CSV_COLUMN_MAP ? 'text-green-600 dark:text-green-400' : 'text-orange-500 dark:text-orange-400'}`}>
                          {col}
                          {!(col in CSV_COLUMN_MAP) && <span className="ml-1 text-orange-400" title="Unrecognised column">⚠</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-slate-700">
                        {previewColumns.map((col) => (
                          <td key={col} className="px-3 py-1.5 text-gray-700 dark:text-gray-300 max-w-[140px] truncate">
                            {row[col] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs mt-1.5">
                <span className="text-green-600 dark:text-green-400 font-medium">Green</span>
                <span className="text-gray-400"> = recognised field, </span>
                <span className="text-orange-500 dark:text-orange-400 font-medium">orange</span>
                <span className="text-gray-400"> = unrecognised (stored as raw data).</span>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <button onClick={downloadTemplate} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            <Download className="h-3.5 w-3.5" />
            Download template CSV
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button variant="primary" onClick={handleImport} disabled={isSubmitting || rows.length === 0}>
              {isSubmitting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Importing...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" />Import {rows.length > 0 ? `${rows.length} row${rows.length !== 1 ? 's' : ''}` : ''}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
